import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { fehlerText } from "@/lib/fehler";
import { INTERN_KEY } from "@/components/AnalyticsTracker";
import { de } from "date-fns/locale";
import { format } from "date-fns";
import { Lock, LogOut, Loader2, Sparkles, FileText, Clock, FileSignature, Timer } from "lucide-react";

import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { formatDateDE, bookingDates, isWeekendOrHoliday, toYMDLocal } from "@/lib/utils";
import { MWST_RATE, MWST_RATE_PERCENT, MWST_RATE_PERCENT_REDUCED, taxRateForCustomerType, mwstLabelFromItems, grossFromItems, mwstFromItems, calcVatTotals } from "@/lib/vatConfig";
import { POSITION_PRESETS, applyTypePreset } from "@/lib/positionPresets";
import { waOeffnen, rechnungWaText } from "@/lib/wa";
import useAvailability, {
  AVAILABILITY_MODIFIER_CLASSES,
  STATUS_DOT_CLASS,
  STATUS_LABELS,
  STATUS_ORDER,
  toYMD,
} from "@/lib/useAvailability";
import VouchersTab from "@/pages/admin/VouchersTab";
import ShiftsTab from "@/pages/admin/ShiftsTab";
import EinsatzplanTab from "@/pages/admin/EinsatzplanTab";
import EventSeitenTab from "@/pages/admin/EventSeitenTab";
import KalkulationTab from "@/pages/admin/KalkulationTab";
import BewertungenTab from "@/pages/admin/BewertungenTab";
import ZeiterfassungTab from "@/pages/admin/ZeiterfassungTab";
import KarteiTab from "@/pages/admin/KarteiTab";
import AngebotSeitenTab from "@/pages/admin/AngebotSeitenTab";
import { AuswahlHub } from "@/pages/admin/AuswahlHub";
import PortalKpis from "@/pages/admin/PortalKpis";
import RechnungHub from "@/pages/admin/RechnungHub";
import AuswertungTab from "@/pages/admin/AuswertungTab";
import AngebotUebersicht from "@/pages/admin/AngebotUebersicht";
import KundenTab from "@/pages/admin/KundenTab";
import StundenkontoTab from "@/pages/admin/StundenkontoTab";
import MenuekartenTab from "@/pages/admin/MenuekartenTab";
import MehrTab from "@/pages/admin/MehrTab";
import MahnungenTab from "@/pages/admin/MahnungenTab";
import { CustomerPicker } from "@/components/CustomerPicker";
import { VermittlungTab } from "@/pages/admin/VermittlungTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "ls_admin_pwd";

const TAB_LABELS = {
  availability: "Kalender & Verfügbarkeit",
  inquiries: "Anfragen",
  quotes: "Angebote",
  shifts: "Stundennachweis",
  stundenkonto: "Stundenkonto",
  "direct-invoices": "Direkt-Rechnung",
  payments: "Zahlungen",
  auswertung: "Auswertung",
  stats: "Statistik",
  mileage: "Fahrten",
  kunden: "Kundenkartei",
  angebote: "Angebote",
  rechnungen: "Rechnungen",
  stunden: "Stunden",
  customers: "Anfragen-Historie",
  vermittlung: "Vermittlung",
  vouchers: "Gutscheine",
  menuekarten: "Menükarten",
  kalkulation: "Kalkulation",
  bewertungen: "Bewertungen",
  zeiterfassung: "Zeiterfassung",
  karteien: "Veranstaltungs-Kartei",
  angebotsseiten: "Angebotsseiten",
  mehr: "Mehr",
};

// Oben sechs Einstiege — der Kunde ist der Startpunkt, Angebote/Rechnungen/Stunden sind Hubs
const TOP_TABS = ["kunden", "availability", "angebote", "rechnungen", "stunden", "mehr"];
const TOP_LABELS = {
  kunden: "Kunden", availability: "Kalender", angebote: "Angebote",
  rechnungen: "Rechnungen", stunden: "Stunden", mehr: "Mehr",
};
// Unterseiten der Hubs — bestimmen, welcher Einstieg oben aktiv bleibt und wohin „Zurück" führt
const HUB_KINDER = {
  angebote: ["angebotsseiten", "quotes"],
  rechnungen: ["direct-invoices", "payments", "auswertung", "mahnungen"],
  stunden: ["shifts", "stundenkonto", "zeiterfassung"],
  kunden: ["customers"],
};
const HUB_VON_TAB = Object.entries(HUB_KINDER).reduce((acc, [hub, kinder]) => {
  kinder.forEach((k) => { acc[k] = hub; });
  return acc;
}, {});
const MEHR_TABS = [
  "inquiries", "einsatzplan", "event-seiten", "kalkulation",
  "stats", "mileage", "vermittlung",
  "menuekarten", "bewertungen", "vouchers", "karteien",
];
const TAB_HINTS = {
  availability: "Termine blocken und Verfügbarkeit pflegen",
  inquiries: "Eingegangene Anfragen bearbeiten",
  quotes: "Angebote erstellen und nachfassen",
  einsatzplan: "Geplante Tage vorab bestätigen lassen — ohne Preise",
  "event-seiten": "Persönliche Gästeseite mit Countdown und Menü",
  kalkulation: "Kosten, Wareneinsatz und Verkaufspreis genau berechnen",
  bewertungen: "Google-Bewertungen für die Startseite pflegen",
  zeiterfassung: "Arbeitszeiten der Mitarbeiter prüfen und freigeben",
  karteien: "Einkauf, Mitnehmen, To-dos und Bilder je Veranstaltung",
  angebotsseiten: "Kalkulation als schöne Kundenseite mit Annahme verschicken",
  shifts: "Spontane Einsätze erfassen und bestätigen lassen",
  stundenkonto: "Bestätigte Stunden je Kunde und Monat abrechnen",
  "direct-invoices": "Rechnung ohne vorherige Anfrage stellen",
  payments: "Offene und bezahlte Rechnungen im Blick",
  mahnungen: "Überfällige Rechnungen anmahnen — Erinnerung, Mahnung, letzte Mahnung",
  stats: "Umsatz, Auslastung und Kennzahlen",
  mileage: "Fahrten für die Steuer dokumentieren",
  kunden: "Dein Startpunkt — Kunde auswählen und direkt weiterarbeiten",
  angebote: "Drei Wege zum Angebot — wähle den passenden",
  rechnungen: "Rechnung schreiben — frei, aus Stunden oder aus einem angenommenen Angebot",
  stunden: "Nachweise, offene Stunden und Mitarbeiterzeiten",
  customers: "Kunden aus früheren Anfragen",
  vermittlung: "Köche, Jobs und Vermittlungsanfragen",
  vouchers: "Gutscheine verkaufen und einlösen",
  menuekarten: "Gänge eintippen, Druckdateien sofort erzeugen",
  mehr: "Alles, was nicht direkt an einem Kunden hängt",
};

export default function Admin() {
  const [pwd, setPwd] = useState("");
  const [authPwd, setAuthPwd] = useState(() => sessionStorage.getItem(LS_KEY) || "");
  const [loggingIn, setLoggingIn] = useState(false);

  const login = async (e) => {
    e?.preventDefault();
    setLoggingIn(true);
    try {
      await axios.post(`${API}/admin/login`, { password: pwd });
      sessionStorage.setItem(LS_KEY, pwd);
      localStorage.setItem(INTERN_KEY, "1");   // eigene Besuche nicht mitzählen
      setAuthPwd(pwd);
      toast.success("Angemeldet");
    } catch (err) {
      toast.error("Falsches Passwort");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(LS_KEY);
    setAuthPwd("");
    setPwd("");
  };

  if (!authPwd) {
    return (
      <div data-testid="admin-login" className="max-w-md mx-auto px-6 md:px-12 py-32">
        <div className="border border-[#E3DECE] p-10 bg-[#FAFAF7]">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="text-[#BA6D52]" size={20} />
            <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">Admin</p>
          </div>
          <h1 className="font-serif text-3xl font-light tracking-tight">Anmeldung</h1>
          <p className="text-sm text-[#757067] font-light mt-3">
            Geben Sie das Admin-Passwort ein, um die Verfügbarkeit zu verwalten.
          </p>
          <form onSubmit={login} className="mt-8 space-y-6" method="post" action="#">
            {/* Hidden username-Feld: WebKit/Safari-Empfehlung, damit Face ID / Passwort-Manager
                die Credentials fuer die Domain korrekt speichern und anbieten koennen. */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              value="admin"
              readOnly
              hidden
              aria-hidden="true"
              tabIndex={-1}
            />
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">
                Passwort
              </Label>
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                autoFocus
                required
                data-testid="admin-password-input"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                className="mt-3 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-0 py-3 text-[#2C2A26] text-base"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loggingIn}
              className="w-full bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-8 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2"
            >
              {loggingIn ? <Loader2 size={16} className="animate-spin" /> : null}
              Anmelden
            </button>
          </form>
        </div>
      </div>
    );
  }

  return <AdminDashboard pwd={authPwd} onLogout={logout} />;
}

function AdminDashboard({ pwd, onLogout }) {
  const { items, byStatus, refetch, statusOf } = useAvailability();
  const [selected, setSelected] = useState(undefined);
  const [updating, setUpdating] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [tab, setTab] = useState("kunden");
  const [invoicePrefill, setInvoicePrefill] = useState(null);
  const [shiftPrefill, setShiftPrefill] = useState(null);
  const [selectedKundeId, setSelectedKundeId] = useState(null);
  const [invoiceOrigin, setInvoiceOrigin] = useState(null);
  const [menuPrefill, setMenuPrefill] = useState(null);
  const [karteiPrefill, setKarteiPrefill] = useState(null);

  // Alle zukünftigen Tage ohne expliziten Status → grün als „verfügbar" darstellen,
  // damit der Kontrast zu ausgebucht (rot) / anfrage (gelb) / nicht verfügbar (schwarz)
  // auf einen Blick sichtbar ist.
  const calendarModifiers = useMemo(
    () => ({
      ...byStatus,
      defaultAvailable: (d) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return d >= today && !statusOf(d);
      },
    }),
    [byStatus, statusOf],
  );
  const calendarModifierClasses = useMemo(
    () => ({
      ...AVAILABILITY_MODIFIER_CLASSES,
      defaultAvailable: AVAILABILITY_MODIFIER_CLASSES.available,
    }),
    [],
  );

  const authHeaders = { headers: { "X-Admin-Password": pwd } };

  const refetchInquiries = async () => {
    try {
      const { data } = await axios.get(`${API}/inquiries`, {
        headers: { "X-Admin-Password": pwd },
      });
      setInquiries(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sitzung abgelaufen — bitte neu anmelden");
        onLogout();
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/inquiries`, {
          headers: { "X-Admin-Password": pwd },
        });
        if (!cancelled) setInquiries(data);
      } catch (err) {
        if (err?.response?.status === 401) {
          toast.error("Sitzung abgelaufen — bitte neu anmelden");
          onLogout();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pwd, onLogout]);

  async function updateStatus(status) {
    if (!selected) return;
    setUpdating(true);
    try {
      await axios.put(
        `${API}/admin/availability`,
        { date: toYMD(selected), status },
        authHeaders,
      );
      toast.success(`Status gesetzt: ${STATUS_LABELS[status]}`);
      await refetch();
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sitzung abgelaufen");
        onLogout();
      } else {
        toast.error("Fehler beim Speichern");
      }
    } finally {
      setUpdating(false);
    }
  }

  const clearStatus = async () => {
    if (!selected) return;
    setUpdating(true);
    try {
      await axios.delete(`${API}/admin/availability/${toYMD(selected)}`, authHeaders);
      toast.success("Status entfernt");
      await refetch();
    } catch {
      toast.error("Fehler beim Löschen");
    } finally {
      setUpdating(false);
    }
  };

  const currentStatus = statusOf(selected);

  return (
    <div data-testid="admin-dashboard" className="max-w-6xl mx-auto px-6 md:px-12 py-16">
      <div className="flex items-start justify-between mb-10 gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">Admin-Portal</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-3">
            {TAB_LABELS[tab] || "Übersicht"}
          </h1>
          <p className="text-sm text-[#757067] mt-2">{TAB_HINTS[tab] || ""}</p>
        </div>
        <div className="flex items-center gap-3">
          <RunningTrackerBadge pwd={pwd} onSwitchTab={() => setTab("quotes")} />
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] px-5 py-3 text-xs uppercase tracking-[0.2em]"
          >
            <LogOut size={14} /> Abmelden
          </button>
        </div>
      </div>

      {/* Navigation: Kunde zuerst, dann die Arbeitswege */}
      <div className="flex gap-x-7 gap-y-2 border-b border-[#E3DECE] mb-4 flex-wrap overflow-x-auto">
        {TOP_TABS.map((id) => {
          const active = id === "mehr"
            ? (tab === "mehr" || MEHR_TABS.includes(tab))
            : (tab === id || (HUB_KINDER[id] || []).includes(tab));
          return (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`pb-4 text-sm uppercase tracking-[0.2em] border-b-2 -mb-px whitespace-nowrap transition-colors ${
                active ? "border-[#BA6D52] text-[#BA6D52]" : "border-transparent text-[#757067] hover:text-[#2C2A26]"
              }`}
            >
              {TOP_LABELS[id]}
            </button>
          );
        })}
      </div>

      {(MEHR_TABS.includes(tab) || HUB_VON_TAB[tab]) && (
        <button
          onClick={() => setTab(HUB_VON_TAB[tab] || "mehr")}
          data-testid="admin-back-to-mehr"
          className="inline-flex items-center gap-2 mb-8 text-xs uppercase tracking-[0.2em] text-[#757067] hover:text-[#BA6D52] transition-colors"
        >
          ← Zurück zu „{TOP_LABELS[HUB_VON_TAB[tab]] || "Mehr"}"
        </button>
      )}
      {!MEHR_TABS.includes(tab) && !HUB_VON_TAB[tab] && <div className="mb-6" />}

      {tab === "angebote" && (
        <AuswahlHub
          testid="angebot-hub"
          titel="Angebot erstellen"
          hinweis="Drei Wege zum Kunden — die schöne Angebotsseite zum Online-Annehmen, das klassische Paket-Angebot über Lexoffice oder ein Angebot auf Stundenbasis."
          karten={[
            { id: "angebotsseite", icon: Sparkles, label: "Angebotsseite",
              hint: "Eigene Kundenseite im Stil der Eventseite mit Menü, Preis und Online-Annahme. Termin wird bei Zusage geblockt.",
              onClick: () => setTab("angebotsseiten") },
            { id: "paket-angebot", icon: FileText, label: "Paket-Angebot",
              hint: "Klassisches Angebot mit Positionen und Paketen, direkt aus Lexoffice als PDF an den Kunden.",
              onClick: () => setTab("quotes") },
            { id: "stunden-angebot", icon: Clock, label: "Stunden-Angebot",
              hint: "Angebot auf Stundenbasis für Betriebe — Abrechnung später nach tatsächlich bestätigten Stunden.",
              onClick: () => setTab("quotes") },
          ]}
          extra={<AngebotUebersicht pwd={pwd} onOpenTab={(id) => setTab(id)} />}
        />
      )}
      {tab === "rechnungen" && (
        <RechnungHub
          pwd={pwd}
          onCreateInvoice={(prefill) => {
            setInvoicePrefill(prefill);
            setInvoiceOrigin("rechnungen");
            setTab("direct-invoices");
          }}
          onOpenTab={(id, payload) => {
            if (id === "direct-invoices" && payload?.openCreate) {
              setInvoicePrefill({});
              setInvoiceOrigin("rechnungen");
            }
            setTab(id);
          }}
        />
      )}
      {tab === "stunden" && (
        <AuswahlHub
          testid="stunden-hub"
          titel="Stunden"
          hinweis="Nachweise schreiben und bestätigen lassen, offene Stunden abrechnen oder die Zeiten Deiner Mitarbeiter freigeben."
          karten={[
            { id: "nachweise", icon: FileSignature, label: "Stundennachweise",
              hint: "Einsatz eintragen, per WhatsApp oder Mail bestätigen lassen, als PDF sichern.",
              onClick: () => setTab("shifts") },
            { id: "konto", icon: Clock, label: "Offene Stunden",
              hint: "Alle bestätigten, noch nicht abgerechneten Stunden je Kunde — direkt zur Sammelrechnung.",
              onClick: () => setTab("stundenkonto") },
            { id: "mitarbeiter", icon: Timer, label: "Mitarbeiterzeiten",
              hint: "Zeiten der Minijobber prüfen, freigeben und monatlich ans Steuerbüro geben.",
              onClick: () => setTab("zeiterfassung") },
          ]}
        />
      )}
      {tab === "availability" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="border border-[#E3DECE] p-6 bg-[#FAFAF7]">
            <Calendar
              mode="single"
              selected={selected}
              onSelect={setSelected}
              locale={de}
              modifiers={calendarModifiers}
              modifiersClassNames={calendarModifierClasses}
              numberOfMonths={1}
              className="mx-auto"
            />
            <div className="border-t border-[#E3DECE] mt-6 pt-6 grid grid-cols-2 gap-3 text-sm">
              {STATUS_ORDER.map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <span className={`inline-block w-3 h-3 rounded-full ${STATUS_DOT_CLASS[s]}`} />
                  <span className="text-[#2C2A26]">{STATUS_LABELS[s]}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-[#E3DECE] p-8 bg-[#F2EFE9]">
            <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">Tag bearbeiten</p>
            <p className="font-serif text-2xl mt-3">
              {selected
                ? format(selected, "EEEE, dd. MMMM yyyy", { locale: de })
                : "Bitte ein Datum wählen"}
            </p>

            {selected && (
              <>
                <p className="mt-3 text-sm text-[#757067]">
                  Aktueller Status:{" "}
                  <span className="font-medium text-[#2C2A26]">
                    {currentStatus ? STATUS_LABELS[currentStatus] : "Nicht gesetzt"}
                  </span>
                </p>

                <div className="mt-8 grid grid-cols-2 gap-3">
                  {STATUS_ORDER.map((s) => (
                    <button
                      key={s}
                      data-testid={`admin-set-${s}`}
                      disabled={updating}
                      onClick={() => updateStatus(s)}
                      className="flex items-center gap-3 px-4 py-3 border border-[#E3DECE] hover:border-[#BA6D52] bg-[#FAFAF7] text-sm"
                    >
                      <span className={`inline-block w-3 h-3 rounded-full ${STATUS_DOT_CLASS[s]}`} />
                      {STATUS_LABELS[s]}
                    </button>
                  ))}
                </div>

                {currentStatus && (
                  <button
                    onClick={clearStatus}
                    data-testid="admin-clear-status"
                    disabled={updating}
                    className="mt-6 text-xs uppercase tracking-[0.2em] text-[#757067] hover:text-[#BA6D52]"
                  >
                    Status entfernen
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {tab === "inquiries" && (
        <div data-testid="admin-inquiries-list" className="pt-4">
          {inquiries.length === 0 && (
            <p className="py-12 text-center text-[#757067]">Keine Anfragen vorhanden.</p>
          )}
          {inquiries.map((i) => (
            <InquiryRow
              key={i.id}
              inquiry={i}
              pwd={pwd}
              onUpdated={async () => {
                await refetchInquiries();
                await refetch();
              }}
            />
          ))}
        </div>
      )}

      {tab === "payments" && <PaymentsTab pwd={pwd} />}
      {tab === "mahnungen" && <MahnungenTab pwd={pwd} />}
      {tab === "auswertung" && <AuswertungTab pwd={pwd} />}
      {tab === "quotes" && <QuotesTab pwd={pwd} />}
      {tab === "vermittlung" && <VermittlungTab pwd={pwd} />}
      {tab === "direct-invoices" && (
        <DirectInvoicesTab
          pwd={pwd}
          prefill={invoicePrefill}
          onPrefillUsed={() => {
            setInvoicePrefill(null);
            if (invoiceOrigin === "kunden") { setInvoiceOrigin(null); setTab("kunden"); }
            if (invoiceOrigin === "rechnungen") { setInvoiceOrigin(null); setTab("rechnungen"); }
          }}
        />
      )}
      {tab === "einsatzplan" && <EinsatzplanTab pwd={pwd} />}
      {tab === "event-seiten" && <EventSeitenTab pwd={pwd} />}
      {tab === "kalkulation" && <KalkulationTab pwd={pwd} />}
      {tab === "bewertungen" && <BewertungenTab pwd={pwd} />}
      {tab === "zeiterfassung" && <ZeiterfassungTab pwd={pwd} />}
      {tab === "karteien" && (
        <KarteiTab pwd={pwd} prefill={karteiPrefill} onPrefillUsed={() => setKarteiPrefill(null)}
          onOpenTab={(id, daten) => { if (daten) setMenuPrefill?.(daten); setTab(id); }} />
      )}
      {tab === "angebotsseiten" && <AngebotSeitenTab pwd={pwd} />}
      {tab === "shifts" && (
        <ShiftsTab
          pwd={pwd}
          prefillCustomer={shiftPrefill}
          onPrefillUsed={() => setShiftPrefill(null)}
          onCreateInvoice={(prefill) => { setInvoicePrefill(prefill); setTab("direct-invoices"); }}
        />
      )}
      {tab === "stundenkonto" && (
        <StundenkontoTab
          pwd={pwd}
          onCreateInvoice={(prefill) => { setInvoicePrefill(prefill); setTab("direct-invoices"); }}
        />
      )}
      {tab === "kunden" && (
        <>
          <PortalKpis pwd={pwd} onOpenTab={(id) => setTab(id)} />
          <KundenTab
            pwd={pwd}
            selectedId={selectedKundeId}
            onSelect={setSelectedKundeId}
            onShowHistorie={() => setTab("customers")}
            onCreateInvoice={(prefill) => {
              setInvoicePrefill(prefill);
              setInvoiceOrigin("kunden");
              setTab("direct-invoices");
            }}
            onRecordHours={(kunde) => { setShiftPrefill(kunde); setTab("shifts"); }}
            onOpenTab={(id, payload) => {
              if (id === "menuekarten" && payload) setMenuPrefill(payload);
              if (id === "karteien" && payload) setKarteiPrefill(payload);
              setTab(id);
            }}
          />
        </>
      )}
      {tab === "mehr" && <MehrTab onOpenTab={(id) => setTab(id)} counts={{ inquiries: inquiries.length }} />}
      {tab === "menuekarten" && (
        <MenuekartenTab pwd={pwd} prefill={menuPrefill} onPrefillUsed={() => setMenuPrefill(null)} />
      )}
      {tab === "stats" && <StatsTab pwd={pwd} />}
      {tab === "customers" && <CustomersTab pwd={pwd} />}
      {tab === "mileage" && <MileageTab pwd={pwd} />}
      {tab === "vouchers" && <VouchersTab pwd={pwd} />}

      {tab === "calendar" && (
        <p className="mt-12 text-xs text-[#757067]">
          {items.length} Tag(e) im Kalender markiert.
        </p>
      )}
    </div>
  );
}


const STATUS_BADGE = {
  pending: { label: "Offen", cls: "bg-amber-400/20 text-amber-900" },
  confirmed: { label: "Bestätigt", cls: "bg-emerald-500/15 text-emerald-900" },
  alternative: { label: "Alternative vorgeschlagen", cls: "bg-blue-500/15 text-blue-900" },
  declined: { label: "Abgelehnt", cls: "bg-red-500/15 text-red-900" },
};

function InquiryRow({ inquiry: i, pwd, onUpdated }) {
  const [mode, setMode] = useState(null); // null | "confirm" | "alternative" | "decline"
  const [von, setVon] = useState(i.von_uhrzeit || "");
  const [bis, setBis] = useState(i.bis_uhrzeit || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // --- Binding confirm form state ---
  const initialHours = (() => {
    if (!i.von_uhrzeit || !i.bis_uhrzeit) return 8;
    const [vh, vm] = i.von_uhrzeit.split(":").map(Number);
    const [bh, bm] = i.bis_uhrzeit.split(":").map(Number);
    let mins = bh * 60 + bm - (vh * 60 + vm);
    if (mins <= 0) mins += 24 * 60;
    return Math.max(1, Math.round((mins / 60) * 4) / 4);
  })();
  // Base rate: if the inquiry already included spontaneous surcharge, strip it for display
  // so the toggle can be re-applied cleanly on a custom rate.
  const baseRateInitial = i.is_spontaneous && i.hourly_rate_eur
    ? Math.round((i.hourly_rate_eur / 1.10) * 100) / 100
    : (i.hourly_rate_eur || 49);
  const [confHourlyRate, setConfHourlyRate] = useState(baseRateInitial);
  const [confWeekendRate, setConfWeekendRate] = useState(i.hourly_rate_weekend_eur || 59);
  const [applySpontan, setApplySpontan] = useState(!!i.is_spontaneous);
  const [confHours, setConfHours] = useState(initialHours);
  const [confDays, setConfDays] = useState(i.anzahl_tage || 1);
  const [confTravel, setConfTravel] = useState(i.travel_cost_eur || 0);
  const [confNotes, setConfNotes] = useState("");
  const [confNumber, setConfNumber] = useState(i.confirmation_number || "");
  const [depositPercent, setDepositPercent] = useState(i.deposit_percent || 0);
  const [depositDue, setDepositDue] = useState(
    i.deposit_due_date || toYMDLocal(new Date(Date.now() + 7 * 86400000)),
  );
  const [depositSuggestion, setDepositSuggestion] = useState(null);

  // Einsatztage + WE-/Feiertags-Klassifizierung (pro Tag umschaltbar)
  const dayList = useMemo(
    () => bookingDates(i.einsatzdatum, Number(confDays) || 1),
    [i.einsatzdatum, confDays],
  );
  const [weekendDates, setWeekendDates] = useState([]);
  useEffect(() => {
    setWeekendDates(dayList.filter((d) => (i.weekend_dates || []).includes(d) || isWeekendOrHoliday(d)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayList.join(",")]);
  const toggleWeekendDay = (d) =>
    setWeekendDates((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  const effectiveRate = (Number(confHourlyRate) || 0) * (applySpontan ? 1.10 : 1);
  const effectiveWeekendRate = (Number(confWeekendRate) || 0) * (applySpontan ? 1.10 : 1);
  const weDaysCount = weekendDates.length;
  const wdDaysCount = Math.max(0, dayList.length - weDaysCount);
  const confLabor = (Number(confHours) || 0) * (wdDaysCount * effectiveRate + weDaysCount * effectiveWeekendRate);
  const confNet = confLabor + (Number(confTravel) || 0);
  const confMwst = Math.round(confNet * MWST_RATE * 100) / 100;
  const confTotal = Math.round((confNet + confMwst) * 100) / 100; // brutto
  const depositAmount = depositPercent > 0 ? Math.round(confTotal * depositPercent) / 100 : 0;
  const restAmount = Math.max(0, confTotal - depositAmount);

  // --- Time Tracker state ---
  const [tDate, setTDate] = useState(i.einsatzdatum || "");
  const [tVon, setTVon] = useState("09:00");
  const [tBis, setTBis] = useState("17:00");
  const [tNotes, setTNotes] = useState("");
  const [tBusy, setTBusy] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const totalTracked = (i.time_entries || []).reduce((s, e) => s + Number(e.hours || 0), 0);

  // Payment tracking
  const [payInvoice, setPayInvoice] = useState(i.invoice_number || "");
  const [payStatus, setPayStatus] = useState(i.payment_status || "open");
  const [payDue, setPayDue] = useState(i.payment_due_date || "");
  const [payBusy, setPayBusy] = useState(false);

  // Invoice-Vorschau Modal
  const [invoiceModal, setInvoiceModal] = useState(null); // null | {type: "deposit"|"final"}

  const openInvoiceModal = (type) => setInvoiceModal({ type });
  const closeInvoiceModal = () => setInvoiceModal(null);

  // Anzahlung anfordern (Modal mit voreingetragenen Werten aus der Anfrage)
  const [showAddDeposit, setShowAddDeposit] = useState(false);
  const [addDepPct, setAddDepPct] = useState(i.deposit_percent || 50);
  const [addDepDue, setAddDepDue] = useState(
    i.deposit_due_date || toYMDLocal(new Date(Date.now() + 7 * 86400000)),
  );
  const [addDepBusy, setAddDepBusy] = useState(false);
  // Wenn Modal geöffnet wird → aktuelle Werte übernehmen
  const openDepositModal = () => {
    setAddDepPct(i.deposit_percent || 50);
    setAddDepDue(i.deposit_due_date || toYMDLocal(new Date(Date.now() + 7 * 86400000)));
    setShowAddDeposit(true);
  };
  const submitAddDeposit = async () => {
    setAddDepBusy(true);
    try {
      await axios.put(
        `${API}/admin/inquiries/${i.id}/deposit`,
        { deposit_percent: Number(addDepPct), deposit_due_date: addDepDue },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(`Anzahlung ${addDepPct} % nachgetragen`);
      setShowAddDeposit(false);
      await onUpdated();
    } catch (err) {
      toast.error(fehlerText(err, "Fehler beim Speichern"));
    } finally {
      setAddDepBusy(false);
    }
  };

  const submitAddDepositAndSend = async () => {
    if (!window.confirm(
      `Anzahlungsrechnung JETZT an ${i.betrieb || "den Kunden"} versenden?\n\n` +
      `Prozentsatz: ${addDepPct} %\n` +
      `Fällig bis: ${addDepDue}\n\n` +
      `Die Rechnung wird in LexOffice erstellt und per E-Mail an den Kunden geschickt.`
    )) return;
    setAddDepBusy(true);
    try {
      await axios.post(
        `${API}/admin/inquiries/${i.id}/send-deposit-invoice-now`,
        { deposit_percent: Number(addDepPct), deposit_due_date: addDepDue },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(`Anzahlungsrechnung an ${i.betrieb || "Kunden"} versandt`);
      setShowAddDeposit(false);
      await onUpdated();
    } catch (err) {
      toast.error(fehlerText(err, "Fehler beim Versenden"));
    } finally {
      setAddDepBusy(false);
    }
  };

  const submitPreviewDepositEmail = async () => {
    setAddDepBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/inquiries/${i.id}/preview-deposit-invoice-email`,
        { deposit_percent: Number(addDepPct), deposit_due_date: addDepDue },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(`Vorschau an ${res.data?.sent_to || "dich"} gesendet — bitte E-Mail prüfen`);
    } catch (err) {
      toast.error(fehlerText(err, "Fehler beim Versenden der Vorschau"));
    } finally {
      setAddDepBusy(false);
    }
  };

  const [resendBusy, setResendBusy] = useState(false);
  const [orderConfBusy, setOrderConfBusy] = useState(false);

  const resendOrderConfirmation = async (toAdmin) => {
    if (!window.confirm(
      toAdmin
        ? `Auftragsbestätigung ${i.confirmation_number} als Kontroll-Kopie an dich senden?\n\nDer Kunde erhält nichts.`
        : `Auftragsbestätigung ${i.confirmation_number} erneut an ${i.betrieb || "den Kunden"} (${i.email}) senden?\n\nGleiche Auftragsnummer, korrigiertes PDF. Es wird nichts neu angelegt.`
    )) return;
    setOrderConfBusy(true);
    try {
      await axios.post(
        `${API}/admin/inquiries/${i.id}/resend-order-confirmation${toAdmin ? "?to_admin=true" : ""}`,
        {},
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(toAdmin
        ? "Kontroll-Kopie der Auftragsbestätigung an dich versandt"
        : `Auftragsbestätigung erneut an ${i.betrieb || "Kunden"} versandt`);
    } catch (err) {
      toast.error(fehlerText(err, "Fehler beim Versenden"));
    } finally {
      setOrderConfBusy(false);
    }
  };
  const [corrModal, setCorrModal] = useState(null); // null | "deposit" | "final"

  const sendCorrectedCopy = async (kind, toAdmin) => {
    const path = kind === "deposit" ? "resend-deposit-invoice" : "resend-final-invoice";
    const number = kind === "deposit" ? i.deposit_invoice_number : i.invoice_number;
    setResendBusy(true);
    try {
      await axios.post(
        `${API}/admin/inquiries/${i.id}/${path}?corrected=true${toAdmin ? "&to_admin=true" : ""}`,
        {},
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(
        toAdmin
          ? `Kontroll-Kopie von ${number} an dich versandt — Kunde hat nichts erhalten`
          : `Berichtigte Rechnung ${number} an ${i.betrieb || "Kunden"} versandt`,
      );
      setCorrModal(null);
      if (!toAdmin) await onUpdated();
    } catch (err) {
      toast.error(fehlerText(err, "Fehler beim Versenden"));
    } finally {
      setResendBusy(false);
    }
  };

  const resendDepositInvoice = async (corrected = false) => {
    if (!window.confirm(
      corrected
        ? `Berichtigte Kopie der Anzahlungsrechnung ${i.deposit_invoice_number} an ${i.betrieb || "den Kunden"} senden?\n\n` +
          `Gleiche Rechnungsnummer, gleicher Betrag — ergänzt um die Steuernummer. Die E-Mail und das PDF werden als "Berichtigte Ausfertigung" gekennzeichnet. Kein neuer LexOffice-Beleg.`
        : `Anzahlungsrechnung ${i.deposit_invoice_number} erneut an ${i.betrieb || "den Kunden"} senden?\n\n` +
          `Es wird KEINE neue Rechnung in LexOffice erstellt — nur die vorhandene wird als gebrandetes PDF (mit IBAN/Bankdaten) per E-Mail nochmal verschickt.`
    )) return;
    setResendBusy(true);
    try {
      await axios.post(
        `${API}/admin/inquiries/${i.id}/resend-deposit-invoice${corrected ? "?corrected=true" : ""}`,
        {},
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(
        corrected
          ? `Berichtigte Anzahlungsrechnung an ${i.betrieb || "Kunden"} versandt`
          : `Anzahlungsrechnung erneut an ${i.betrieb || "Kunden"} versandt`,
      );
      await onUpdated();
    } catch (err) {
      toast.error(fehlerText(err, "Fehler beim erneuten Versenden"));
    } finally {
      setResendBusy(false);
    }
  };

  const resendFinalInvoice = async (corrected = false) => {
    if (!window.confirm(
      corrected
        ? `Berichtigte Kopie der Schlussrechnung ${i.invoice_number} an ${i.betrieb || "den Kunden"} senden?\n\n` +
          `Gleiche Rechnungsnummer, gleicher Betrag — ergänzt um die Steuernummer. Die E-Mail und das PDF werden als "Berichtigte Ausfertigung" gekennzeichnet. Kein neuer LexOffice-Beleg.`
        : `Schlussrechnung ${i.invoice_number} erneut an ${i.betrieb || "den Kunden"} senden?\n\n` +
          `Es wird KEINE neue Rechnung in LexOffice erstellt — nur die vorhandene wird als gebrandetes PDF (mit IBAN/Bankdaten) per E-Mail nochmal verschickt.`
    )) return;
    setResendBusy(true);
    try {
      await axios.post(
        `${API}/admin/inquiries/${i.id}/resend-final-invoice${corrected ? "?corrected=true" : ""}`,
        {},
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(
        corrected
          ? `Berichtigte Schlussrechnung an ${i.betrieb || "Kunden"} versandt`
          : `Schlussrechnung erneut an ${i.betrieb || "Kunden"} versandt`,
      );
      await onUpdated();
    } catch (err) {
      toast.error(fehlerText(err, "Fehler beim erneuten Versenden"));
    } finally {
      setResendBusy(false);
    }
  };

  useEffect(() => {
    setPayInvoice(i.invoice_number || "");
    setPayStatus(i.payment_status || "open");
    setPayDue(i.payment_due_date || "");
  }, [i.invoice_number, i.payment_status, i.payment_due_date]);

  const updatePayment = async (overrides = {}) => {
    setPayBusy(true);
    try {
      const nextStatus = overrides.payment_status ?? payStatus;
      await axios.put(
        `${API}/admin/inquiries/${i.id}/payment`,
        { invoice_number: payInvoice, payment_status: payStatus, payment_due_date: payDue, ...overrides },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success("Bezahlstatus aktualisiert");
      await onUpdated();
      // Wenn gerade Anzahlung markiert wurde → Anzahlungsrechnungs-Modal öffnen
      if (overrides.deposit_received_at && !i.deposit_invoice_id) {
        openInvoiceModal("deposit");
      }
      // Wenn Status auf "invoiced" oder "paid" gesetzt wurde UND noch keine Schlussrechnung existiert
      // AND Anzahlung erfüllt (falls angesetzt) → Schlussrechnungs-Modal öffnen
      if (
        (nextStatus === "invoiced" || nextStatus === "paid")
        && !i.lexoffice_invoice_id
        && (!i.deposit_percent || i.deposit_received_at)
      ) {
        openInvoiceModal("final");
      }
    } catch (err) {
      toast.error("Fehler");
    } finally {
      setPayBusy(false);
    }
  };

  // Smart default für Anzahlung beim Öffnen des Bestätigungs-Formulars laden
  useEffect(() => {
    if (mode !== "confirm" || depositSuggestion !== null) return;
    const total = confLabor + (Number(confTravel) || 0);
    axios
      .get(`${API}/admin/inquiries/${i.id}/deposit-suggestion?total=${total.toFixed(2)}`, {
        headers: { "X-Admin-Password": pwd },
      })
      .then(({ data }) => {
        setDepositSuggestion(data);
        // Nur vorbefüllen wenn Admin noch nichts gewählt hat
        if (!i.deposit_percent && depositPercent === 0 && data.suggested_percent > 0) {
          setDepositPercent(data.suggested_percent);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const sendConfirmBinding = async () => {
    setConfirmBusy(true);
    try {
      await axios.post(
        `${API}/admin/inquiries/${i.id}/confirm-binding`,
        {
          hourly_rate_eur: Number(effectiveRate.toFixed(2)),
          hourly_rate_weekend_eur: Number(effectiveWeekendRate.toFixed(2)),
          weekend_dates: weekendDates,
          hours_per_day: Number(confHours),
          anzahl_tage: Number(confDays),
          travel_cost_eur: Number(confTravel),
          notes: confNotes,
          confirmed_von: von,
          confirmed_bis: bis,
          confirmation_number: confNumber,
          deposit_percent: Number(depositPercent),
          deposit_due_date: Number(depositPercent) > 0 ? depositDue : "",
        },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success("Auftrag verbindlich bestätigt — E-Mail gesendet");
      setMode(null);
      setConfNotes("");
      await onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Bestätigen");
    } finally {
      setConfirmBusy(false);
    }
  };

  const send = async (action) => {
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/inquiries/${i.id}/respond`,
        { action, message, confirmed_von: von, confirmed_bis: bis },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(action === "alternative" ? "Alternativvorschlag gesendet" : "Absage gesendet");
      setMode(null);
      setMessage("");
      await onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setSending(false);
    }
  };

  const addTimeEntry = async () => {
    if (!tDate || !tVon || !tBis) {
      toast.error("Datum, Von und Bis sind Pflichtfelder");
      return;
    }
    setTBusy(true);
    try {
      await axios.post(
        `${API}/admin/inquiries/${i.id}/time-entries`,
        { date: tDate, von: tVon, bis: tBis, notes: tNotes },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success("Stunden erfasst");
      setTNotes("");
      await onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    } finally {
      setTBusy(false);
    }
  };

  const deleteTimeEntry = async (entryId) => {
    if (!window.confirm("Diesen Eintrag löschen?")) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${i.id}/time-entries/${entryId}`, {
        headers: { "X-Admin-Password": pwd },
      });
      await onUpdated();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const sendTimeConfirmation = async () => {
    if (!window.confirm(`Stundenabrechnung mit ${totalTracked.toFixed(2).replace(".", ",")} h an den Kunden senden?`)) return;
    setTBusy(true);
    try {
      await axios.post(
        `${API}/admin/inquiries/${i.id}/send-time-confirmation`,
        {},
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success("Stundenabrechnung an Kunden gesendet");
      await onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    } finally {
      setTBusy(false);
    }
  };

  const deleteInquiry = async () => {
    const label = i.betrieb || i.email || i.id;
    if (!window.confirm(`Anfrage von „${label}" unwiderruflich löschen?\n\nAlle zugehörigen Daten (Stundeneinträge, Bestätigungen, Bewertungen) gehen verloren.`)) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${i.id}`, {
        headers: { "X-Admin-Password": pwd },
      });
      toast.success("Anfrage gelöscht");
      await onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const badge = STATUS_BADGE[i.status || "pending"] || STATUS_BADGE.pending;
  const _flex = i.time_flexibility || "fest";
  let zeitFenster = null;
  if (_flex === "absprache") {
    zeitFenster = "Nach Absprache";
  } else if (i.von_uhrzeit && i.bis_uhrzeit) {
    zeitFenster = _flex === "ca"
      ? `ca. ${i.von_uhrzeit} – ca. ${i.bis_uhrzeit} Uhr · flexibel`
      : `${i.von_uhrzeit} – ${i.bis_uhrzeit} Uhr`;
  }
  const isConfirmed = i.status === "confirmed";
  // Time Tracker erst sichtbar, wenn der Einsatz-Tag erreicht ist.
  // Wichtig: einsatzdatum als LOKALES Datum parsen, sonst gibt es bei UTC+2 (CH)
  // einen Off-by-one wegen Sommerzeit-Verschiebung (siehe Bug 28.06.2026).
  const todayYMD = (() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  })();
  const einsatzReached = i.einsatzdatum && i.einsatzdatum <= todayYMD;
  const showTimeTracker = isConfirmed && einsatzReached;

  // Status → Farben für visuelle Unterscheidung
  const statusColor = {
    pending: { border: "border-l-amber-500", bg: "bg-amber-50", dot: "bg-amber-500", text: "text-amber-900" },
    confirmed: { border: "border-l-emerald-500", bg: "bg-emerald-50", dot: "bg-emerald-500", text: "text-emerald-900" },
    alternative: { border: "border-l-sky-500", bg: "bg-sky-50", dot: "bg-sky-500", text: "text-sky-900" },
    declined: { border: "border-l-stone-400", bg: "bg-stone-50", dot: "bg-stone-400", text: "text-stone-700" },
  }[i.status] || { border: "border-l-amber-500", bg: "bg-amber-50", dot: "bg-amber-500", text: "text-amber-900" };

  // Initialen aus Betrieb für Avatar
  const initials = (i.betrieb || i.name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();
  // Deterministische Farbe pro Kunde anhand Email-Hash
  const customerColors = ["#BA6D52", "#7E8B71", "#9E7676", "#5C7D8A", "#A47551", "#6B7B8C", "#8B6F47", "#7D6E83"];
  const customerColor = customerColors[(i.email || i.id).split("").reduce((s, c) => s + c.charCodeAt(0), 0) % customerColors.length];

  return (
    <div
      data-testid={`inquiry-row-${i.id}`}
      className={`relative group mb-4 bg-white border border-[#E3DECE] border-l-4 ${statusColor.border} hover:shadow-md transition-shadow`}
    >
      <button
        type="button"
        onClick={deleteInquiry}
        data-testid={`inquiry-${i.id}-delete`}
        title="Anfrage löschen"
        className="absolute top-3 right-3 text-xs uppercase tracking-[0.15em] text-[#757067] hover:text-red-700 transition opacity-30 hover:opacity-100 z-10"
      >
        ✕ Löschen
      </button>

      {/* Header: Avatar + Firmenname + Status + Datum */}
      <div className="flex flex-wrap items-start gap-4 px-5 py-4 border-b border-[#E3DECE] bg-[#FAFAF7]">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-serif text-lg flex-shrink-0"
          style={{ backgroundColor: customerColor }}
          title={i.email}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif text-xl text-[#2C2A26] truncate">{i.betrieb || "—"}</h3>
            {i.service_type === "private" && (
              <span className="inline-flex items-center text-[10px] px-2 py-0.5 bg-[#BA6D52] text-white uppercase tracking-wider font-medium" title="Privatkoch-Anfrage">
                ★ Privat
              </span>
            )}
            <span className={`inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 ${statusColor.bg} ${statusColor.text} uppercase tracking-wider font-medium`}>
              <span className={`w-1.5 h-1.5 rounded-full ${statusColor.dot}`} /> {badge.label}
            </span>
            {i.confirmation_number && (
              <span className="text-[10px] text-[#BA6D52] font-mono tracking-wider bg-[#BA6D52]/10 px-2 py-0.5">
                {i.confirmation_number}
              </span>
            )}
          </div>
          <p className="text-xs text-[#757067] mt-1 font-light">
            <span className="font-medium text-[#2C2A26]">{i.name}</span>
            {i.ort && <> · {i.ort}</>}
            {i.bereich && <> · {i.bereich}</>}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs uppercase tracking-[0.2em] text-[#BA6D52] font-semibold">{formatDateDE(i.einsatzdatum)}</p>
          {zeitFenster && <p className="text-xs text-[#757067] mt-0.5">{zeitFenster}</p>}
          {i.anzahl_tage > 1 && (
            <p className="text-[10px] text-[#BA6D52] font-medium mt-0.5">× {i.anzahl_tage} Tage</p>
          )}
          <p className="text-[10px] text-[#757067] mt-1">Eingang {formatDateDE(i.created_at)}</p>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-6 grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-3">
          {/* Spacer in header layout — keep for alignment, but show key facts */}
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Anzahl Personen</p>
          <p className="text-sm text-[#2C2A26]">{i.anzahl_personen}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mt-3">Anlass</p>
          <p className="text-sm text-[#2C2A26]">{i.anlass || "—"}</p>
          {i.anzahl_gaenge > 0 && (
            <>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mt-3">Gänge</p>
              <p className="text-sm text-[#2C2A26]">{i.anzahl_gaenge}</p>
            </>
          )}
          {i.budget_pro_person && (
            <>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mt-3">Budget/Person</p>
              <p className="text-sm text-[#2C2A26]">{({"bis-40":"bis 40 €","40-70":"40–70 €","70-100":"70–100 €","100-plus":"100 € +","offen":"offen"}[i.budget_pro_person] || i.budget_pro_person)}</p>
            </>
          )}
          {(i.kuechenstil?.length > 0) && (
            <>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mt-3">Küchenstil</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {i.kuechenstil.map((k) => (
                  <span key={k} className="text-[10px] px-2 py-0.5 bg-[#FBF7EC] border border-[#E3DECE] text-[#5C574E]">{k}</span>
                ))}
              </div>
            </>
          )}
          {(i.allergien?.length > 0 || i.allergien_freitext) && (
            <>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mt-3">Allergien</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {(i.allergien || []).map((a) => (
                  <span key={a} className="text-[10px] px-2 py-0.5 bg-red-50 border border-red-200 text-red-800">{a}</span>
                ))}
              </div>
              {i.allergien_freitext && <p className="text-[11px] text-[#5C574E] italic mt-1">&bdquo;{i.allergien_freitext}&ldquo;</p>}
            </>
          )}
          {i.kueche_vor_ort && (
            <div className="mt-3 space-y-1 text-[11px] text-[#5C574E]">
              <p><b>Küche:</b> {({"voll":"vollausgestattet","klein":"klein/einfach","unbekannt":"unbekannt — anrufen"}[i.kueche_vor_ort] || i.kueche_vor_ort)}</p>
            </div>
          )}
        </div>
        <div className="md:col-span-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Einsatzort</p>
          <p className="text-sm text-[#2C2A26]">{i.einsatzort}</p>
          {i.nachricht && (
            <p className="text-sm text-[#2C2A26] mt-3 italic border-l-2 border-[#E3DECE] pl-3">
              „{i.nachricht}&ldquo;
            </p>
          )}
          {i.estimated_total_eur > 0 && (
            <div className="mt-4 bg-[#F2EFE9] border border-[#E3DECE] p-4 text-xs space-y-1">
              <div className="flex justify-between">
                <span className="text-[#757067]">
                  {i.estimated_hours.toFixed(1).replace(".", ",")} h × {i.hourly_rate_eur.toFixed(2).replace(".", ",")} €
                  {i.anzahl_tage > 1 && ` × ${i.anzahl_tage} T.`}
                  {i.is_holiday && " · Sonn-/Feiertag"}
                  {i.is_spontaneous && " · Spontan +10%"}
                </span>
                <span>{i.labor_cost_eur.toFixed(2).replace(".", ",")} €</span>
              </div>
              {i.travel_distance_km > 0 && (
                <div className="flex justify-between">
                  <span className="text-[#757067]">Anfahrt {i.travel_distance_km.toFixed(1).replace(".", ",")} km</span>
                  <span>{i.travel_cost_eur.toFixed(2).replace(".", ",")} €</span>
                </div>
              )}
              <div className="flex justify-between pt-1 border-t border-[#E3DECE] mt-1">
                <span className="font-medium text-[#2C2A26]">Geschätzt gesamt</span>
                <span className="font-serif text-base text-[#BA6D52]">
                  {i.estimated_total_eur.toFixed(2).replace(".", ",")} €
                </span>
              </div>
              {i.is_longterm && (
                <p className="text-[10px] text-[#BA6D52] italic mt-2 uppercase tracking-wider">
                  ⚠ Langzeit (≥3 Tage) — Stundensatz verhandelbar
                </p>
              )}
            </div>
          )}
          {isConfirmed && i.confirmed_total_eur > 0 && (
            <div className="mt-3 bg-emerald-50 border border-emerald-200 p-4 text-xs space-y-1">
              <p className="font-medium text-emerald-900 uppercase tracking-wider text-[10px]">
                ✓ Verbindlich bestätigt
              </p>
              <div className="flex justify-between">
                <span className="text-[#757067]">
                  {i.confirmed_hours_per_day?.toFixed(1).replace(".", ",")} h × {i.confirmed_hourly_rate_eur?.toFixed(2).replace(".", ",")} €
                  {i.confirmed_anzahl_tage > 1 && ` × ${i.confirmed_anzahl_tage} T.`}
                  {(i.weekend_dates || []).length > 0 && i.confirmed_hourly_rate_weekend_eur > 0 &&
                    ` (${i.weekend_dates.length} × WE à ${i.confirmed_hourly_rate_weekend_eur.toFixed(2).replace(".", ",")} €)`}
                </span>
                <span>{i.confirmed_labor_cost_eur?.toFixed(2).replace(".", ",")} €</span>
              </div>
              <div className="flex justify-between pt-1 border-t border-emerald-200 mt-1">
                <span className="font-medium text-[#2C2A26]">Netto</span>
                <span className="text-[#757067]">
                  {i.confirmed_total_eur?.toFixed(2).replace(".", ",")} €
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#757067] text-xs">+ {(i.confirmed_total_eur > 0 ? Math.round(((i.confirmed_mwst_eur || 0) / i.confirmed_total_eur) * 100) : MWST_RATE_PERCENT)} % MwSt.</span>
                <span className="text-[#757067] text-xs">
                  {(i.confirmed_mwst_eur || 0).toFixed(2).replace(".", ",")} €
                </span>
              </div>
              <div className="flex justify-between pt-1 border-t border-emerald-200 mt-1">
                <span className="font-medium text-[#2C2A26]">Brutto gesamt</span>
                <span className="font-serif text-base text-emerald-700">
                  {(i.confirmed_gross_eur || i.confirmed_total_eur)?.toFixed(2).replace(".", ",")} €
                </span>
              </div>
              {i.deposit_percent > 0 && (
                <div className="mt-2 pt-2 border-t border-emerald-200 text-[#2C2A26]">
                  <div className="flex justify-between">
                    <span className="text-[#757067]">
                      Anzahlung {i.deposit_percent}% · fällig {formatDateDE(i.deposit_due_date)}
                    </span>
                    <span className="font-medium">{(i.deposit_amount_eur || 0).toFixed(2).replace(".", ",")} €</span>
                  </div>
                  <p className="text-[10px] mt-1 uppercase tracking-wider">
                    {i.deposit_received_at ? (
                      <span className="text-emerald-700">✓ Erhalten am {formatDateDE(i.deposit_received_at)}</span>
                    ) : i.deposit_confirmed_by_customer_at ? (
                      <span className="text-amber-700">⏳ Kunde meldet Überweisung am {formatDateDE(i.deposit_confirmed_by_customer_at)} — Eingang prüfen</span>
                    ) : (
                      <span className="text-[#BA6D52]">○ Anzahlung offen</span>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}
          {isConfirmed && i.confirmation_number && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-testid={`inquiry-${i.id}-resend-order-confirmation`}
                disabled={orderConfBusy}
                onClick={() => resendOrderConfirmation(true)}
                className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] disabled:opacity-60 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em]"
                title="Auftragsbestätigung als Kontroll-Kopie an mich senden (Kunde erhält nichts)"
              >
                {orderConfBusy ? "Sende…" : "AB-Kopie an mich"}
              </button>
              <button
                type="button"
                data-testid={`inquiry-${i.id}-resend-order-confirmation-customer`}
                disabled={orderConfBusy || !i.email}
                onClick={() => resendOrderConfirmation(false)}
                className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em]"
                title="Auftragsbestätigung (korrigiertes PDF) erneut an den Kunden senden — gleiche Auftragsnummer"
              >
                {orderConfBusy ? "Sende…" : "AB erneut an Kunden"}
              </button>
            </div>
          )}
        </div>
        <div className="md:col-span-4 text-sm">
          <a href={`mailto:${i.email}`} className="text-[#BA6D52] hover:underline block">{i.email}</a>
          <a href={`tel:${i.telefon}`} className="text-[#2C2A26] hover:text-[#BA6D52] block mt-1">{i.telefon}</a>

          {!mode && !isConfirmed && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                data-testid={`inquiry-${i.id}-confirm`}
                onClick={() => setMode("confirm")}
                className="px-3 py-2 text-xs uppercase tracking-[0.15em] bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Bestätigen
              </button>
              <button
                data-testid={`inquiry-${i.id}-alternative`}
                onClick={() => setMode("alternative")}
                className="px-3 py-2 text-xs uppercase tracking-[0.15em] border border-[#BA6D52] text-[#BA6D52] hover:bg-[#BA6D52] hover:text-white"
              >
                Alternative
              </button>
              <button
                data-testid={`inquiry-${i.id}-decline`}
                onClick={() => setMode("decline")}
                className="px-3 py-2 text-xs uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:border-red-500 hover:text-red-700"
              >
                Absagen
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Binding Confirm Form */}
      {mode === "confirm" && (
        <div data-testid={`inquiry-${i.id}-confirm-form`} className="mt-8 bg-[#FAF5F1] border border-[#BA6D52] p-6 md:p-8">
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Verbindlich bestätigen</p>
          <p className="text-sm text-[#757067] mt-2 font-light">
            Konditionen anpassen → Kunde erhält eine rechtlich verbindliche Auftragsbestätigung per E-Mail.
          </p>

          {/* Auftragsnummer */}
          <div className="mt-6 max-w-md">
            <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">
              Auftragsnummer
              <span className="ml-2 normal-case text-[#757067] tracking-normal text-[10px]">(z. B. aus LexOffice / Finom)</span>
            </label>
            <input
              type="text"
              data-testid={`inquiry-${i.id}-number`}
              value={confNumber}
              onChange={(e) => setConfNumber(e.target.value)}
              placeholder="Leer lassen → Auto-Nummer (AB-2026-XXX)"
              className="mt-2 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-3 text-base font-mono tracking-wider"
            />
            <p className="text-xs text-[#757067] mt-2 font-light">
              Trage hier dieselbe Nummer ein wie in LexOffice / Finom — so bleibt deine Buchhaltung synchron. Bei leerem Feld wird automatisch <code className="bg-white px-1">AB-{new Date().getFullYear()}-XXX</code> erzeugt.
            </p>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Stundensatz €</label>
              <input
                type="number" step="0.50" min="0"
                data-testid={`inquiry-${i.id}-rate`}
                value={confHourlyRate}
                onChange={(e) => setConfHourlyRate(e.target.value)}
                className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 text-lg"
              />
              <button
                type="button"
                data-testid={`inquiry-${i.id}-toggle-spontan`}
                onClick={() => setApplySpontan(!applySpontan)}
                className={`mt-2 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border transition ${
                  applySpontan
                    ? "bg-[#BA6D52] text-white border-[#BA6D52]"
                    : "bg-white text-[#757067] border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52]"
                }`}
              >
                <span>{applySpontan ? "✓" : "+"}</span>
                Spontanaufschlag +10%
              </button>
              {applySpontan && (
                <p className="text-[11px] text-[#BA6D52] mt-1.5 font-medium">
                  Effektiv: {effectiveRate.toFixed(2).replace(".", ",")} € / Std
                </p>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Satz WE/Feiertag €</label>
              <input
                type="number" step="0.50" min="0"
                data-testid={`inquiry-${i.id}-weekend-rate`}
                value={confWeekendRate}
                onChange={(e) => setConfWeekendRate(e.target.value)}
                className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 text-lg"
              />
              {applySpontan && (
                <p className="text-[11px] text-[#BA6D52] mt-1.5 font-medium">
                  Effektiv: {effectiveWeekendRate.toFixed(2).replace(".", ",")} € / Std
                </p>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Stunden / Tag</label>
              <input
                type="number" step="0.25" min="0"
                data-testid={`inquiry-${i.id}-hours`}
                value={confHours}
                onChange={(e) => setConfHours(e.target.value)}
                className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 text-lg"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Anzahl Tage</label>
              <input
                type="number" step="1" min="1"
                data-testid={`inquiry-${i.id}-days`}
                value={confDays}
                onChange={(e) => setConfDays(e.target.value)}
                className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 text-lg"
              />
            </div>
          </div>

          {/* Einsatztage: Werktag / WE-Feiertag Klassifizierung */}
          {dayList.length > 0 && (
            <div className="mt-6">
              <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">
                Einsatztage
                <span className="ml-2 normal-case text-[#757067] tracking-normal text-[10px]">
                  ({wdDaysCount} × Werktag à {effectiveRate.toFixed(2).replace(".", ",")} € · {weDaysCount} × WE/Feiertag à {effectiveWeekendRate.toFixed(2).replace(".", ",")} €)
                </span>
              </label>
              <div className="mt-3 flex flex-wrap gap-2">
                {dayList.map((d) => {
                  const isWe = weekendDates.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      data-testid={`inquiry-${i.id}-daytoggle-${d}`}
                      onClick={() => toggleWeekendDay(d)}
                      className={`text-[11px] uppercase tracking-[0.12em] px-3 py-2 border transition ${
                        isWe
                          ? "bg-[#BA6D52] text-white border-[#BA6D52]"
                          : "bg-white text-[#2C2A26] border-[#E3DECE] hover:border-[#BA6D52]"
                      }`}
                    >
                      {formatDateDE(d)} · {isWe ? "WE/Feiertag" : "Werktag"}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-[#757067] mt-2 font-light">
                Sa/So + bundesweite Feiertage sind vorausgewählt — Tag anklicken zum Umschalten (z. B. regionale Feiertage).
              </p>
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-6 max-w-md">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Bestätigt von</label>
              <input
                type="time"
                value={von}
                onChange={(e) => setVon(e.target.value)}
                className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Bestätigt bis</label>
              <input
                type="time"
                value={bis}
                onChange={(e) => setBis(e.target.value)}
                className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Vermerk / Bedingungen (optional)</label>
            <textarea
              rows={3}
              data-testid={`inquiry-${i.id}-notes`}
              value={confNotes}
              onChange={(e) => setConfNotes(e.target.value)}
              placeholder='z. B. "Stundensatz inkl. Verpflegung vor Ort" oder "Tagessatz pauschal vereinbart"'
              className="mt-2 w-full bg-[#FAFAF7] border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] p-3 text-sm font-light"
            />
          </div>

          {/* Anzahlung */}
          <div className="mt-6 bg-[#FAFAF7] border border-[#E3DECE] p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-[#BA6D52] font-semibold">Anzahlung</p>
            {depositSuggestion && (
              <p className="text-[11px] text-[#757067] mt-1 italic">💡 {depositSuggestion.reason}</p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {[0, 25, 50].map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`inquiry-${i.id}-deposit-${p}`}
                  onClick={() => setDepositPercent(p)}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition ${
                    depositPercent === p
                      ? "bg-[#BA6D52] text-white border-[#BA6D52]"
                      : "bg-white text-[#757067] border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52]"
                  }`}
                >
                  {p === 0 ? "Keine" : `${p} %`}
                </button>
              ))}
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="5"
                  value={[0, 25, 50].includes(Number(depositPercent)) ? "" : depositPercent}
                  onChange={(e) => setDepositPercent(Number(e.target.value) || 0)}
                  data-testid={`inquiry-${i.id}-deposit-custom`}
                  placeholder="andere %"
                  className="w-24 bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-2 py-2 text-xs"
                />
                <span className="text-xs text-[#757067]">%</span>
              </div>
            </div>
            {depositPercent > 0 && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Fällig bis</label>
                  <input
                    type="date"
                    value={depositDue}
                    onChange={(e) => setDepositDue(e.target.value)}
                    data-testid={`inquiry-${i.id}-deposit-due`}
                    className="mt-2 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2"
                  />
                </div>
                <div className="text-sm bg-white border border-[#E3DECE] p-3">
                  <div className="flex justify-between">
                    <span className="text-[#757067]">Anzahlung ({depositPercent} %) vom Brutto</span>
                    <span className="font-medium text-[#BA6D52]">{depositAmount.toFixed(2).replace(".", ",")} €</span>
                  </div>
                  <div className="flex justify-between mt-1 text-[#757067]">
                    <span>Restbetrag nach Einsatz</span>
                    <span>{restAmount.toFixed(2).replace(".", ",")} €</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 bg-white border border-[#E3DECE] p-4">
            <div className="flex justify-between text-sm">
              <span className="text-[#757067]">
                Lohnkosten ({confHours} h × {effectiveRate.toFixed(2).replace(".", ",")} €
                {applySpontan && <span className="text-[#BA6D52]"> [+10% Spontan]</span>}
                {Number(confDays) > 1 && ` × ${confDays} Tage`})
              </span>
              <span>{confLabor.toFixed(2).replace(".", ",")} €</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-[#757067]">Anfahrt</span>
              <span>{Number(confTravel || 0).toFixed(2).replace(".", ",")} €</span>
            </div>
            <div className="flex justify-between text-sm mt-2 pt-2 border-t border-[#E3DECE]">
              <span className="text-[#757067]">Zwischensumme netto</span>
              <span>{confNet.toFixed(2).replace(".", ",")} €</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-[#757067]">+ {MWST_RATE_PERCENT} % MwSt.</span>
              <span className="text-[#757067]">{confMwst.toFixed(2).replace(".", ",")} €</span>
            </div>
            <div className="flex justify-between mt-3 pt-3 border-t border-[#E3DECE]">
              <span className="font-medium uppercase text-xs tracking-[0.2em]">Verbindliche Gesamtsumme (brutto)</span>
              <span className="font-serif text-2xl text-[#BA6D52]">{confTotal.toFixed(2).replace(".", ",")} €</span>
            </div>
          </div>

          <div className="mt-6 flex gap-3 flex-wrap">
            <button
              data-testid={`inquiry-${i.id}-confirm-send`}
              onClick={sendConfirmBinding}
              disabled={confirmBusy}
              className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.2em]"
            >
              {confirmBusy ? "Wird gesendet…" : "Verbindlich bestätigen & E-Mail senden"}
            </button>
            <button onClick={() => setMode(null)} className="border border-[#E3DECE] hover:border-[#BA6D52] px-6 py-3 text-xs uppercase tracking-[0.2em]">
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Alternative & Decline forms */}
      {(mode === "alternative" || mode === "decline") && (
        <div className="mt-8 bg-[#F2EFE9] border border-[#E3DECE] p-6 md:p-8">
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">
            {mode === "alternative" ? "Alternative vorschlagen" : "Absage senden"}
          </p>

          {mode === "alternative" && (
            <div className="mt-6 grid grid-cols-2 gap-6 max-w-md">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Von</label>
                <input type="time" value={von} onChange={(e) => setVon(e.target.value)}
                  className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Bis</label>
                <input type="time" value={bis} onChange={(e) => setBis(e.target.value)}
                  className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2" />
              </div>
            </div>
          )}

          <div className="mt-6">
            <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Nachricht</label>
            <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
              className="mt-2 w-full bg-[#FAFAF7] border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] p-3 text-sm font-light" />
          </div>

          <div className="mt-6 flex gap-3">
            <button onClick={() => send(mode)} disabled={sending}
              className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.2em]">
              {sending ? "Wird gesendet…" : "E-Mail senden"}
            </button>
            <button onClick={() => setMode(null)} className="border border-[#E3DECE] hover:border-[#BA6D52] px-6 py-3 text-xs uppercase tracking-[0.2em]">
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Time Tracker (only after binding confirmation AND when einsatz date is reached) */}
      {isConfirmed && !einsatzReached && (
        <div className="mt-6 bg-[#FAF5F1] border border-dashed border-[#BA6D52]/40 p-4 text-sm text-[#757067] italic">
          ⏳ Der Time Tracker wird ab dem Einsatztag <b className="not-italic text-[#2C2A26]">{formatDateDE(i.einsatzdatum)}</b> automatisch hier angezeigt.
        </div>
      )}
      {showTimeTracker && (
        <div data-testid={`inquiry-${i.id}-timetracker`} className="mt-8 bg-[#FAFAF7] border border-[#E3DECE] p-6 md:p-8">
          <div className="flex justify-between items-baseline flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Time Tracker</p>
              <p className="text-sm text-[#757067] mt-1 font-light">
                Erfasse pro Tag Arbeitszeit. Kunde bestätigt am Ende verbindlich per Klick.
              </p>
            </div>
            <p className="font-serif text-2xl text-[#BA6D52]">{totalTracked.toFixed(2).replace(".", ",")} h <span className="text-xs text-[#757067] uppercase tracking-wider">gesamt</span></p>
          </div>

          {/* Add entry */}
          {!i.times_confirmed_at && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Datum</label>
                <input type="date" data-testid={`inquiry-${i.id}-tdate`} value={tDate} onChange={(e) => setTDate(e.target.value)}
                  className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Von</label>
                <input type="time" data-testid={`inquiry-${i.id}-tvon`} value={tVon} onChange={(e) => setTVon(e.target.value)}
                  className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Bis</label>
                <input type="time" data-testid={`inquiry-${i.id}-tbis`} value={tBis} onChange={(e) => setTBis(e.target.value)}
                  className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Vermerk</label>
                <input type="text" data-testid={`inquiry-${i.id}-tnotes`} value={tNotes} onChange={(e) => setTNotes(e.target.value)}
                  placeholder="optional" className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2" />
              </div>
              <div className="flex items-end">
                <button onClick={addTimeEntry} disabled={tBusy} data-testid={`inquiry-${i.id}-tadd`}
                  className="w-full bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-4 py-3 text-xs uppercase tracking-[0.15em]">
                  + Eintrag
                </button>
              </div>
            </div>
          )}

          {/* Entries list */}
          {i.time_entries && i.time_entries.length > 0 && (
            <div className="mt-6 border border-[#E3DECE] bg-white">
              <table className="w-full text-sm">
                <thead className="bg-[#F2EFE9] text-xs uppercase tracking-wider text-[#BA6D52]">
                  <tr>
                    <th className="text-left p-3">Datum</th>
                    <th className="text-left p-3">Zeit</th>
                    <th className="text-right p-3">Stunden</th>
                    <th className="text-left p-3">Vermerk</th>
                    {!i.times_confirmed_at && <th className="p-3"></th>}
                  </tr>
                </thead>
                <tbody>
                  {i.time_entries.map((e) => (
                    <tr key={e.id} className="border-t border-[#E3DECE]">
                      <td className="p-3">{formatDateDE(e.date)}</td>
                      <td className="p-3 text-[#757067]">{e.von} – {e.bis}</td>
                      <td className="p-3 text-right font-medium">{Number(e.hours).toFixed(2).replace(".", ",")} h</td>
                      <td className="p-3 text-[#757067] text-xs italic">{e.notes || "—"}</td>
                      {!i.times_confirmed_at && (
                        <td className="p-3 text-right">
                          <button onClick={() => deleteTimeEntry(e.id)} className="text-xs text-red-700 hover:underline">Löschen</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Send / status */}
          <div className="mt-6 flex flex-wrap items-center gap-4">
            {i.times_confirmed_at ? (
              <div className="bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm flex-1">
                <p className="font-medium text-emerald-900">✓ Vom Kunden bestätigt</p>
                <p className="text-xs text-emerald-700 mt-1">
                  Am {new Date(i.times_confirmed_at).toLocaleString("de-CH")} · {(i.times_confirmed_total_hours || 0).toFixed(2).replace(".", ",")} h
                </p>
                {i.review_submitted_at ? (
                  <p className="text-xs text-emerald-700 mt-2">⭐ Bewertung: {i.review_rating}/5 abgegeben</p>
                ) : (
                  <button
                    onClick={async () => {
                      if (!window.confirm("Bewertungs-Anfrage an den Kunden senden?")) return;
                      try {
                        await axios.post(`${API}/admin/inquiries/${i.id}/send-review-request`, {}, { headers: { "X-Admin-Password": pwd } });
                        toast.success("Bewertungs-Anfrage gesendet");
                        await onUpdated();
                      } catch { toast.error("Fehler"); }
                    }}
                    className="mt-3 text-xs underline text-emerald-700 hover:text-emerald-900"
                  >
                    {i.review_request_sent_at ? "⭐ Bewertungsanfrage erneut senden" : "⭐ Bewertungsanfrage an Kunden senden"}
                  </button>
                )}
              </div>
            ) : (
              <>
                {i.time_confirmation_sent_at && (
                  <p className="text-xs text-[#757067] italic flex-1">
                    Bestätigungs-Mail gesendet am {new Date(i.time_confirmation_sent_at).toLocaleString("de-CH")} — wartet auf Kundenbestätigung.
                  </p>
                )}
                <button
                  onClick={sendTimeConfirmation}
                  disabled={tBusy || !i.time_entries?.length}
                  data-testid={`inquiry-${i.id}-tsend`}
                  className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 px-5 py-3 text-xs uppercase tracking-[0.15em]"
                >
                  {i.time_confirmation_sent_at ? "Erneut senden" : "An Kunden zur Bestätigung senden"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Payment / Rechnung */}
      {isConfirmed && (
        <div data-testid={`inquiry-${i.id}-payment`} className="mt-6 bg-white border border-[#E3DECE] p-6 md:p-8">
          <div className="flex justify-between items-baseline flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Rechnung &amp; Bezahlstatus</p>
              <p className="text-sm text-[#757067] mt-1 font-light">Nach Stundenbestätigung wird die Schlussrechnung mit editierbaren Positionen zur Prüfung angezeigt, bevor sie in LexOffice erstellt und versendet wird.</p>
            </div>
            {(() => {
              const map = {
                open: { label: "Offen", cls: "bg-amber-400/20 text-amber-900" },
                invoiced: { label: "Rechnung gestellt", cls: "bg-blue-500/15 text-blue-900" },
                paid: { label: "✓ Bezahlt", cls: "bg-emerald-500/15 text-emerald-900" },
                overdue: { label: "⚠ Überfällig", cls: "bg-red-500/15 text-red-900" },
              };
              const b = map[i.payment_status || "open"] || map.open;
              return <span className={`text-xs px-3 py-1 ${b.cls}`}>{b.label}</span>;
            })()}
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Rechnungsnummer</label>
              <input type="text" value={payInvoice} onChange={(e) => setPayInvoice(e.target.value)}
                placeholder="z. B. R-2026-001"
                className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 font-mono text-sm" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Status</label>
              <select value={payStatus} onChange={(e) => setPayStatus(e.target.value)}
                className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2">
                <option value="open">Offen</option>
                <option value="invoiced">Rechnung gestellt</option>
                <option value="paid">Bezahlt</option>
                <option value="overdue">Überfällig</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Fälligkeit</label>
              <input type="date" value={payDue} onChange={(e) => setPayDue(e.target.value)}
                className="mt-2 w-full bg-transparent border-0 border-b border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2" />
            </div>
            <div className="flex items-end">
              <button onClick={() => updatePayment()} disabled={payBusy}
                className="w-full bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-4 py-3 text-xs uppercase tracking-[0.15em]">
                Speichern
              </button>
            </div>
          </div>
          {i.lexoffice_invoice_id && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <p data-testid={`inquiry-${i.id}-lexoffice-badge`} className="text-xs text-emerald-700 bg-emerald-500/10 inline-block px-3 py-1.5">
                ✓ Rechnung {i.invoice_number} in LexOffice erstellt &amp; an den Kunden versendet
                {i.invoice_auto_created_at ? ` (${formatDateDE(i.invoice_auto_created_at)})` : ""}
              </p>
              <button
                type="button"
                data-testid={`inquiry-${i.id}-resend-final-invoice`}
                disabled={resendBusy}
                onClick={() => resendFinalInvoice(false)}
                className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] disabled:opacity-60 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em]"
                title="Rechnung mit IBAN/Bankdaten erneut an den Kunden mailen (kein neuer LexOffice-Beleg)"
              >
                {resendBusy ? "Sende…" : "Erneut senden"}
              </button>
              <button
                type="button"
                data-testid={`inquiry-${i.id}-resend-final-invoice-corrected`}
                disabled={resendBusy}
                onClick={() => setCorrModal("final")}
                className="border border-amber-600 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-60 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em]"
                title="Berichtigte Kopie mit Steuernummer senden — gleiche Rechnungsnummer, Hinweis für die Buchhaltung"
              >
                {resendBusy ? "Sende…" : "⚖ Berichtigte Kopie (mit Steuernummer)"}
              </button>
            </div>
          )}
          {/* Schlussrechnung erstellen — sichtbar sobald keine Rechnung existiert und (keine Anzahlung oder Anzahlung erhalten).
              Time-Tracker-Bestätigung ist NICHT mehr zwingend Voraussetzung — für Fälle wo direkt abgerechnet wird. */}
          {!i.lexoffice_invoice_id
            && (!i.deposit_percent || i.deposit_received_at) && (
            <div className="mt-3">
              <button
                type="button"
                data-testid={`inquiry-${i.id}-create-final-invoice`}
                onClick={() => openInvoiceModal("final")}
                className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-5 py-2.5 text-xs uppercase tracking-[0.15em]"
              >
                📋 Schlussrechnung erstellen &amp; prüfen
              </button>
              <p className="text-xs text-[#757067] mt-2 font-light">
                {i.times_confirmed_at
                  ? "Positionen (aus bestätigten Time-Tracker-Stunden) werden zur Prüfung angezeigt."
                  : "⚠ Stunden wurden noch nicht bestätigt — Positionen werden aus Angebot/Buchung vorbelegt. Bitte vor Versand prüfen/anpassen."}
              </p>
            </div>
          )}
          {!i.lexoffice_invoice_id && i.deposit_percent > 0 && !i.deposit_received_at && (
            <p data-testid={`inquiry-${i.id}-invoice-waiting-deposit`} className="text-xs text-amber-900 mt-3 bg-amber-400/15 inline-block px-3 py-1.5">
              ⏳ Bitte zuerst &bdquo;Anzahlung erhalten&ldquo; markieren, bevor die Schlussrechnung erstellt werden kann.
            </p>
          )}
          {i.invoice_error && !i.lexoffice_invoice_id && (
            <p data-testid={`inquiry-${i.id}-invoice-error`} className="text-xs text-red-800 mt-3 bg-red-500/10 inline-block px-3 py-1.5">
              ⚠️ Automatische LexOffice-Rechnung fehlgeschlagen — bitte manuell erstellen ({i.invoice_error.slice(0, 120)})
            </p>
          )}
          {i.payment_received_at && (
            <p className="text-xs text-emerald-700 mt-3 italic">Bezahlt am {new Date(i.payment_received_at).toLocaleString("de-CH")}</p>
          )}

          {i.deposit_percent > 0 && (
            <div className="mt-6 pt-6 border-t border-[#E3DECE]">
              <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Anzahlung</p>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
                <span className="text-[#757067]">
                  <b className="text-[#2C2A26]">{(i.deposit_amount_eur || 0).toFixed(2).replace(".", ",")} €</b> ({i.deposit_percent} %)
                  &nbsp;·&nbsp; fällig {formatDateDE(i.deposit_due_date)}
                </span>
                {i.deposit_received_at ? (
                  <span className="bg-emerald-500/15 text-emerald-900 px-3 py-1 text-xs">
                    ✓ Erhalten am {formatDateDE(i.deposit_received_at)}
                  </span>
                ) : (
                  <>
                    {i.deposit_confirmed_by_customer_at && (
                      <span className="bg-amber-400/20 text-amber-900 px-3 py-1 text-xs">
                        ⏳ Kunde meldet Überweisung am {formatDateDE(i.deposit_confirmed_by_customer_at)}
                      </span>
                    )}
                    <button
                      type="button"
                      data-testid={`inquiry-${i.id}-mark-deposit-received`}
                      disabled={payBusy}
                      onClick={() => updatePayment({ deposit_received_at: new Date().toISOString() })}
                      className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-[0.15em]"
                    >
                      Anzahlung erhalten
                    </button>
                  </>
                )}
              </div>

              {/* NEU: Anzahlungsrechnung anfordern — sichtbar wenn Anzahlung eingetragen aber noch keine Rechnung versandt */}
              {/* Anzahlung anfordern — sichtbar wenn noch keine Rechnung versandt und Formular geschlossen */}
              {!i.deposit_invoice_id && !i.deposit_received_at && !showAddDeposit && (
                <div className="mt-3">
                  <button
                    type="button"
                    data-testid={`inquiry-${i.id}-open-deposit-request`}
                    onClick={openDepositModal}
                    className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold"
                  >
                    Anzahlung anfordern
                  </button>
                </div>
              )}

              {/* Anzahlungsrechnung-Status */}
              {(i.deposit_received_at || i.deposit_invoice_id || i.deposit_invoice_error) && (
                <div className="mt-3 text-xs">
                  {i.deposit_invoice_number ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-1.5">
                        ✓ Anzahlungsrechnung {i.deposit_invoice_number} versendet
                      </span>
                      <button
                        type="button"
                        data-testid={`inquiry-${i.id}-resend-deposit-invoice`}
                        disabled={resendBusy}
                        onClick={() => resendDepositInvoice(false)}
                        className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] disabled:opacity-60 px-3 py-1.5 uppercase tracking-[0.15em] text-[10px]"
                        title="Rechnung mit IBAN/Bankdaten erneut an den Kunden mailen (kein neuer LexOffice-Beleg)"
                      >
                        {resendBusy ? "Sende…" : "Erneut senden"}
                      </button>
                      <button
                        type="button"
                        data-testid={`inquiry-${i.id}-resend-deposit-invoice-corrected`}
                        disabled={resendBusy}
                        onClick={() => setCorrModal("deposit")}
                        className="border border-amber-600 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-60 px-3 py-1.5 uppercase tracking-[0.15em] text-[10px]"
                        title="Berichtigte Kopie mit Steuernummer senden — gleiche Rechnungsnummer, Hinweis für die Buchhaltung"
                      >
                        {resendBusy ? "Sende…" : "⚖ Berichtigte Kopie (mit Steuernummer)"}
                      </button>
                    </div>
                  ) : i.deposit_invoice_error ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className="inline-flex items-center gap-2 bg-red-50 border border-red-200 text-red-900 px-3 py-1.5"
                        title={i.deposit_invoice_error}
                      >
                        ⚠ Anzahlungsrechnung fehlgeschlagen
                      </span>
                      <button
                        type="button"
                        data-testid={`inquiry-${i.id}-retry-deposit-invoice`}
                        onClick={() => openInvoiceModal("deposit")}
                        className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-3 py-1.5 uppercase tracking-[0.15em] text-[10px]"
                      >
                        Erneut erstellen
                      </button>
                    </div>
                  ) : i.deposit_received_at ? (
                    <button
                      type="button"
                      data-testid={`inquiry-${i.id}-create-deposit-invoice`}
                      onClick={() => openInvoiceModal("deposit")}
                      className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-4 py-2 uppercase tracking-[0.15em]"
                    >
                      📋 Anzahlungsrechnung erstellen &amp; prüfen
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          )}

          {/* Anzahlung nachträglich hinzufügen — Trigger-Button nur bei bestätigten Aufträgen OHNE Anzahlung */}
          {isConfirmed && (i.deposit_percent || 0) === 0 && !i.lexoffice_invoice_id && !i.deposit_invoice_id && !showAddDeposit && (
            <div className="mt-6 pt-6 border-t border-[#E3DECE]">
              <button
                type="button"
                data-testid={`inquiry-${i.id}-add-deposit`}
                onClick={openDepositModal}
                className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold"
              >
                Anzahlung anfordern
              </button>
            </div>
          )}

          {/* Anzahlungs-Formular — rendert sobald showAddDeposit true ist (unabhängig vom bereits gesetzten deposit_percent) */}
          {showAddDeposit && isConfirmed && !i.lexoffice_invoice_id && !i.deposit_invoice_id && (
            <div className="mt-6 pt-6 border-t border-[#E3DECE]">
                <div className="bg-[#FAF5F1] border border-[#BA6D52] p-5">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">
                    {(i.deposit_percent || 0) > 0 ? "Anzahlung anfordern" : "Anzahlung nachtragen"}
                  </p>
                  <p className="text-xs text-[#757067] mt-1 font-light">
                    {(i.deposit_percent || 0) > 0
                      ? "Vorschau senden oder direkt an den Kunden verschicken. Werte können vorher angepasst werden."
                      : "Für später — z.\u00a0B. wenn Steuernummer & Gewerbe da sind und Anzahlung eingefordert werden soll."}
                  </p>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Anzahlung %</label>
                      <input
                        type="number" min="1" max="100" step="5"
                        data-testid={`inquiry-${i.id}-add-deposit-pct`}
                        value={addDepPct}
                        onChange={(e) => setAddDepPct(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1.5 px-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Fällig bis</label>
                      <input
                        type="date"
                        data-testid={`inquiry-${i.id}-add-deposit-due`}
                        value={addDepDue}
                        onChange={(e) => setAddDepDue(e.target.value)}
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1.5 px-2 text-sm"
                      />
                    </div>
                    <div className="text-sm text-[#757067]">
                      → Betrag: <b className="text-[#2C2A26]">
                        {((i.confirmed_gross_eur || 0) * Number(addDepPct || 0) / 100).toFixed(2).replace(".", ",")} €
                      </b>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2 flex-wrap">
                    <button
                      onClick={submitAddDepositAndSend}
                      disabled={addDepBusy}
                      data-testid={`inquiry-${i.id}-add-deposit-send`}
                      className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold"
                    >
                      {addDepBusy ? "Sende…" : "Jetzt Anzahlungsrechnung senden"}
                    </button>
                    <button
                      onClick={submitPreviewDepositEmail}
                      disabled={addDepBusy}
                      data-testid={`inquiry-${i.id}-add-deposit-preview`}
                      className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-[0.15em]"
                    >
                      Kopie an mich
                    </button>
                    <button
                      onClick={submitAddDeposit}
                      disabled={addDepBusy}
                      data-testid={`inquiry-${i.id}-add-deposit-save`}
                      className="border border-[#E3DECE] text-[#757067] hover:border-[#BA6D52] hover:text-[#BA6D52] disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-[0.15em]"
                    >
                      {addDepBusy ? "Speichere…" : "Nur nachtragen"}
                    </button>
                    <button
                      onClick={() => setShowAddDeposit(false)}
                      className="border border-[#E3DECE] hover:border-[#BA6D52] px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#757067]"
                    >
                      Abbrechen
                    </button>
                  </div>
                  <p className="text-[11px] text-[#757067] mt-3 font-light italic leading-relaxed">
                    <b>Kopie an mich</b>: Erstellt nur eine Test-Vorschau als PDF und mailt sie an dich — kein LexOffice, kein Kunde.<br/>
                    <b>Jetzt senden</b>: Erstellt sofort die Rechnung in LexOffice und mailt sie an den Kunden.<br/>
                    <b>Nur nachtragen</b>: Speichert nur die Werte — die Rechnung erstellst du später manuell.
                  </p>
                </div>
            </div>
          )}
        </div>
      )}

      {corrModal && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          data-testid="corrected-invoice-modal"
        >
          <div className="bg-[#FAFAF7] max-w-lg w-full p-6 md:p-8 border border-[#E3DECE]">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Berichtigte Rechnungskopie</p>
            <h3 className="font-serif text-2xl mt-2 text-[#2C2A26]">
              {corrModal === "deposit" ? i.deposit_invoice_number : i.invoice_number}
            </h3>
            <p className="text-sm text-[#575349] mt-4 leading-relaxed">
              Gleiche Rechnungsnummer, gleicher Betrag — ergänzt um die Steuernummer.
              PDF und E-Mail werden als <b>„Berichtigte Ausfertigung"</b> gekennzeichnet.
              Es wird <b>kein</b> neuer LexOffice-Beleg erzeugt.
            </p>
            <div className="mt-5 bg-[#FAF5F1] border-l-[3px] border-[#BA6D52] p-4 text-xs text-[#575349] leading-relaxed">
              <b>Kopie an mich</b>: schickt die Mail samt PDF nur an dich — der Kunde erhält nichts.<br />
              <b>An Kunden senden</b>: verschickt die berichtigte Rechnung an{" "}
              <b>{i.email || "—"}</b> (Kopie an dich per BCC).
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                data-testid="corrected-invoice-send-to-me"
                disabled={resendBusy}
                onClick={() => sendCorrectedCopy(corrModal, true)}
                className="flex-1 border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] disabled:opacity-60 px-4 py-3 text-xs uppercase tracking-[0.15em]"
              >
                {resendBusy ? "Sende…" : "Kopie an mich"}
              </button>
              <button
                type="button"
                data-testid="corrected-invoice-send-to-customer"
                disabled={resendBusy || !i.email}
                onClick={() => sendCorrectedCopy(corrModal, false)}
                className="flex-1 bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-4 py-3 text-xs uppercase tracking-[0.15em]"
              >
                {resendBusy ? "Sende…" : "An Kunden senden"}
              </button>
            </div>
            <button
              type="button"
              data-testid="corrected-invoice-cancel"
              disabled={resendBusy}
              onClick={() => setCorrModal(null)}
              className="mt-3 w-full text-[#757067] hover:text-[#2C2A26] disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-[0.15em]"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {invoiceModal && (
        <InvoicePreviewModal
          inquiry={i}
          type={invoiceModal.type}
          pwd={pwd}
          onClose={closeInvoiceModal}
          onSuccess={async () => {
            closeInvoiceModal();
            await onUpdated();
          }}
        />
      )}
    </div>
  );
}

// ---------- Rechnungs-Vorschau Modal (Positions-Editor) ----------
function InvoicePreviewModal({ inquiry, type, pwd, onClose, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [positionTypes, setPositionTypes] = useState([]);
  const [mwstRate, setMwstRate] = useState(19);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/admin/inquiries/${inquiry.id}/invoice-preview?type=${type}`, {
        headers: { "X-Admin-Password": pwd },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setLineItems(data.line_items || []);
        setPositionTypes(data.position_types || []);
        setMwstRate(data.mwst_rate_percent || 19);
      })
      .catch(() => toast.error("Vorschau konnte nicht geladen werden"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [inquiry.id, type, pwd]);

  const updateItem = (idx, patch) =>
    setLineItems((items) => items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => setLineItems((items) => items.filter((_, i) => i !== idx));
  const moveItem = (idx, direction) => {
    setLineItems((items) => {
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= items.length) return items;
      const copy = [...items];
      [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
      return copy;
    });
  };
  const addItem = () =>
    setLineItems((items) => [
      ...items,
      { type_key: "sonstiges", name: "", description: "", quantity: 1, unit_name: "Stück", unit_price_net: 0, tax_rate: mwstRate },
    ]);

  const { netSum, mwstSum, grossSum } = calcVatTotals(lineItems, mwstRate);

  const send = async () => {
    if (!lineItems.length) {
      toast.error("Mindestens eine Position erforderlich");
      return;
    }
    if (lineItems.some((it) => !it.name || !it.name.trim())) {
      toast.error("Alle Positionen brauchen einen Namen");
      return;
    }
    if (type === "final" && !window.confirm(
      `Schlussrechnung JETZT an ${inquiry.betrieb || "den Kunden"} versenden?\n\n` +
      `Positionen: ${lineItems.length}\n` +
      `Gesamt Brutto: ${grossSum.toFixed(2).replace(".", ",")} €\n\n` +
      `Die Rechnung wird in LexOffice erstellt und per E-Mail an den Kunden geschickt.`
    )) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/inquiries/${inquiry.id}/create-invoice`,
        { type, line_items: lineItems },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(type === "deposit" ? "Anzahlungsrechnung erstellt & versendet" : "Schlussrechnung erstellt & versendet");
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error("Fehler: " + detail);
    } finally {
      setSending(false);
    }
  };

  const sendPreview = async () => {
    if (!lineItems.length) {
      toast.error("Mindestens eine Position erforderlich");
      return;
    }
    if (lineItems.some((it) => !it.name || !it.name.trim())) {
      toast.error("Alle Positionen brauchen einen Namen");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/inquiries/${inquiry.id}/preview-final-invoice-email`,
        { line_items: lineItems },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(`Vorschau an ${res.data?.sent_to || "dich"} gesendet — bitte E-Mail prüfen`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error("Fehler: " + detail);
    } finally {
      setSending(false);
    }
  };

  const title = type === "deposit" ? "Anzahlungsrechnung — Vorschau" : "Schlussrechnung — Vorschau";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 md:p-6" data-testid="invoice-preview-modal">
      <div className="bg-white w-full max-w-4xl max-h-[94vh] overflow-y-auto border border-[#E3DECE]">
        <div className="sticky top-0 bg-white border-b border-[#E3DECE] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">{type === "deposit" ? "Anzahlung" : "Schlussrechnung"}</p>
            <h3 className="font-serif text-2xl text-[#2C2A26]">{title}</h3>
            <p className="text-xs text-[#757067] mt-1">
              {inquiry.betrieb} · {inquiry.email} · Auftrag {inquiry.confirmation_number || "—"}
            </p>
            {type === "final" && (
              <p className="text-[11px] text-[#BA6D52] mt-2 font-light italic">
                Tipp: Erst „Kopie an mich senden" → prüfen → dann „Jetzt an Kunde senden".
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="invoice-modal-close"
            className="text-[#757067] hover:text-[#2C2A26] text-2xl leading-none px-3"
          >
            ×
          </button>
        </div>

        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="animate-spin text-[#BA6D52]" />
          </div>
        ) : (
          <div className="p-6">
            <div className="space-y-4">
              {lineItems.map((it, idx) => (
                <div key={idx} className="border border-[#E3DECE] p-4 bg-[#FAFAF7]" data-testid={`invoice-line-${idx}`}>
                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#E3DECE]/60">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52] font-medium">
                      Position {idx + 1}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => moveItem(idx, -1)}
                        disabled={idx === 0}
                        data-testid={`invoice-line-${idx}-up`}
                        className="text-[#757067] hover:text-[#BA6D52] hover:bg-[#BA6D52]/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#757067] px-2 py-1 text-sm leading-none"
                        title="Position nach oben"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveItem(idx, 1)}
                        disabled={idx === lineItems.length - 1}
                        data-testid={`invoice-line-${idx}-down`}
                        className="text-[#757067] hover:text-[#BA6D52] hover:bg-[#BA6D52]/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#757067] px-2 py-1 text-sm leading-none"
                        title="Position nach unten"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        data-testid={`invoice-line-${idx}-remove`}
                        className="text-red-700 hover:text-red-900 hover:bg-red-50 px-2 py-1 text-lg leading-none ml-1"
                        title="Position entfernen"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-4">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Positionstyp</label>
                      <select
                        value={it.type_key || "sonstiges"}
                        data-testid={`invoice-line-${idx}-type`}
                        onChange={(e) => updateItem(idx, applyTypePreset(it, e.target.value, it.tax_rate ?? mwstRate))}
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
                      >
                        {positionTypes.map((t) => (
                          <option key={t.key} value={t.key}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-8">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Bezeichnung</label>
                      <input
                        type="text"
                        value={it.name || ""}
                        data-testid={`invoice-line-${idx}-name`}
                        onChange={(e) => updateItem(idx, { name: e.target.value })}
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Menge</label>
                      <input
                        type="number"
                        step="0.25"
                        value={it.quantity ?? 0}
                        data-testid={`invoice-line-${idx}-qty`}
                        onChange={(e) => updateItem(idx, { quantity: Number(e.target.value) })}
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Einheit</label>
                      <input
                        type="text"
                        value={it.unit_name || "Stück"}
                        onChange={(e) => updateItem(idx, { unit_name: e.target.value })}
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Netto/Einheit €</label>
                      <input
                        type="number"
                        step="0.01"
                        value={it.unit_price_net ?? 0}
                        data-testid={`invoice-line-${idx}-price`}
                        onChange={(e) => updateItem(idx, { unit_price_net: Number(e.target.value) })}
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <div className="text-right">
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Netto ges.</label>
                        <div className="mt-1 py-2 px-2 text-sm font-mono">
                          {(Number(it.quantity || 0) * Number(it.unit_price_net || 0)).toFixed(2).replace(".", ",")} €
                        </div>
                      </div>
                    </div>
                  </div>
                  {(it.description || it.type_key !== "sonstiges") && (
                    <div className="mt-2">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Beschreibung (optional)</label>
                      <input
                        type="text"
                        value={it.description || ""}
                        onChange={(e) => updateItem(idx, { description: e.target.value })}
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1.5 px-2 text-xs text-[#757067]"
                      />
                    </div>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={addItem}
                data-testid="invoice-add-position"
                className="w-full border border-dashed border-[#BA6D52] text-[#BA6D52] hover:bg-[#BA6D52]/5 py-3 uppercase tracking-[0.15em] text-xs"
              >
                + Position hinzufügen
              </button>
            </div>

            <div className="mt-6 border-t border-[#E3DECE] pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#757067]">Zwischensumme (Netto)</span>
                <span className="font-mono" data-testid="invoice-total-net">{netSum.toFixed(2).replace(".", ",")} €</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#757067]">zzgl. {mwstRate} % MwSt</span>
                <span className="font-mono" data-testid="invoice-total-mwst">{mwstSum.toFixed(2).replace(".", ",")} €</span>
              </div>
              <div className="flex justify-between text-base font-medium border-t border-[#E3DECE] pt-2">
                <span>Gesamt Brutto</span>
                <span className="font-serif text-xl text-[#BA6D52]" data-testid="invoice-total-gross">{grossSum.toFixed(2).replace(".", ",")} €</span>
              </div>
            </div>
          </div>
        )}

        <div className="sticky bottom-0 bg-white border-t border-[#E3DECE] px-6 py-4 flex flex-col-reverse md:flex-row justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            data-testid="invoice-modal-cancel"
            className="px-6 py-3 text-xs uppercase tracking-[0.15em] border border-[#E3DECE] hover:bg-[#FAFAF7] disabled:opacity-60"
          >
            Abbrechen
          </button>
          {type === "final" && (
            <button
              type="button"
              onClick={sendPreview}
              disabled={sending || loading || !lineItems.length}
              data-testid="invoice-modal-preview"
              className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2"
            >
              {sending && <Loader2 size={14} className="animate-spin" />}
              Kopie an mich senden
            </button>
          )}
          <button
            type="button"
            onClick={send}
            disabled={sending || loading || !lineItems.length}
            data-testid="invoice-modal-submit"
            className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2"
          >
            {sending && <Loader2 size={14} className="animate-spin" />}
            {type === "final" ? "Jetzt an Kunde senden" : "Rechnung erstellen & senden"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Offene Zahlungen Tab ----------
function PaymentsTab({ pwd }) {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    try {
      const res = await axios.get(`${API}/admin/open-payments`, { headers: { "X-Admin-Password": pwd } });
      setData(res.data);
    } catch {
      toast.error("Offene Zahlungen konnten nicht geladen werden");
    }
  };
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwd]);

  const markReceived = async (item) => {
    const key = item.inquiry_id + item.type;
    setBusyId(key);
    try {
      const headers = { "X-Admin-Password": pwd };
      if (item.type === "direct") {
        await axios.post(`${API}/admin/direct-invoices/${item.inquiry_id}/mark-paid`, {}, { headers });
        toast.success(`Rechnung ${item.reference} als bezahlt markiert`);
      } else if (item.type === "angebot") {
        await axios.post(`${API}/admin/angebotsseiten/${item.inquiry_id}/rechnung/bezahlt?bezahlt=true`,
          {}, { headers });
        toast.success(`Rechnung ${item.reference} als bezahlt markiert — der Kunde sieht das auf seiner Rechnungsseite`);
      } else {
      const body = item.type === "deposit"
        ? { deposit_received_at: new Date().toISOString() }
        : { payment_status: "paid" };
      await axios.put(`${API}/admin/inquiries/${item.inquiry_id}/payment`, body, { headers });
      toast.success(
        item.type === "deposit"
          ? "Anzahlung markiert — Schlussrechnung wird nach Stundenbestätigung automatisch erstellt & versendet"
          : "Als bezahlt markiert — Zahlungsbestätigung und Bewertungsanfrage wurden an den Kunden gesendet"
      );
      }
      await reload();
    } catch {
      toast.error("Fehler beim Aktualisieren");
    } finally {
      setBusyId(null);
    }
  };

  if (!data) return <p className="py-12 text-center text-[#757067]">Lade offene Zahlungen…</p>;

  return (
    <div data-testid="admin-payments-tab" className="pt-4">
      <div className="border border-[#E3DECE] bg-[#F2EFE9] p-6 flex flex-wrap justify-between items-baseline gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Erwartete Zahlungseingänge</p>
          <p className="text-sm text-[#757067] mt-1 font-light">
            Kontoabgleich in 30 Sekunden: Verwendungszweck auf dem Kontoauszug prüfen und mit einem Klick als erhalten markieren.
          </p>
        </div>
        <p className="font-serif text-4xl" data-testid="open-payments-total">
          {(data.total_expected_eur || 0).toFixed(2).replace(".", ",")} €
          <span className="text-sm text-[#757067] font-sans font-light ml-2">({data.count} Posten)</span>
        </p>
      </div>

      {data.items.length === 0 ? (
        <div className="py-16 text-center" data-testid="open-payments-empty">
          <p className="font-serif text-2xl text-[#2C2A26]">Alles bezahlt 🎉</p>
          <p className="text-sm text-[#757067] mt-2">Aktuell werden keine Zahlungseingänge erwartet.</p>
        </div>
      ) : (
        <div className="mt-6 border border-[#E3DECE] divide-y divide-[#E3DECE]">
          {data.items.map((item) => (
            <div
              key={item.inquiry_id + item.type}
              data-testid={`open-payment-${item.inquiry_id}-${item.type}`}
              className="p-5 flex flex-wrap items-center gap-4 bg-[#FAFAF7]"
            >
              <span className={`text-xs px-3 py-1 ${item.type === "deposit" ? "bg-amber-400/20 text-amber-900" : "bg-blue-500/15 text-blue-900"}`}>
                {item.type === "deposit" ? "Anzahlung"
                  : item.type === "direct" ? "Sammelrechnung"
                  : item.type === "angebot" ? "Rechnung (Angebot)" : "Rechnung"}
              </span>
              <div className="flex-1 min-w-[180px]">
                <p className="text-[#2C2A26] font-medium">{item.betrieb}</p>
                <p className="text-xs text-[#757067] mt-0.5">
                  Einsatz {formatDateDE(item.einsatzdatum)} · Verwendungszweck:{" "}
                  <span className="font-mono text-[#2C2A26]">{item.reference || "—"}</span>
                </p>
                {item.customer_reported && (
                  <p className="text-xs text-amber-800 mt-1">⏳ Kunde meldet: bereits überwiesen</p>
                )}
              </div>
              <div className="text-right">
                <p className="font-serif text-xl">{(item.amount_eur || 0).toFixed(2).replace(".", ",")} €</p>
                <p className={`text-xs mt-0.5 ${item.overdue ? "text-red-700 font-semibold" : "text-[#757067]"}`}>
                  {item.overdue ? "⚠ überfällig seit" : "fällig"} {formatDateDE(item.due_date)}
                </p>
              </div>
              <button
                data-testid={`open-payment-${item.inquiry_id}-${item.type}-mark-received`}
                disabled={busyId === item.inquiry_id + item.type}
                onClick={() => markReceived(item)}
                className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 px-4 py-2.5 text-xs uppercase tracking-[0.15em]"
              >
                ✓ Erhalten
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Statistik Tab ----------
function StatsTab({ pwd }) {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportMonth, setExportMonth] = useState("");
  const [reminding, setReminding] = useState(false);
  const [wiping, setWiping] = useState(false);

  const reloadStats = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`, {
        headers: { "X-Admin-Password": pwd },
      });
      setStats(data);
    } catch (err) {
      toast.error("Statistik konnte nicht geladen werden");
    }
    try {
      const { data } = await axios.get(`${API}/admin/analytics/summary`, {
        headers: { "X-Admin-Password": pwd },
      });
      setAnalytics(data);
    } catch (err) {
      /* Analytics-Fehler ist non-fatal */
    }
  };

  useEffect(() => {
    reloadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwd]);

  const wipeAll = async () => {
    const total = stats?.total_inquiries || 0;
    if (!window.confirm(`Wirklich ALLE ${total} Anfragen unwiderruflich löschen?\n\nAlle Daten (Statistik, Kunden, Bestätigungen, Bewertungen) werden zurückgesetzt.\n\nIm nächsten Schritt musst du die Bestätigungsphrase eingeben.`)) return;
    const phrase = window.prompt('Tippe zur Bestätigung exakt:\n\nALLE LOESCHEN');
    if (!phrase) return;
    setWiping(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/inquiries/wipe-all`,
        { confirm_phrase: phrase },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(`${data.deleted} Anfragen gelöscht`);
      await reloadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setWiping(false);
    }
  };

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportYear) params.set("year", exportYear);
      if (exportMonth) params.set("month", exportMonth);
      const url = `${API}/admin/export.csv?${params.toString()}`;
      const res = await axios.get(url, {
        headers: { "X-Admin-Password": pwd },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?(.+?)"?$/);
      link.download = m ? m[1] : `Auftraege_${exportYear}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV heruntergeladen");
    } catch (err) {
      toast.error("Export fehlgeschlagen");
    } finally {
      setExporting(false);
    }
  };

  const runReminders = async () => {
    if (!window.confirm("Erinnerungs-E-Mails manuell senden?")) return;
    setReminding(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/run-reminders`,
        {},
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(`Reminder gesendet: ${data.einsatz_reminders} Einsatz · ${data.time_reminders} Stunden · ${data.payment_overdue} Zahlungen`);
    } catch (err) {
      toast.error("Reminder fehlgeschlagen");
    } finally {
      setReminding(false);
    }
  };

  if (!stats) return <p className="py-12 text-center text-[#757067]">Lade Statistik…</p>;

  const fmt = (n) => (n || 0).toFixed(2).replace(".", ",") + " €";

  return (
    <div data-testid="stats-tab" className="space-y-12">
      <PendingInvoicingTile pwd={pwd} />
      <DocumentsTile pwd={pwd} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard label="Aufträge gesamt" value={stats.total_confirmed} sub={`von ${stats.total_inquiries} Anfragen`} />
        <StatCard label="Diesen Monat" value={stats.this_month.count} sub={`${stats.this_month.actual_hours.toFixed(1).replace('.', ',')} h erfasst`} />
        <StatCard label="Dieses Jahr" value={stats.this_year.count} sub={`${stats.this_year.actual_hours.toFixed(1).replace('.', ',')} h erfasst`} />
        <StatCard label="Umsatz dieser Monat" value={fmt(stats.this_month.actual_revenue || stats.this_month.planned_revenue)} sub="netto (geplant / tatsächlich)" />
        <StatCard label="Umsatz dieses Jahr" value={fmt(stats.this_year.actual_revenue || stats.this_year.planned_revenue)} sub="netto" />
        <StatCard label="Offene Zahlungen" value={fmt(stats.payments.open_amount)} sub={`${stats.payments.open_count} offen · ${stats.payments.paid_count} bezahlt`} highlight />
      </div>

      {/* Website-Besucher (analytics) */}
      {analytics && <AnalyticsSection analytics={analytics} />}

      {stats.top_customers && stats.top_customers.length > 0 && (
        <div className="bg-[#FAFAF7] border border-[#E3DECE] p-6 md:p-8">
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Top-Kunden (nach Umsatz)</p>
          <table className="w-full mt-4 text-sm">
            <thead className="text-xs uppercase tracking-wider text-[#BA6D52] border-b border-[#E3DECE]">
              <tr><th className="text-left py-2">Kunde</th><th className="text-right py-2">Aufträge</th><th className="text-right py-2">Umsatz</th></tr>
            </thead>
            <tbody>
              {stats.top_customers.map((c, idx) => (
                <tr key={idx} className="border-b border-[#E3DECE]">
                  <td className="py-2">{c.name}</td>
                  <td className="py-2 text-right">{c.count}</td>
                  <td className="py-2 text-right font-medium">{fmt(c.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-[#FAF5F1] border border-[#BA6D52] p-6 md:p-8">
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">CSV-Export für LexOffice / Finom</p>
        <p className="text-sm text-[#757067] mt-2 font-light">Lade alle bestätigten Aufträge als CSV-Datei herunter (Semikolon-getrennt, mit Kommas als Dezimaltrenner).</p>
        <div className="mt-4 flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Jahr</label>
            <input type="number" value={exportYear} onChange={(e) => setExportYear(e.target.value)} className="mt-2 bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2 w-28" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Monat (optional)</label>
            <select value={exportMonth} onChange={(e) => setExportMonth(e.target.value)} className="mt-2 bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2 w-32">
              <option value="">Alle</option>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => <option key={m} value={m}>{m.toString().padStart(2,"0")}</option>)}
            </select>
          </div>
          <button onClick={downloadCsv} disabled={exporting} data-testid="export-csv-btn" className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.2em]">
            {exporting ? "Wird erstellt…" : "CSV herunterladen"}
          </button>
        </div>
      </div>

      <div className="bg-[#FAFAF7] border border-[#E3DECE] p-6 md:p-8">
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Erinnerungs-System</p>
        <p className="text-sm text-[#757067] mt-2 font-light">Sendet automatisch Einsatz-Erinnerungen (1 Tag vorher), Stunden-Mahnungen (nach 7 Tagen) und Zahlungs-Erinnerungen (nach Fälligkeit). Du kannst jetzt manuell auslösen oder einen Cron-Job einrichten.</p>
        <button onClick={runReminders} disabled={reminding} className="mt-4 border border-[#BA6D52] text-[#BA6D52] hover:bg-[#BA6D52] hover:text-white disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.2em]">
          {reminding ? "Wird gesendet…" : "Reminder jetzt ausführen"}
        </button>
      </div>

      {/* Steuer-Rücklage */}
      <TaxReserveSection pwd={pwd} />

      <div className="bg-red-50 border-2 border-red-300 p-6 md:p-8">
        <p className="text-xs uppercase tracking-[0.25em] text-red-700 font-semibold">⚠ Gefahrenzone</p>
        <p className="text-sm text-[#757067] mt-2 font-light">
          Setzt <strong>alle</strong> Statistik-, Kunden- und Anfragedaten zurück. Vorher unbedingt CSV-Export
          erstellen! Diese Aktion ist <strong>nicht umkehrbar</strong>.
        </p>
        <button
          onClick={wipeAll}
          disabled={wiping}
          data-testid="wipe-all-btn"
          className="mt-4 border border-red-500 text-red-700 hover:bg-red-700 hover:text-white disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.2em]"
        >
          {wiping ? "Wird gelöscht…" : "Alle Daten zurücksetzen"}
        </button>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, highlight }) {
  return (
    <div className={`border ${highlight ? "border-[#BA6D52] bg-[#FAF5F1]" : "border-[#E3DECE] bg-[#FAFAF7]"} p-6`}>
      <p className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">{label}</p>
      <p className="font-serif text-3xl text-[#2C2A26] mt-3">{value}</p>
      {sub && <p className="text-xs text-[#757067] mt-2">{sub}</p>}
    </div>
  );
}

// ---------- Analytics Section (minimal & übersichtlich) ----------
function AnalyticsSection({ analytics }) {
  const today = analytics.today || { unique_visitors: 0, pageviews: 0 };
  const month = analytics.month || { unique_visitors: 0, pageviews: 0 };
  const topPages = analytics.top_pages || [];
  const [eigeneZaehlen, setEigeneZaehlen] = useState(
    () => localStorage.getItem(INTERN_KEY) !== "1");

  const pageLabel = (path) => {
    const map = {
      "/": "Startseite",
      "/leistungen": "Leistungen",
      "/preise": "Preise",
      "/anfrage": "Anfrage (Gastronomie)",
      "/privatkoch": "Privatkoch",
      "/anfrage-privat": "Anfrage (Privatkoch)",
      "/ueber-mich": "Über mich",
      "/kontakt": "Kontakt",
      "/impressum": "Impressum",
      "/datenschutz": "Datenschutz",
    };
    return map[path] || path;
  };

  return (
    <div data-testid="analytics-section" className="space-y-6">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Website-Besucher</p>
        <p className="text-[10px] text-[#757067] italic">Cookiefrei · DSGVO-konform</p>
      </div>

      <div className="border border-[#E3DECE] bg-white px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
        data-testid="analytics-eigene-besuche">
        <p className="text-xs text-[#757067]">
          {eigeneZaehlen
            ? "Deine eigenen Besuche werden auf diesem Gerät mitgezählt."
            : "Deine eigenen Besuche werden auf diesem Gerät nicht mitgezählt."}
        </p>
        <button
          type="button"
          onClick={() => {
            const neu = !eigeneZaehlen;
            localStorage.setItem(INTERN_KEY, neu ? "0" : "1");
            setEigeneZaehlen(neu);
            toast.success(neu ? "Eigene Besuche werden ab jetzt mitgezählt"
              : "Eigene Besuche werden ab jetzt ignoriert");
          }}
          data-testid="analytics-eigene-besuche-toggle"
          className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:text-[#BA6D52] hover:border-[#BA6D52] px-3 py-2"
        >
          {eigeneZaehlen ? "Nicht mitzählen" : "Doch mitzählen"}
        </button>
      </div>

      {/* Nur 2 grosse Karten: Heute + Monat */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-[#BA6D52] bg-[#FAF5F1] p-8" data-testid="analytics-today">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Heute</p>
          <p className="font-serif text-5xl text-[#2C2A26] mt-4">{today.unique_visitors}</p>
          <p className="text-sm text-[#757067] mt-2">
            {today.unique_visitors === 1 ? "Person hat" : "Personen haben"} deine Website besucht
          </p>
        </div>
        <div className="border border-[#E3DECE] bg-[#FAFAF7] p-8" data-testid="analytics-month">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Diesen Monat</p>
          <p className="font-serif text-5xl text-[#2C2A26] mt-4">{month.unique_visitors}</p>
          <p className="text-sm text-[#757067] mt-2">
            {month.unique_visitors === 1 ? "Person hat" : "Personen haben"} deine Website besucht
          </p>
        </div>
      </div>

      {/* Welche Seiten wurden aufgerufen */}
      <div className="bg-[#FAFAF7] border border-[#E3DECE] p-6" data-testid="analytics-pages-list">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#BA6D52] font-semibold mb-1">
          Welche Seiten wurden aufgerufen · Letzte 30 Tage
        </p>
        <p className="text-[11px] text-[#757067] italic mb-4">
          So siehst du, wofür sich deine Besucher am meisten interessieren.
        </p>
        {topPages.length > 0 ? (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-[#757067] border-b border-[#E3DECE]">
              <tr>
                <th className="text-left py-2">Seite</th>
                <th className="text-right py-2">Besucher</th>
              </tr>
            </thead>
            <tbody>
              {topPages.map((p, i) => (
                <tr key={i} className="border-b border-[#E3DECE]/60 hover:bg-white/60">
                  <td className="py-3">
                    <span className="font-medium text-[#2C2A26]">{pageLabel(p.path)}</span>
                    {pageLabel(p.path) !== p.path && (
                      <span className="text-[10px] text-[#757067] ml-2 font-mono">{p.path}</span>
                    )}
                  </td>
                  <td className="py-3 text-right font-mono text-[#2C2A26]">{p.unique}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-[#757067] italic py-4 text-center">
            Noch keine Seitenaufrufe in den letzten 30 Tagen.
          </p>
        )}
      </div>
    </div>
  );
}

// ---------- Steuer-Rücklage ----------
const LS_TAX_RATE_KEY = "stutz_admin_tax_rate";
const LS_KLEINUNTERNEHMER_KEY = "stutz_admin_kleinunternehmer";

function TaxReserveSection({ pwd }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taxRate, setTaxRate] = useState(() => {
    const stored = localStorage.getItem(LS_TAX_RATE_KEY);
    return stored ? Number(stored) : 30;
  });
  const [kleinunt, setKleinunt] = useState(() => localStorage.getItem(LS_KLEINUNTERNEHMER_KEY) === "1");
  const [yearFilter, setYearFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/tax-overview`, {
          headers: { "X-Admin-Password": pwd },
        });
        setData(data);
      } catch {
        toast.error("Steuerübersicht konnte nicht geladen werden");
      } finally {
        setLoading(false);
      }
    })();
  }, [pwd]);

  useEffect(() => { localStorage.setItem(LS_TAX_RATE_KEY, String(taxRate)); }, [taxRate]);
  useEffect(() => { localStorage.setItem(LS_KLEINUNTERNEHMER_KEY, kleinunt ? "1" : "0"); }, [kleinunt]);

  const eur = (n) => (n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

  const rows = useMemo(() => {
    const src = data?.rows || [];
    if (!yearFilter) return src;
    return src.filter((r) => (r.payment_received_at || r.einsatzdatum || "").startsWith(yearFilter));
  }, [data, yearFilter]);

  const totals = useMemo(() => {
    if (yearFilter) {
      return rows.reduce((a, r) => ({
        gross_eur: a.gross_eur + r.gross_eur,
        mwst_eur: a.mwst_eur + r.mwst_eur,
        net_total_eur: a.net_total_eur + r.net_total_eur,
        travel_net_eur: a.travel_net_eur + r.travel_net_eur,
        taxable_profit_eur: a.taxable_profit_eur + r.taxable_profit_eur,
        count: a.count + 1,
      }), { gross_eur: 0, mwst_eur: 0, net_total_eur: 0, travel_net_eur: 0, taxable_profit_eur: 0, count: 0 });
    }
    return data?.totals || { gross_eur: 0, mwst_eur: 0, net_total_eur: 0, travel_net_eur: 0, taxable_profit_eur: 0, count: 0 };
  }, [rows, yearFilter, data]);

  // Berechnungslogik
  const mwstAbfuehrung = kleinunt ? 0 : totals.mwst_eur;
  const reserveEuro = (totals.taxable_profit_eur * taxRate) / 100;
  const remainingProfit = totals.taxable_profit_eur - reserveEuro;

  return (
    <div className="border border-[#E3DECE] bg-[#FAFAF7] p-6 md:p-8" data-testid="tax-reserve-section">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <div>
          <h3 className="font-serif text-2xl md:text-3xl font-light tracking-tighter text-[#2C2A26]">Steuer-Rücklage</h3>
          <p className="text-sm text-[#757067] mt-2 font-light max-w-2xl">
            Hilft dir zu wissen, wieviel du nach jeder bezahlten Rechnung fürs Finanzamt zurücklegen solltest.
            Basierend auf allen als <b>{`„bezahlt"`}</b> markierten Aufträgen.
          </p>
        </div>
      </div>

      {/* Konfiguration */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 bg-white border border-[#E3DECE] p-5">
        <div>
          <label className="text-xs uppercase tracking-[0.15em] text-[#BA6D52] font-semibold">Persönlicher Steuersatz</label>
          <div className="mt-2 flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="45"
              step="1"
              value={taxRate}
              onChange={(e) => setTaxRate(Number(e.target.value))}
              data-testid="tax-rate-slider"
              className="flex-1 accent-[#BA6D52]"
            />
            <input
              type="number"
              min="0"
              max="60"
              value={taxRate}
              onChange={(e) => setTaxRate(Math.max(0, Math.min(60, Number(e.target.value) || 0)))}
              data-testid="tax-rate-input"
              className="w-16 border border-[#E3DECE] px-2 py-1 text-right text-sm"
            />
            <span className="text-sm text-[#2C2A26] font-medium">%</span>
          </div>
          <p className="text-[10px] text-[#757067] mt-2 font-light">
            Grober Mix aus Einkommensteuer + Soli (+ ggf. Kirchen-/Gewerbesteuer). Frag im Zweifel deinen Steuerberater.
          </p>
        </div>
        <div>
          <label htmlFor="kleinunt-check" className="text-xs uppercase tracking-[0.15em] text-[#BA6D52] font-semibold cursor-pointer">
            Umsatzsteuer-Status
          </label>
          <label htmlFor="kleinunt-check" className="mt-3 flex items-start gap-3 cursor-pointer">
            <input
              id="kleinunt-check"
              type="checkbox"
              checked={kleinunt}
              onChange={(e) => setKleinunt(e.target.checked)}
              data-testid="kleinunternehmer-check"
              className="mt-1 w-4 h-4 accent-[#BA6D52]"
            />
            <span className="text-xs text-[#2C2A26] font-light leading-tight">
              Kleinunternehmer (§ 19 UStG) — <b>keine MwSt-Abführung</b>
            </span>
          </label>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.15em] text-[#BA6D52] font-semibold">Zeitraum</label>
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            data-testid="tax-year-filter"
            className="mt-3 w-full border border-[#E3DECE] px-3 py-2 text-sm bg-white"
          >
            <option value="">Alle Zeiten ({data?.totals?.count || 0})</option>
            {(data?.by_year || []).map((y) => (
              <option key={y.year} value={y.year}>{y.year} ({y.count})</option>
            ))}
          </select>
        </div>
      </div>

      {/* Rechnungs-Zusammenfassung */}
      <div className="bg-white border border-[#E3DECE] divide-y divide-[#E3DECE]">
        <TaxLine label="Bruttoeinnahmen (was Kunden gezahlt haben)" value={eur(totals.gross_eur)} />
        {!kleinunt && (
          <TaxLine
            label="− MwSt (19 %) — geht ans Finanzamt"
            value={"− " + eur(mwstAbfuehrung)}
            sub="Umsatzsteuer-Voranmeldung"
            emphasize
          />
        )}
        <TaxLine label="= Nettoumsatz" value={eur(totals.net_total_eur)} />
        <TaxLine
          label="− Reisekosten (grobe Betriebsausgabe)"
          value={"− " + eur(totals.travel_net_eur)}
          sub="Kraftstoff, AfA, Versicherung o.ä."
        />
        <TaxLine label="= Steuerpflichtiger Gewinn (grob)" value={eur(totals.taxable_profit_eur)} bold />
        <TaxLine
          label={`− Steuerrücklage (${taxRate} %)`}
          value={"− " + eur(reserveEuro)}
          sub="Empfehlung fürs Steuerkonto"
          emphasize
          highlight
        />
        <div className="bg-emerald-50 px-5 py-4 flex justify-between items-baseline">
          <span className="text-sm text-emerald-900 font-medium">= Bleibt netto übrig</span>
          <span className="font-serif text-2xl text-emerald-800" data-testid="tax-remaining">{eur(remainingProfit)}</span>
        </div>
      </div>

      {/* Detail-Tabelle (aufklappbar) */}
      <details className="mt-6">
        <summary className="cursor-pointer text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold hover:text-[#9E5841]">
          Alle {rows.length} bezahlten Aufträge anzeigen
        </summary>
        <div className="mt-4 overflow-x-auto border border-[#E3DECE] bg-white">
          {loading ? (
            <p className="p-4 text-sm text-[#757067]">Lädt …</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-[#757067] italic">
              Noch keine als {`„bezahlt"`} markierten Aufträge vorhanden.
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-[#FAFAF7] border-b border-[#E3DECE]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-[#757067]">Datum</th>
                  <th className="text-left px-3 py-2 font-medium text-[#757067]">Kunde</th>
                  <th className="text-right px-3 py-2 font-medium text-[#757067]">Brutto</th>
                  <th className="text-right px-3 py-2 font-medium text-[#757067]">MwSt</th>
                  <th className="text-right px-3 py-2 font-medium text-[#757067]">Netto</th>
                  <th className="text-right px-3 py-2 font-medium text-[#757067]">Reise</th>
                  <th className="text-right px-3 py-2 font-medium text-[#757067]">Gewinn</th>
                  <th className="text-right px-3 py-2 font-medium text-[#BA6D52]">Rücklage</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const reserve = (r.taxable_profit_eur * taxRate) / 100;
                  return (
                    <tr key={r.id} className="border-b border-[#E3DECE] hover:bg-[#FAFAF7]">
                      <td className="px-3 py-2 text-[#757067]">
                        {r.payment_received_at
                          ? new Date(r.payment_received_at).toLocaleDateString("de-DE")
                          : (r.einsatzdatum ? new Date(r.einsatzdatum).toLocaleDateString("de-DE") : "—")}
                      </td>
                      <td className="px-3 py-2 text-[#2C2A26]">
                        {r.betrieb}
                        {r.confirmation_number && <span className="ml-2 text-[10px] text-[#757067]">({r.confirmation_number})</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-[#2C2A26]">{eur(r.gross_eur)}</td>
                      <td className="px-3 py-2 text-right text-[#757067]">{kleinunt ? "—" : eur(r.mwst_eur)}</td>
                      <td className="px-3 py-2 text-right text-[#2C2A26]">{eur(r.net_total_eur)}</td>
                      <td className="px-3 py-2 text-right text-[#757067]">{eur(r.travel_net_eur)}</td>
                      <td className="px-3 py-2 text-right text-[#2C2A26] font-medium">{eur(r.taxable_profit_eur)}</td>
                      <td className="px-3 py-2 text-right text-[#BA6D52] font-medium">{eur(reserve)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </details>

      <p className="text-[10px] text-[#757067] mt-4 font-light italic leading-relaxed">
        ⚠ Hinweis: Diese Berechnung ist eine grobe <b>Faustregel zur Rücklagenplanung</b>, keine Steuerberatung.
        Reisekosten sind hier nur ein Näherungswert für Betriebsausgaben (in echt kommen z. B. Materialkosten,
        Handykosten, Versicherungen, AfA hinzu). Für die reale Steuererklärung bitte deinen Steuerberater fragen.
      </p>

      {/* UStVA Export */}
      <UStVAExportBlock pwd={pwd} availableYears={(data?.by_year || []).map((y) => y.year)} />
    </div>
  );
}

function UStVAExportBlock({ pwd, availableYears }) {
  const currentYear = new Date().getFullYear();
  const yearOptions = availableYears?.length ? availableYears : [String(currentYear)];
  const [year, setYear] = useState(yearOptions[0]);
  const [period, setPeriod] = useState("full");
  const [busy, setBusy] = useState(false);

  const PERIOD_OPTIONS = [
    { value: "full", label: "Gesamtjahr" },
    { value: "Q1", label: "1. Quartal (Jan–Mär)" },
    { value: "Q2", label: "2. Quartal (Apr–Jun)" },
    { value: "Q3", label: "3. Quartal (Jul–Sep)" },
    { value: "Q4", label: "4. Quartal (Okt–Dez)" },
    { value: "M01", label: "Januar" }, { value: "M02", label: "Februar" }, { value: "M03", label: "März" },
    { value: "M04", label: "April" }, { value: "M05", label: "Mai" }, { value: "M06", label: "Juni" },
    { value: "M07", label: "Juli" }, { value: "M08", label: "August" }, { value: "M09", label: "September" },
    { value: "M10", label: "Oktober" }, { value: "M11", label: "November" }, { value: "M12", label: "Dezember" },
  ];

  const download = async (format) => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/admin/ustva-export`, {
        params: { year, period, format },
        headers: { "X-Admin-Password": pwd },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: format === "pdf" ? "application/pdf" : "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `UStVA_${year}_${period}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`UStVA ${format.toUpperCase()} für ${period} ${year} erstellt`);
    } catch {
      toast.error("Export fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 bg-white border border-[#E3DECE] p-5 md:p-6" data-testid="ustva-export-block">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <div>
          <h4 className="font-serif text-xl text-[#2C2A26]">Umsatzsteuer-Voranmeldung — Export</h4>
          <p className="text-xs text-[#757067] mt-1 font-light">
            Für die Meldung bei ELSTER oder deinen Steuerberater · <b>Ist-Versteuerung</b> (nach Zahlungseingang)
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">§ 18 UStG</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="text-xs uppercase tracking-[0.15em] text-[#757067]">Jahr</label>
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            data-testid="ustva-year"
            className="mt-1 w-full border border-[#E3DECE] px-3 py-2 text-sm bg-[#FAFAF7]"
          >
            {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.15em] text-[#757067]">Zeitraum</label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            data-testid="ustva-period"
            className="mt-1 w-full border border-[#E3DECE] px-3 py-2 text-sm bg-[#FAFAF7]"
          >
            {PERIOD_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => download("pdf")}
          disabled={busy}
          data-testid="ustva-export-pdf"
          className="inline-flex items-center gap-2 border border-[#2C2A26] text-[#2C2A26] hover:bg-[#2C2A26] hover:text-white disabled:opacity-60 px-5 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors"
        >
          {busy ? "Wird erstellt…" : "PDF herunterladen"}
        </button>
        <button
          onClick={() => download("csv")}
          disabled={busy}
          data-testid="ustva-export-csv"
          className="inline-flex items-center gap-2 border border-[#BA6D52] text-[#BA6D52] hover:bg-[#BA6D52] hover:text-white disabled:opacity-60 px-5 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors"
        >
          {busy ? "Wird erstellt…" : "CSV (für Steuerberater)"}
        </button>
      </div>

      <p className="text-[10px] text-[#757067] mt-4 font-light italic">
        Der Report enthält Zeile 81 (Netto-Umsätze zu 19 %) + entstehende Umsatzsteuer (Zeile 83).
        Vorsteuer aus Eingangsrechnungen (Zeile 66) musst du separat ergänzen.
      </p>
    </div>
  );
}

function TaxLine({ label, value, sub, emphasize, bold, highlight }) {
  return (
    <div className={`px-5 py-3 flex flex-wrap items-baseline justify-between gap-2 ${highlight ? "bg-[#FAF5F1]" : ""}`}>
      <div>
        <p className={`text-sm ${bold ? "text-[#2C2A26] font-semibold" : emphasize ? "text-[#BA6D52] font-medium" : "text-[#2C2A26] font-light"}`}>{label}</p>
        {sub && <p className="text-[10px] text-[#757067] mt-0.5">{sub}</p>}
      </div>
      <p className={`text-sm ${bold ? "font-serif text-lg text-[#2C2A26]" : emphasize ? "text-[#BA6D52] font-medium" : "text-[#2C2A26]"}`}>{value}</p>
    </div>
  );
}

// ---------- Kunden Tab ----------
function CustomersTab({ pwd }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/customers`, {
        headers: { "X-Admin-Password": pwd },
      });
      setCustomers(data.customers || []);
    } catch (err) {
      toast.error("Kundenliste konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwd]);

  const deleteCustomer = async (c) => {
    if (!window.confirm(`Alle ${c.inquiry_count} Anfragen von „${c.betrieb || c.email}" unwiderruflich löschen?\n\nE-Mail: ${c.email}\n\nDieser Schritt kann nicht rückgängig gemacht werden.`)) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/customers/delete-by-email`,
        { email: c.email },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success(`${data.deleted} Anfragen gelöscht`);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  if (loading) return <p className="py-12 text-center text-[#757067]">Lade Kunden…</p>;
  if (!customers.length) return <p className="py-12 text-center text-[#757067]">Noch keine Kunden vorhanden.</p>;

  return (
    <div data-testid="customers-tab" className="border border-[#E3DECE] bg-[#FAFAF7] overflow-x-auto">
      <table className="w-full text-sm min-w-[700px]">
        <thead className="bg-[#F2EFE9] text-xs uppercase tracking-wider text-[#BA6D52]">
          <tr>
            <th className="text-left p-4">Kunde</th>
            <th className="text-left p-4">Kontakt</th>
            <th className="text-right p-4">Anfragen</th>
            <th className="text-right p-4">Bestätigt</th>
            <th className="text-right p-4">Umsatz</th>
            <th className="text-left p-4">Letzte Anfrage</th>
            <th className="text-right p-4">Aktion</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((c, idx) => (
            <tr key={idx} className="border-t border-[#E3DECE]">
              <td className="p-4 font-medium">{c.betrieb || "—"}</td>
              <td className="p-4">
                <a href={`mailto:${c.email}`} className="text-[#BA6D52] hover:underline block">{c.email}</a>
                <span className="text-xs text-[#757067]">{c.telefon}</span>
              </td>
              <td className="p-4 text-right">{c.inquiry_count}</td>
              <td className="p-4 text-right">
                {c.confirmed_count > 0 ? (
                  <span className="bg-emerald-500/15 text-emerald-900 px-2 py-1 text-xs">{c.confirmed_count}</span>
                ) : (
                  <span className="text-[#757067]">0</span>
                )}
              </td>
              <td className="p-4 text-right font-medium">{(c.total_revenue || 0).toFixed(2).replace(".", ",")} €</td>
              <td className="p-4 text-xs text-[#757067]">{formatDateDE(c.last_date)}</td>
              <td className="p-4 text-right">
                <button
                  type="button"
                  onClick={() => deleteCustomer(c)}
                  data-testid={`customer-${idx}-delete`}
                  className="text-xs uppercase tracking-[0.15em] text-[#757067] hover:text-red-700 transition"
                  title="Alle Anfragen dieses Kunden löschen"
                >
                  ✕ Löschen
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DirectInvoicesTab({ pwd, prefill, onPrefillUsed }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { if (prefill) setShowCreate(true); }, [prefill]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/direct-invoices`, {
        headers: { "X-Admin-Password": pwd },
      });
      setInvoices(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchInvoices(); }, [pwd]);

  useEffect(() => {
    const hasProcessing = (invoices || []).some((i) => i.status === "processing");
    if (!hasProcessing) return;
    const iv = setInterval(fetchInvoices, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);

  const badge = (s) => {
    const map = {
      processing: ["bg-amber-50 text-amber-900 border-amber-200 animate-pulse", "Wird erstellt …"],
      sent: ["bg-emerald-50 text-emerald-900 border-emerald-200", "Versendet"],
      paid: ["bg-[#EDF5EE] text-[#2F6B3C] border-[#BFD9C4]", "✓ Bezahlt"],
      send_failed: ["bg-red-50 text-red-900 border-red-200", "⚠ Versand fehlgeschl."],
      cancelled: ["bg-[#F2EFE9] text-[#757067] border-[#E3DECE] line-through", "Storniert"],
      test: ["bg-[#FAF5F1] text-[#8B7A68] border-[#E3DECE]", "Testrechnung"],
    };
    const [cls, label] = map[s] || ["bg-gray-100 text-gray-700 border-gray-300", s];
    return <span className={`inline-block px-3 py-1 text-[10px] uppercase tracking-wider border ${cls}`}>{label}</span>;
  };

  const fmtDT = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div data-testid="direct-invoices-tab" className="space-y-6">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Direkt-Rechnung</p>
          <p className="text-sm text-[#757067] mt-1 max-w-2xl">
            Für spontane Einsätze ohne vorheriges Angebot — z.&nbsp;B. wenn eine Bekannte am nächsten Tag Hilfe braucht.
            Rechnung wird sofort in LexOffice erstellt und im gebrandeten Stil an den Kunden geschickt.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="direct-invoice-new"
          className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-6 py-3 text-xs uppercase tracking-[0.15em]"
        >
          + Neue Direkt-Rechnung
        </button>
      </div>

      {loading && <p className="text-sm text-[#757067]">Lade …</p>}
      {!loading && invoices.length === 0 && (
        <p className="text-sm text-[#757067] italic py-8 text-center border border-dashed border-[#E3DECE]">
          Noch keine Direkt-Rechnungen. Klicke oben rechts, um deine erste zu erstellen.
        </p>
      )}

      <div className="grid grid-cols-1 gap-3">
        {invoices.map((i) => {
          const istTest = i.status === "test";
          const canDelete = istTest || (!i.invoice_number && !i.lex_invoice_id);
          return (
          <div key={i.id} data-testid={`direct-invoice-${i.id}`} className="border border-[#E3DECE] bg-white p-5 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 flex-wrap mb-1">
                <span className="text-[10px] font-mono text-[#BA6D52]">{i.invoice_number || "—"}</span>
                {badge(i.status)}
              </div>
              <p className="font-serif text-lg text-[#2C2A26]">{i.betrieb}</p>
              <p className="text-xs text-[#757067]">{i.email}</p>
              <p className="text-[11px] text-[#757067] mt-1">
                {fmtDT(i.created_at)} · {(i.total_gross_eur || 0).toFixed(2).replace(".", ",")} € brutto
              </p>
              {istTest && (
                <p className="text-[11px] text-[#8B7A68] mt-1" data-testid={`direct-invoice-${i.id}-test-note`}>
                  Testrechnung aus „Testmail an mich" — zählt nirgends mit, kein LexOffice-Beleg.
                  Mit „Löschen" verschwindet sie samt Rechnungsseite.
                </p>
              )}
              {i.status === "cancelled" && (
                <p className="text-[11px] text-[#BA6D52] mt-1">
                  {i.credit_note_number
                    ? `Storniert durch Rechnungskorrektur ${i.credit_note_number} — offener Betrag 0 €, kein Geld zurück`
                    : "Im Portal storniert — Storno-Beleg in LexOffice noch selbst erstellen"}
                </p>
              )}
              {i.status === "paid" && (
                <p className="text-[11px] text-[#2F6B3C] mt-1" data-testid={`direct-invoice-${i.id}-paid-note`}>
                  Zahlung erhalten{i.paid_at ? ` am ${i.paid_at.split("-").reverse().join(".")}` : ""}
                  {i.payment_thankyou_sent_at ? " · Bestätigung an Kunde gesendet" : " · keine Kunden-Mail versendet"}
                </p>
              )}
            </div>
            {i.invoice_number && i.status !== "cancelled" && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    const { data } = await axios.post(
                      `${API}/admin/direct-invoices/${i.id}/public-link`, {},
                      { headers: { "X-Admin-Password": pwd } });
                    await navigator.clipboard?.writeText(data.url);
                    toast.success("Kundenlink kopiert — der Chef kann die Rechnung dort selbst herunterladen", { duration: 7000 });
                    window.open(data.url, "_blank", "noopener");
                  } catch (err) {
                    toast.error("Fehler: " + fehlerText(err));
                  }
                }}
                data-testid={`direct-invoice-${i.id}-public-link`}
                title="Rechnungsseite für den Kunden öffnen und Link kopieren"
                className="text-[10px] uppercase tracking-[0.15em] text-[#757067] hover:text-[#BA6D52] px-3 py-2 border border-[#E3DECE] shrink-0"
              >
                Rechnungsseite
              </button>
            )}
            {i.invoice_number && i.status !== "cancelled" && !istTest && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    const { data } = await axios.post(
                      `${API}/admin/direct-invoices/${i.id}/public-link`, {},
                      { headers: { "X-Admin-Password": pwd } });
                    await navigator.clipboard?.writeText(data.url);
                    const mitNummer = waOeffnen(i.telefon, rechnungWaText({
                      ansprechpartner: i.ansprechpartner || "",
                      nummer: i.invoice_number, url: data.url,
                    }));
                    toast.success(mitNummer
                      ? "WhatsApp geöffnet — Nachricht mit Rechnungslink ist vorbereitet"
                      : "WhatsApp geöffnet — Kontakt noch auswählen (keine Nummer hinterlegt). Link ist kopiert.",
                      { duration: 8000 });
                  } catch (err) {
                    toast.error("Fehler: " + fehlerText(err));
                  }
                }}
                data-testid={`direct-invoice-${i.id}-whatsapp`}
                title="Rechnungslink per WhatsApp an den Kunden senden"
                className="text-[10px] uppercase tracking-[0.15em] text-[#25632F] hover:bg-[#EDF5EE] px-3 py-2 border border-[#CFE0C3] shrink-0"
              >
                WhatsApp
              </button>
            )}
            {i.invoice_number && i.status !== "cancelled" && i.status !== "paid" && !istTest && (
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm(
                    `Zahlung für Rechnung ${i.invoice_number} (${i.betrieb}) als erhalten markieren?\n\n` +
                    `• Die Rechnung wird im Portal auf „Bezahlt" gesetzt\n` +
                    `• Der Kunde bekommt eine kurze Bestätigung per E-Mail\n` +
                    `• In LexOffice musst du die Zahlung noch selbst eintragen`)) return;
                  try {
                    const { data } = await axios.post(`${API}/admin/direct-invoices/${i.id}/mark-paid`,
                      { notify_customer: true },
                      { headers: { "X-Admin-Password": pwd } });
                    toast.success(data.mail_sent
                      ? `Als bezahlt markiert — Bestätigung an ${data.mail_to} gesendet`
                      : "Als bezahlt markiert (keine E-Mail versendet)", { duration: 7000 });
                    if (data.lexoffice_hint) toast(data.lexoffice_hint, { duration: 8000 });
                    fetchInvoices();
                  } catch (err) {
                    toast.error("Fehler: " + fehlerText(err));
                  }
                }}
                data-testid={`direct-invoice-${i.id}-mark-paid`}
                title="Zahlungseingang markieren und dem Kunden eine Bestätigung senden"
                className="text-[10px] uppercase tracking-[0.15em] text-[#2F6B3C] hover:bg-[#EDF5EE] px-3 py-2 border border-[#2F6B3C] shrink-0"
              >
                ✓ Zahlung erhalten
              </button>
            )}
            {i.invoice_number && i.status !== "cancelled" && !istTest && (
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm(
                    `Rechnung ${i.invoice_number} (${i.betrieb}) stornieren?\n\n` +
                    `• Die Rechnung wird im Portal als storniert markiert\n` +
                    `• Die zugehörigen Stundennachweise werden wieder abrechenbar\n` +
                    `• Der Kunde wird NICHT automatisch informiert`)) return;
                  const withLex = i.lex_invoice_id
                    ? window.confirm(
                        `Auch in LexOffice stornieren?\n\n` +
                        `OK = LexOffice erstellt die Rechnungskorrektur (Storno-Beleg) zu ${i.invoice_number}.\n` +
                        `Der offene Betrag wird dadurch auf 0 € gesetzt — es fließt KEIN Geld zurück.\n` +
                        `Der Beleg ist endgültig und kann nicht zurückgenommen werden.\n\n` +
                        `Abbrechen = nur im Portal stornieren, LexOffice machst du selbst.`)
                    : false;
                  try {
                    const { data } = await axios.post(`${API}/admin/direct-invoices/${i.id}/cancel`,
                      { lexoffice_credit_note: withLex },
                      { headers: { "X-Admin-Password": pwd } });
                    toast.success(`Storniert — ${data.freed_shifts} Stundennachweis(e) wieder abrechenbar`, { duration: 7000 });
                    if (data.credit_note_number) toast.success(`LexOffice-Rechnungskorrektur ${data.credit_note_number} erstellt — alte Rechnung auf 0 € gesetzt`, { duration: 9000 });
                    if (data.lexoffice_hint) toast(data.lexoffice_hint, { duration: 9000 });
                    fetchInvoices();
                  } catch (err) {
                    toast.error("Fehler: " + fehlerText(err));
                  }
                }}
                data-testid={`direct-invoice-${i.id}-cancel`}
                title="Rechnung im Portal stornieren und Stunden wieder freigeben"
                className="text-[10px] uppercase tracking-[0.15em] text-[#BA6D52] hover:bg-[#FAF5F1] px-3 py-2 border border-[#BA6D52] shrink-0"
              >
                Stornieren
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm(`Direktrechnung „${i.betrieb}" (${(i.total_gross_eur || 0).toFixed(2)} €) wirklich löschen?\n\nHinweis: In LexOffice existiert noch KEIN Beleg — der Eintrag wird nur aus deinem Portal entfernt.`)) return;
                  try {
                    await axios.delete(`${API}/admin/direct-invoices/${i.id}`, { headers: { "X-Admin-Password": pwd } });
                    toast.success("Direktrechnung gelöscht");
                    fetchInvoices();
                  } catch (err) {
                    toast.error("Fehler: " + fehlerText(err));
                  }
                }}
                data-testid={`direct-invoice-${i.id}-delete`}
                title="Diese Direktrechnung löschen (nur wenn noch kein LexOffice-Beleg existiert)"
                className="text-[10px] uppercase tracking-[0.15em] text-red-700 hover:bg-red-50 px-3 py-2 border border-red-200 shrink-0"
              >
                🗑 Löschen
              </button>
            )}
          </div>
          );
        })}
      </div>

      {showCreate && (
        <DirectInvoiceCreateModal
          pwd={pwd}
          initial={prefill}
          onClose={() => { setShowCreate(false); onPrefillUsed?.(); }}
          onSuccess={() => { setShowCreate(false); onPrefillUsed?.(); fetchInvoices(); }}
        />
      )}
    </div>
  );
}


// ---------- Modal: Neue Direkt-Rechnung ----------
function DirectInvoiceCreateModal({ pwd, onClose, onSuccess, initial }) {
  const STORAGE_KEY = "lstz.direct_invoice_draft.v1";
  const DEFAULT_FORM = {
    betrieb: "", ansprechpartner: "", email: "", telefon: "",
    ort: "", einsatzdatum: toYMDLocal(),
    leistungszeitraum: "",
    notes: "", admin_notes: "",
  };
  const DEFAULT_ITEMS = [
    { type_key: "mietkoch_werktag", name: "Mietkoch-Stunden", description: "", quantity: 4, unit_name: "Stunde", unit_price_net: 45, tax_rate: MWST_RATE_PERCENT },
  ];
  const load = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch { return null; } };
  const saved = load();

  const [form, setForm] = useState(initial?.form || saved?.form || DEFAULT_FORM);
  const [lineItems, setLineItems] = useState(initial?.lineItems || saved?.lineItems || DEFAULT_ITEMS);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ form, lineItems })); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, lineItems]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateItem = (i, patch) => setLineItems((l) => l.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const addItem = () => setLineItems((l) => [...l, { type_key: "sonstiges", name: "", description: "", quantity: 1, unit_name: "Stück", unit_price_net: 0, tax_rate: MWST_RATE_PERCENT }]);
  const removeItem = (i) => setLineItems((l) => l.filter((_, idx) => idx !== i));

  // --- Anzahlung abziehen (Schlussrechnung = Gesamtleistung minus bereits gezahlter Anzahlung) ---
  const [depNr, setDepNr] = useState("");
  const [depAmount, setDepAmount] = useState("");
  const [depMode, setDepMode] = useState("brutto");
  const hasDeposit = lineItems.some((it) => it.type_key === "anzahlung_abzug");
  const addDepositItem = () => {
    const amount = Number(String(depAmount).replace(",", "."));
    if (!amount || amount <= 0) { toast.error("Bitte den Betrag der Anzahlung eingeben"); return; }
    const rate = Number(lineItems[0]?.tax_rate ?? MWST_RATE_PERCENT);
    const net = depMode === "brutto"
      ? Math.round((amount / (1 + rate / 100)) * 100) / 100
      : Math.round(amount * 100) / 100;
    const vat = Math.round(net * (rate / 100) * 100) / 100;
    const gross = Math.round((net + vat) * 100) / 100;
    const eur = (v) => v.toFixed(2).replace(".", ",");
    setLineItems((l) => [...l, {
      type_key: "anzahlung_abzug",
      name: `Abzüglich erhaltener Anzahlung${depNr.trim() ? ` (Rechnung ${depNr.trim()})` : ""}`,
      description: `Bereits gezahlte Anzahlung: ${eur(net)} € netto zzgl. ${eur(vat)} € USt (${rate} %) = ${eur(gross)} € brutto — wird mit dieser Schlussrechnung verrechnet`,
      quantity: 1,
      unit_name: "pauschal",
      unit_price_net: -net,
      tax_rate: rate,
    }]);
    setDepAmount("");
    toast.success(`Anzahlung verrechnet — ${net.toFixed(2).replace(".", ",")} € netto abgezogen`);
  };

  const clearDraft = () => {
    if (!window.confirm("Formular komplett zurücksetzen?")) return;
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setForm(DEFAULT_FORM); setLineItems(DEFAULT_ITEMS);
    toast.success("Entwurf zurückgesetzt");
  };

  // Netto/MwSt/Brutto exakt wie LexOffice (MwSt je Steuersatz-Gruppe, kaufmännisch gerundet)
  const { netSum, mwstSum, grossSum } = calcVatTotals(lineItems, MWST_RATE_PERCENT);
  const aktuellerSatz = Math.round(Number(lineItems[0]?.tax_rate ?? MWST_RATE_PERCENT));

  const previewPdf = async () => {
    if (!form.betrieb || !form.email || !form.ort) {
      toast.error("Bitte Kunden-Angaben, E-Mail und Adresse ausfüllen");
      return;
    }
    if (netSum <= 0) { toast.error("Rechnungssumme muss > 0 € sein"); return; }
    setPreviewing(true);
    try {
      const { data } = await axios.post(`${API}/admin/direct-invoices/preview-pdf`,
        { ...form, line_items: lineItems, shift_ids: [] },
        { headers: { "X-Admin-Password": pwd }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const win = window.open(url, "_blank", "noopener");
      if (!win) {
        const a = document.createElement("a");
        a.href = url; a.download = "Rechnungsvorschau.pdf"; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error("Vorschau fehlgeschlagen — bitte Eingaben prüfen");
    } finally { setPreviewing(false); }
  };

  const testMail = async () => {
    if (!form.betrieb || !form.email || !form.ort) {
      toast.error("Bitte Kunden-Angaben, E-Mail und Adresse ausfüllen");
      return;
    }
    if (netSum <= 0) { toast.error("Rechnungssumme muss > 0 € sein"); return; }
    if (!window.confirm("Die fertige Rechnungsmail wird NUR an dich geschickt — mit Button zur Rechnungsseite, ohne Anhang.\n\nKein LexOffice-Beleg — der Kunde bekommt nichts.")) return;
    setTesting(true);
    try {
      const shiftIds = initial?.shiftIds || (initial?.shiftId ? [initial.shiftId] : []);
      const { data } = await axios.post(`${API}/admin/direct-invoices/test-mail`,
        { ...form, line_items: lineItems, shift_ids: shiftIds },
        { headers: { "X-Admin-Password": pwd } });
      toast.success(
        `Testmail an ${data.sent_to} unterwegs — ohne Anhang, mit Button zur Rechnungsseite${data.nachweise ? ` (${data.nachweise} Nachweise im PDF)` : ""}`,
        { duration: 9000 });
      if (data.public_url) window.open(data.public_url, "_blank", "noopener");
    } catch (err) {
      toast.error("Testmail fehlgeschlagen: " + (err.response?.data?.detail || err.message));
    } finally { setTesting(false); }
  };

  const submit = async () => {
    if (!form.betrieb || !form.email || !form.ort) {
      toast.error("Bitte Kunden-Angaben, E-Mail und Adresse ausfüllen");
      return;
    }
    if (netSum <= 0) { toast.error("Rechnungssumme muss > 0 € sein"); return; }
    const hasTravel = lineItems.some((it) =>
      /fahrt|anfahrt|kilometer|\bkm\b/i.test(`${it.name} ${it.type_key || ""}`));
    if (!hasTravel && !window.confirm("Keine An-/Abfahrt enthalten — trotzdem senden?\n\nTipp: Position „Fahrtkostenpauschale“ ergänzen, bevor die Rechnung rausgeht.")) return;
    setSending(true);
    try {
      const shiftIds = initial?.shiftIds || (initial?.shiftId ? [initial.shiftId] : []);
      await axios.post(`${API}/admin/direct-invoices`, { ...form, line_items: lineItems, shift_ids: shiftIds },
        { headers: { "X-Admin-Password": pwd } });
      const shiftIds2 = initial?.shiftIds || (initial?.shiftId ? [initial.shiftId] : []);
      for (const sid of shiftIds2) {
        try {
          await axios.post(`${API}/admin/stundennachweise/${sid}/invoiced`, {},
            { headers: { "X-Admin-Password": pwd } });
        } catch { /* Nachweis-Status ist nur informativ */ }
      }
      toast.success("Rechnung wird erstellt & an Kunde versendet — du bekommst gleich eine Bestätigungsmail", { duration: 6000 });
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      onSuccess();
    } catch (err) {
      toast.error("Fehler: " + fehlerText(err));
    } finally { setSending(false); }
  };

  const TYPE_OPTIONS = [
    ["mietkoch_werktag", "Mietkoch-Stunden Werktag"],
    ["mietkoch_we", "Mietkoch-Stunden Wochenende"],
    ["fahrtpauschale", "Fahrtkostenpauschale"],
    ["km_pauschale", "Kilometerpauschale"],
    ["uebernachtung", "Übernachtung"],
    ["material", "Material / Einkauf"],
    ["privatkoch_planung", "Planung & Vorbereitung des Menüs"],
    ["privatkoch_einkauf", "Einkauf & Warenbeschaffung"],
    ["privatkoch_zutaten", "Lebensmittel & Zutaten (laut Beleg)"],
    ["privatkoch_zubereitung", "Zubereitung vor Ort als Privatkoch"],
    ["privatkoch_reinigung", "Reinigung des Arbeitsplatzes / Küche"],
    ["anzahlung_abzug", "Abzug bereits gezahlter Anzahlung"],
    ["sonstiges", "Sonstige Position"],
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 md:p-6" data-testid="direct-invoice-modal">
      <div className="bg-white w-full max-w-3xl max-h-[94vh] overflow-y-auto border border-[#E3DECE]">
        <div className="sticky top-0 bg-white border-b border-[#E3DECE] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Direkt-Rechnung</p>
            <h3 className="font-serif text-2xl text-[#2C2A26]">Neue Rechnung schreiben</h3>
            <p className="text-xs text-[#757067] mt-1">Ohne Angebot — direkt an den Kunden. Eingaben werden automatisch gespeichert.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={clearDraft} data-testid="direct-invoice-reset"
              className="text-[10px] uppercase tracking-[0.15em] text-red-700 hover:text-red-900 hover:bg-red-50 px-3 py-2 border border-red-200">
              ⟲ Zurücksetzen
            </button>
            <button onClick={onClose} className="text-[#757067] hover:text-[#2C2A26] text-2xl leading-none px-3">×</button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {(initial?.shiftIds?.length || initial?.shiftId) && (
            <div className="border border-[#BA6D52] bg-[#FAF5F1] px-4 py-3 text-sm text-[#2C2A26]"
              data-testid="direct-invoice-shift-note">
              {initial?.shiftIds?.length || 1} bestätigte Stundennachweise werden als PDF an die
              Rechnungsmail angehängt — der Kunde bekommt seine Bestätigung direkt mit.
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52] mb-3 font-semibold">Kunde</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <CustomerPicker
                pwd={pwd}
                value={form.betrieb}
                onChange={(v) => setField("betrieb", v)}
                onSelect={(k) => setForm((f) => ({
                  ...f,
                  betrieb: k.betrieb,
                  ansprechpartner: k.ansprechpartner || f.ansprechpartner,
                  email: k.email || f.email,
                  telefon: k.telefon || f.telefon,
                  ort: [k.strasse, [k.plz, k.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ") || f.ort,
                }))}
                placeholder="Betrieb / Name des Kunden *"
                testId="di-field-betrieb"
              />
              <input placeholder="Ansprechpartner (optional)" value={form.ansprechpartner}
                onChange={(e) => setField("ansprechpartner", e.target.value)}
                className="border border-[#E3DECE] px-3 py-2 text-sm focus:outline-none focus:border-[#BA6D52]" />
              <input type="email" placeholder="E-Mail *" value={form.email}
                onChange={(e) => setField("email", e.target.value)} data-testid="di-field-email"
                className="border border-[#E3DECE] px-3 py-2 text-sm focus:outline-none focus:border-[#BA6D52]" />
              <input placeholder="Telefon (optional)" value={form.telefon}
                onChange={(e) => setField("telefon", e.target.value)}
                className="border border-[#E3DECE] px-3 py-2 text-sm focus:outline-none focus:border-[#BA6D52]" />
              <input placeholder="Straße Nr., PLZ Ort *" value={form.ort}
                onChange={(e) => setField("ort", e.target.value)} data-testid="di-field-ort"
                className="md:col-span-2 border border-[#E3DECE] px-3 py-2 text-sm focus:outline-none focus:border-[#BA6D52]" />
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Leistungsdatum</label>
                <input type="date" value={form.einsatzdatum}
                  onChange={(e) => setField("einsatzdatum", e.target.value)}
                  className="mt-1 w-full border border-[#E3DECE] px-3 py-2 text-sm focus:outline-none focus:border-[#BA6D52]" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Leistungszeitraum (bei mehreren Tagen)</label>
                <input placeholder="z. B. 21.09.2026 – 24.09.2026" value={form.leistungszeitraum || ""}
                  onChange={(e) => setField("leistungszeitraum", e.target.value)}
                  data-testid="di-field-zeitraum"
                  className="mt-1 w-full border border-[#E3DECE] px-3 py-2 text-sm focus:outline-none focus:border-[#BA6D52]" />
                <p className="text-[10px] text-[#757067] mt-1">Steht ausgefüllt in der Rechnung statt eines einzelnen Einsatzdatums.</p>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-3">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Positionen</p>
              <button onClick={addItem} data-testid="di-add-item"
                className="text-xs text-[#BA6D52] hover:underline">+ Position hinzufügen</button>
            </div>

            <div className="border border-[#E3DECE] bg-white p-3 mb-3" data-testid="di-steuersatz">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Steuersatz</p>
              <div className="flex gap-2 mt-2">
                {[[MWST_RATE_PERCENT, "Gewerblich · 19 % MwSt"],
                  [MWST_RATE_PERCENT_REDUCED, "Privatkunde · 7 % MwSt"]].map(([rate, label]) => (
                  <button key={rate} onClick={() => setLineItems((l) => l.map((it) => ({ ...it, tax_rate: rate })))}
                    data-testid={`di-steuersatz-${rate}`}
                    className={`text-[11px] px-3 py-2 border ${aktuellerSatz === rate
                      ? "border-[#BA6D52] text-[#BA6D52] bg-[#FAF5F1]"
                      : "border-[#E3DECE] text-[#757067] bg-white hover:bg-[#FAFAF7]"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[#757067] mt-2">
                Privatkunden: 7 % auf Speisen (§ 12 Abs. 2 UStG). Gewerbliche Mietkoch-Einsätze: 19 %.
                Der gewählte Satz geht so auch nach Lexoffice.
              </p>
            </div>
            <div className="space-y-3">
              {lineItems.map((it, i) => (
                <div key={i} className="border border-[#E3DECE] bg-[#FAFAF7] p-3 space-y-2">
                  <div className="flex gap-2 items-start">
                    <select value={it.type_key}
                      data-testid={`di-item-${i}-type`}
                      onChange={(e) => updateItem(i, applyTypePreset(it, e.target.value, it.tax_rate ?? MWST_RATE_PERCENT))}
                      className="flex-1 border border-[#E3DECE] bg-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#BA6D52]">
                      {TYPE_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <button onClick={() => removeItem(i)} className="text-red-600 hover:text-red-800 text-lg px-2">×</button>
                  </div>
                  <input placeholder="Bezeichnung (z. B. Küchenleitung Einsatz 15.02.)"
                    value={it.name} onChange={(e) => updateItem(i, { name: e.target.value })}
                    data-testid={`di-item-${i}-name`}
                    className="w-full border border-[#E3DECE] bg-white px-2 py-1.5 text-sm focus:outline-none focus:border-[#BA6D52]" />
                  <input placeholder="Zusatz-Beschreibung (optional)"
                    value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })}
                    className="w-full border border-[#E3DECE] bg-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#BA6D52]" />
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[9px] uppercase tracking-wider text-[#757067]">Menge</label>
                      <input type="number" step="0.01" value={it.quantity}
                        onChange={(e) => updateItem(i, { quantity: Number(e.target.value) })}
                        data-testid={`di-item-${i}-qty`}
                        className="w-full border border-[#E3DECE] bg-white px-2 py-1 text-sm focus:outline-none focus:border-[#BA6D52]" />
                    </div>
                    <div>
                      <label className="text-[9px] uppercase tracking-wider text-[#757067]">Einheit</label>
                      <input value={it.unit_name}
                        onChange={(e) => updateItem(i, { unit_name: e.target.value })}
                        className="w-full border border-[#E3DECE] bg-white px-2 py-1 text-sm focus:outline-none focus:border-[#BA6D52]" />
                    </div>
                    <div>
                      <label className="text-[9px] uppercase tracking-wider text-[#757067]">Einzelpreis netto €</label>
                      <input type="number" step="0.01" value={it.unit_price_net}
                        onChange={(e) => updateItem(i, { unit_price_net: Number(e.target.value) })}
                        data-testid={`di-item-${i}-price`}
                        className="w-full border border-[#E3DECE] bg-white px-2 py-1 text-sm focus:outline-none focus:border-[#BA6D52]" />
                    </div>
                  </div>
                  <p className="text-xs text-[#757067] text-right">
                    Zeilensumme (netto): <b className="text-[#2C2A26]">
                      {(Number(it.quantity || 0) * Number(it.unit_price_net || 0)).toFixed(2).replace(".", ",")} €
                    </b>
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-[#E3DECE] bg-[#FAFAF7] p-4" data-testid="di-deposit-block">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Anzahlung verrechnen</p>
            <p className="text-xs text-[#757067] mt-1">
              Wurde vorab eine Anzahlungsrechnung gestellt und bezahlt? Dann hier den Betrag eintragen —
              er wird als Abzugsposition eingefügt, sodass unten nur noch der offene Restbetrag steht.
            </p>
            {hasDeposit ? (
              <p className="text-xs text-[#2F6B3C] mt-3" data-testid="di-deposit-active">
                ✓ Anzahlungs-Abzug ist als Position enthalten. Zum Ändern die Position oben löschen und neu einfügen.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mt-3">
                <input placeholder="Anzahlungs-Rechnungsnr. (optional)"
                  value={depNr} onChange={(e) => setDepNr(e.target.value)}
                  data-testid="di-deposit-number"
                  className="sm:col-span-2 border border-[#E3DECE] bg-white px-2 py-1.5 text-sm focus:outline-none focus:border-[#BA6D52]" />
                <input type="number" step="0.01" placeholder="Betrag"
                  value={depAmount} onChange={(e) => setDepAmount(e.target.value)}
                  data-testid="di-deposit-amount"
                  className="border border-[#E3DECE] bg-white px-2 py-1.5 text-sm focus:outline-none focus:border-[#BA6D52]" />
                <select value={depMode} onChange={(e) => setDepMode(e.target.value)}
                  data-testid="di-deposit-mode"
                  className="border border-[#E3DECE] bg-white px-2 py-1.5 text-sm focus:outline-none focus:border-[#BA6D52]">
                  <option value="brutto">€ brutto</option>
                  <option value="netto">€ netto</option>
                </select>
                <button type="button" onClick={addDepositItem} data-testid="di-deposit-add"
                  className="sm:col-span-4 border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-4 py-2 text-xs uppercase tracking-[0.15em]">
                  − Anzahlung abziehen
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Anmerkung an Kunde (optional)</label>
              <textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={2}
                className="mt-1 w-full border border-[#E3DECE] px-3 py-2 text-sm focus:outline-none focus:border-[#BA6D52]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Interne Notiz (nur für dich)</label>
              <textarea value={form.admin_notes} onChange={(e) => setField("admin_notes", e.target.value)} rows={2}
                className="mt-1 w-full border border-[#E3DECE] px-3 py-2 text-sm focus:outline-none focus:border-[#BA6D52]" />
            </div>
          </div>

          <div className="bg-[#FAF5F1] border border-[#BA6D52] p-4">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Netto</p>
                <p className="font-serif text-lg text-[#2C2A26]">{netSum.toFixed(2).replace(".", ",")} €</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">+ {(() => { const rates = [...new Set((lineItems || []).map((it) => Math.round(Number(it.tax_rate || MWST_RATE_PERCENT))))]; return rates.length === 1 ? `${rates[0]} % MwSt` : "MwSt"; })()}</p>
                <p className="font-serif text-lg text-[#2C2A26]">{mwstSum.toFixed(2).replace(".", ",")} €</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">{hasDeposit ? "Offener Restbetrag" : "Brutto"}</p>
                <p className="font-serif text-2xl text-[#BA6D52] font-semibold">{grossSum.toFixed(2).replace(".", ",")} €</p>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E3DECE] px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="border border-[#E3DECE] hover:border-[#BA6D52] px-6 py-2 text-xs uppercase tracking-[0.15em] text-[#757067]">
            Abbrechen
          </button>
          <button onClick={previewPdf} disabled={previewing} data-testid="direct-invoice-preview"
            className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.15em]">
            {previewing ? "Erstelle …" : "PDF ansehen"}
          </button>
          <button onClick={testMail} disabled={testing} data-testid="direct-invoice-testmail"
            title="Sendet die fertige Rechnungsmail nur an dich — ohne LexOffice-Beleg"
            className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.15em]">
            {testing ? "Sende …" : "Testmail an mich"}
          </button>
          <button onClick={submit} disabled={sending} data-testid="direct-invoice-submit"
            className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-8 py-3 text-xs uppercase tracking-[0.15em]">
            {sending ? "Sende …" : "Rechnung erstellen & versenden"}
          </button>
        </div>
      </div>
    </div>
  );
}



// ---------- Angebote Tab ----------
function QuotesTab({ pwd }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [acceptQuote, setAcceptQuote] = useState(null); // Quote-Objekt für manuelle Annahme
  const [detailQuote, setDetailQuote] = useState(null); // Quote-Objekt für Detail-Ansicht

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, {
        headers: { "X-Admin-Password": pwd },
      });
      setQuotes(data || []);
    } catch (err) {
      toast.error("Angebote konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQuotes(); }, [pwd]);

  // Auto-refresh alle 4 Sek. solange ein Angebot noch im Status "processing" ist
  useEffect(() => {
    const hasProcessing = (quotes || []).some((q) => q.status === "processing");
    if (!hasProcessing) return;
    const iv = setInterval(fetchQuotes, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotes]);

  const cancelQuote = async (id) => {
    if (!window.confirm("Angebot stornieren? (Bleibt in der Liste, Status → Storniert)")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, { headers: { "X-Admin-Password": pwd } });
      toast.success("Angebot storniert");
      fetchQuotes();
    } catch { toast.error("Fehler beim Stornieren"); }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Angebot ENDGÜLTIG löschen? Dies kann nicht rückgängig gemacht werden.\n\nDas System versucht auch, den Beleg in LexOffice zu entfernen — bei bereits finalisierten Angeboten kann LexOffice das jedoch aus GoBD-Gründen ablehnen.")) return;
    try {
      const { data } = await axios.delete(`${API}/admin/quotes/${id}?hard=true`, { headers: { "X-Admin-Password": pwd } });
      if (data.lexoffice_deleted) {
        toast.success("Angebot vollständig gelöscht (System + LexOffice)");
      } else if (data.lexoffice_error) {
        toast.warning("Bei dir gelöscht — LexOffice-Beleg blieb erhalten (GoBD-Schutz)");
      } else {
        toast.success("Angebot gelöscht");
      }
      fetchQuotes();
    } catch { toast.error("Fehler beim Löschen"); }
  };

  const resendQuote = async (id) => {
    try {
      await axios.post(`${API}/admin/quotes/${id}/resend`, {}, { headers: { "X-Admin-Password": pwd } });
      toast.success("Angebot erneut versendet");
      fetchQuotes();
    } catch (err) {
      toast.error("Fehler: " + fehlerText(err));
    }
  };

  // Angebots-PDF herunterladen — für manuellen Versand aus dem eigenen Mailprogramm
  const downloadQuotePdf = async (q) => {
    try {
      const resp = await axios.get(`${API}/admin/quotes/${q.id}/pdf`, {
        headers: { "X-Admin-Password": pwd },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Angebot_${(q.quote_number || "Entwurf").replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch (err) {
      toast.error("PDF konnte nicht erstellt werden: " + (err.response?.data?.detail || err.message));
    }
  };

  // Angebot-Kundendaten-Editor (E-Mail-Tippfehler etc. korrigieren, dann erneut senden)
  const [editingQuote, setEditingQuote] = useState(null);
  const openEditCustomer = (q) => setEditingQuote({
    id: q.id,
    quote_number: q.quote_number,
    betrieb: q.betrieb || "",
    ansprechpartner: q.ansprechpartner || "",
    email: q.email || "",
    telefon: q.telefon || "",
    ort: q.ort || "",
    anlass: q.anlass || "",
    _saving: false,
    _resend: false,
  });
  const closeEditCustomer = () => setEditingQuote(null);
  const saveEditCustomer = async (alsoResend) => {
    if (!editingQuote) return;
    setEditingQuote((s) => ({ ...s, _saving: true, _resend: alsoResend }));
    try {
      const { id, ...rest } = editingQuote;
      const patch = Object.fromEntries(Object.entries(rest).filter(([k, v]) => !k.startsWith("_") && k !== "quote_number" && v !== ""));
      await axios.patch(`${API}/admin/quotes/${id}/customer-info`, patch, { headers: { "X-Admin-Password": pwd } });
      if (alsoResend) {
        await axios.post(`${API}/admin/quotes/${id}/resend`, {}, { headers: { "X-Admin-Password": pwd } });
        toast.success("Kundendaten aktualisiert & Angebot erneut versendet");
      } else {
        toast.success("Kundendaten aktualisiert");
      }
      closeEditCustomer();
      fetchQuotes();
    } catch (err) {
      toast.error("Fehler: " + fehlerText(err));
      setEditingQuote((s) => ({ ...s, _saving: false }));
    }
  };

  const sendCopyToMe = async (id) => {
    try {
      await axios.post(`${API}/admin/quotes/${id}/send-to-me`, {}, { headers: { "X-Admin-Password": pwd } });
      toast.success("Kopie mit PDF an deine Admin-E-Mail gesendet");
    } catch (err) {
      toast.error("Fehler: " + fehlerText(err));
    }
  };

  const statusBadge = (s) => {
    const map = {
      processing: ["bg-amber-50 text-amber-900 border-amber-200 animate-pulse", "Wird erstellt …"],
      sent: ["bg-blue-50 text-blue-900 border-blue-200", "Versendet"],
      send_failed: ["bg-amber-50 text-amber-900 border-amber-200", "⚠ Versand fehlgeschl."],
      accepted: ["bg-emerald-50 text-emerald-900 border-emerald-200", "Angenommen"],
      declined: ["bg-red-50 text-red-900 border-red-200", "Abgelehnt"],
      expired: ["bg-gray-100 text-gray-700 border-gray-300", "Abgelaufen"],
      cancelled: ["bg-gray-100 text-gray-700 border-gray-300", "Storniert"],
    };
    const [cls, label] = map[s] || ["bg-gray-50 text-gray-700 border-gray-200", s];
    return <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wider border ${cls}`}>{label}</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Angebote</p>
          <p className="text-sm text-[#757067] mt-1 font-light">Unverbindliche Angebote — Kunde kann via Link annehmen/ablehnen.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="quotes-create-new"
          className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-5 py-3 text-xs uppercase tracking-[0.15em]"
        >
          + Neues Angebot
        </button>
      </div>

      {/* Stunden-Angebote (Rate Card) — separater Bereich */}
      <RateCardQuotesSection pwd={pwd} />

      <div className="mt-10 mb-4 border-t border-[#E3DECE] pt-6">
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Projekt-Angebote</p>
        <p className="text-xs text-[#757067] mt-1 font-light">Angebote mit konkreten Tagen, Positionen und Anzahlung — via LexOffice.</p>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#BA6D52]" /></div>
      ) : quotes.length === 0 ? (
        <p className="py-12 text-center text-[#757067]">Noch keine Angebote erstellt.</p>
      ) : (
        <>
          {/* Desktop: Tabelle */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[#E3DECE] text-left">
                <tr className="text-xs uppercase tracking-wider text-[#757067]">
                  <th className="pb-3 pr-4">Nr.</th>
                  <th className="pb-3 pr-4">Betrieb</th>
                  <th className="pb-3 pr-4">Einsatz</th>
                  <th className="pb-3 pr-4">Brutto</th>
                  <th className="pb-3 pr-4">Gültig bis</th>
                  <th className="pb-3 pr-4">Status</th>
                  <th className="pb-3">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E3DECE]">
                {quotes.map((q) => {
                  // Brutto pro Position mit tatsächlicher tax_rate (7 % Privatkunden / 19 % B2B)
                  const gross = (q.line_items || []).reduce((s, it) => {
                    const net = Number(it.quantity || 0) * Number(it.unit_price_net || 0);
                    return s + net * (1 + Number(it.tax_rate || MWST_RATE_PERCENT) / 100);
                  }, 0);
                  return (
                    <tr key={q.id} data-testid={`quote-row-${q.id}`}>
                      <td className="py-3 pr-4 font-mono text-xs">{q.quote_number || "—"}</td>
                      <td className="py-3 pr-4">
                        <div>{q.betrieb}</div>
                        <div className="text-xs text-[#757067]">{q.email}</div>
                      </td>
                      <td className="py-3 pr-4 text-xs text-[#757067]">
                        {q.custom_dates && q.custom_dates.length > 0
                          ? <>ab {formatDateDE(q.einsatzdatum)} · <b>{q.custom_dates.length} Einzeltage</b></>
                          : <>{formatDateDE(q.einsatzdatum)}{q.anzahl_tage > 1 ? ` · ${q.anzahl_tage} Tage` : ""}</>}
                      </td>
                      <td className="py-3 pr-4 font-mono">{gross.toFixed(2).replace(".", ",")} €</td>
                      <td className="py-3 pr-4 text-xs">{formatDateDE(q.valid_until)}</td>
                      <td className="py-3 pr-4">{statusBadge(q.status)}</td>
                      <td className="py-3">
                        <div className="flex flex-col gap-1 items-start">
                          <button
                            onClick={() => setDetailQuote(q)}
                            className="text-xs text-[#2C2A26] hover:text-[#BA6D52] hover:underline font-medium"
                            data-testid={`quote-details-${q.id}`}
                          >
                            👁 Details
                          </button>
                          {q.status === "sent" && (
                            <button
                              onClick={() => setAcceptQuote(q)}
                              className="text-xs text-emerald-800 hover:underline font-semibold"
                              data-testid={`quote-accept-manual-${q.id}`}
                            >
                              ✓ Annehmen
                            </button>
                          )}
                          <button
                            onClick={() => downloadQuotePdf(q)}
                            className="text-xs text-[#2C2A26] hover:text-[#BA6D52] hover:underline"
                            data-testid={`quote-download-pdf-${q.id}`}
                            title="Angebots-PDF herunterladen und selbst per E-Mail verschicken"
                          >
                            ⬇ PDF laden
                          </button>
                          {(q.status === "sent" || q.status === "send_failed") && (
                            <button
                              onClick={() => resendQuote(q.id)}
                              className="text-xs text-[#BA6D52] hover:underline font-medium"
                              data-testid={`quote-resend-${q.id}`}
                            >
                              {q.status === "send_failed" ? "⚠ Erneut senden" : "↻ Erneut senden"}
                            </button>
                          )}
                          {(q.status === "sent" || q.status === "send_failed") && (
                            <button
                              onClick={() => openEditCustomer(q)}
                              className="text-xs text-[#2C2A26] hover:text-[#BA6D52] hover:underline"
                              data-testid={`quote-edit-customer-${q.id}`}
                              title="Kundendaten (E-Mail, Adresse, Anlass) bearbeiten und danach erneut senden"
                            >
                              ✎ Bearbeiten &amp; neu senden
                            </button>
                          )}
                          {(q.status === "sent" || q.status === "accepted") && q.lex_quote_id && (
                            <button
                              onClick={() => sendCopyToMe(q.id)}
                              className="text-xs text-[#2C2A26] hover:text-[#BA6D52] hover:underline"
                              data-testid={`quote-copy-to-me-${q.id}`}
                              title="Angebots-PDF an deine Admin-E-Mail schicken"
                            >
                              📥 Kopie an mich
                            </button>
                          )}
                          {q.status === "sent" && (
                            <button
                              onClick={() => cancelQuote(q.id)}
                              className="text-xs text-amber-800 hover:underline"
                              data-testid={`quote-cancel-${q.id}`}
                            >
                              Stornieren
                            </button>
                          )}
                          {q.status === "accepted" && q.converted_inquiry_id && (
                            <span className="text-xs text-emerald-700">→ Buchung</span>
                          )}
                          <button
                            onClick={() => deleteQuote(q.id)}
                            className="text-xs text-red-700 hover:underline"
                            data-testid={`quote-delete-${q.id}`}
                          >
                            Löschen
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: Karten-Layout */}
          <div className="md:hidden space-y-4">
            {quotes.map((q) => {
              // Brutto pro Position mit tatsächlicher tax_rate (7 % Privatkunden / 19 % B2B)
              const gross = (q.line_items || []).reduce((s, it) => {
                const net = Number(it.quantity || 0) * Number(it.unit_price_net || 0);
                return s + net * (1 + Number(it.tax_rate || MWST_RATE_PERCENT) / 100);
              }, 0);
              return (
                <div
                  key={q.id}
                  data-testid={`quote-card-${q.id}`}
                  className="border border-[#E3DECE] bg-white p-4 shadow-sm"
                >
                  <button
                    type="button"
                    onClick={() => setDetailQuote(q)}
                    data-testid={`quote-details-mobile-${q.id}`}
                    className="w-full text-left flex items-start justify-between gap-3 mb-3 hover:bg-[#FAFAF7] -m-2 p-2 rounded transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-serif text-base text-[#2C2A26] truncate">{q.betrieb}</p>
                      <p className="text-xs text-[#757067] truncate">{q.email}</p>
                      <p className="text-[10px] text-[#BA6D52] font-mono mt-1">{q.quote_number || "—"} · <span className="underline">Details anzeigen</span></p>
                    </div>
                    <div className="shrink-0">{statusBadge(q.status)}</div>
                  </button>

                  <div className="grid grid-cols-3 gap-3 py-3 border-y border-[#E3DECE]/60 text-xs">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-[#757067] mb-1">Einsatz</p>
                      <p className="text-[#2C2A26]">
                        {formatDateDE(q.einsatzdatum)}
                        {q.custom_dates && q.custom_dates.length > 0
                          ? <span className="block text-[10px] text-[#757067]">{q.custom_dates.length} Einzeltage</span>
                          : q.anzahl_tage > 1
                            ? <span className="block text-[10px] text-[#757067]">{q.anzahl_tage} Tage</span>
                            : null}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-[#757067] mb-1">Brutto</p>
                      <p className="font-mono text-[#2C2A26]">{gross.toFixed(2).replace(".", ",")} €</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-[#757067] mb-1">Gültig bis</p>
                      <p className="text-[#2C2A26]">{formatDateDE(q.valid_until)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {q.status === "sent" && (
                      <button
                        onClick={() => setAcceptQuote(q)}
                        data-testid={`quote-accept-manual-mobile-${q.id}`}
                        className="border border-emerald-700 text-emerald-800 hover:bg-emerald-50 py-2 px-3 text-xs uppercase tracking-wider font-medium"
                      >
                        ✓ Annehmen
                      </button>
                    )}
                    {(q.status === "sent" || q.status === "send_failed") && (
                      <button
                        onClick={() => resendQuote(q.id)}
                        data-testid={`quote-resend-mobile-${q.id}`}
                        className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#BA6D52]/5 py-2 px-3 text-xs uppercase tracking-wider font-medium"
                      >
                        {q.status === "send_failed" ? "⚠ Erneut" : "↻ Erneut senden"}
                      </button>
                    )}
                    {(q.status === "sent" || q.status === "send_failed") && (
                      <button
                        onClick={() => openEditCustomer(q)}
                        data-testid={`quote-edit-customer-mobile-${q.id}`}
                        className="border border-[#2C2A26] text-[#2C2A26] hover:bg-[#2C2A26]/5 py-2 px-3 text-xs uppercase tracking-wider"
                      >
                        ✎ Bearbeiten
                      </button>
                    )}
                    {(q.status === "sent" || q.status === "accepted") && q.lex_quote_id && (
                      <button
                        onClick={() => sendCopyToMe(q.id)}
                        data-testid={`quote-copy-to-me-mobile-${q.id}`}
                        className="border border-[#2C2A26] text-[#2C2A26] hover:bg-[#2C2A26]/5 py-2 px-3 text-xs uppercase tracking-wider"
                      >
                        📥 Kopie an mich
                      </button>
                    )}
                    {q.status === "sent" && (
                      <button
                        onClick={() => cancelQuote(q.id)}
                        data-testid={`quote-cancel-mobile-${q.id}`}
                        className="border border-amber-700 text-amber-800 hover:bg-amber-50 py-2 px-3 text-xs uppercase tracking-wider"
                      >
                        Stornieren
                      </button>
                    )}
                    {q.status === "accepted" && q.converted_inquiry_id && (
                      <span className="col-span-2 text-center text-xs text-emerald-700 py-2 px-3 bg-emerald-50 border border-emerald-200">
                        → In Buchungen übernommen
                      </span>
                    )}
                    <button
                      onClick={() => deleteQuote(q.id)}
                      data-testid={`quote-delete-mobile-${q.id}`}
                      className="border border-red-700 text-red-700 hover:bg-red-50 py-2 px-3 text-xs uppercase tracking-wider"
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {showCreate && (
        <QuoteCreateModal
          pwd={pwd}
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); fetchQuotes(); }}
        />
      )}

      {acceptQuote && (
        <QuoteAcceptModal
          quote={acceptQuote}
          pwd={pwd}
          onClose={() => setAcceptQuote(null)}
          onSuccess={() => { setAcceptQuote(null); fetchQuotes(); }}
        />
      )}

      {detailQuote && (
        <QuoteDetailModal
          quote={detailQuote}
          onClose={() => setDetailQuote(null)}
        />
      )}
      {editingQuote && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closeEditCustomer} data-testid="quote-edit-customer-modal">
          <div className="bg-white max-w-lg w-full border border-[#E3DECE] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[#E3DECE] flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Kundendaten bearbeiten</p>
                <h2 className="font-serif text-xl text-[#2C2A26] mt-1">{editingQuote.quote_number || "Angebot"}</h2>
              </div>
              <button onClick={closeEditCustomer} className="text-[#757067] hover:text-[#2C2A26] text-2xl leading-none" aria-label="Schließen">×</button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-[#757067] leading-relaxed bg-[#FAF5F1] border-l-2 border-[#BA6D52] p-3">
                Ändere hier nur die Kundendaten (E-Mail-Tippfehler, Adresse, Anlass). Positionen, Preise und Angebotsnummer bleiben unverändert.
              </p>
              {[
                { k: "betrieb", label: "Betrieb / Familie", type: "text" },
                { k: "ansprechpartner", label: "Ansprechpartner", type: "text" },
                { k: "email", label: "E-Mail", type: "email" },
                { k: "telefon", label: "Telefon", type: "tel" },
                { k: "ort", label: "Adresse (Straße Nr, PLZ Ort)", type: "text" },
                { k: "anlass", label: "Anlass", type: "text" },
              ].map(({ k, label, type }) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">{label}</label>
                  <input
                    type={type}
                    value={editingQuote[k] || ""}
                    onChange={(e) => setEditingQuote((s) => ({ ...s, [k]: e.target.value }))}
                    data-testid={`quote-edit-${k}`}
                    className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-3 text-sm"
                  />
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-[#E3DECE] flex flex-wrap justify-end gap-2">
              <button onClick={closeEditCustomer} disabled={editingQuote._saving}
                className="px-4 py-2 text-xs uppercase tracking-[0.15em] border border-[#E3DECE] hover:bg-[#FAFAF7] disabled:opacity-60">
                Abbrechen
              </button>
              <button onClick={() => saveEditCustomer(false)} disabled={editingQuote._saving} data-testid="quote-edit-save-only"
                className="px-4 py-2 text-xs uppercase tracking-[0.15em] border border-[#2C2A26] text-[#2C2A26] hover:bg-[#2C2A26]/5 disabled:opacity-60">
                {editingQuote._saving && !editingQuote._resend ? "Speichert…" : "Nur speichern"}
              </button>
              <button onClick={() => saveEditCustomer(true)} disabled={editingQuote._saving} data-testid="quote-edit-save-and-resend"
                className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-[0.15em]">
                {editingQuote._saving && editingQuote._resend ? "Sendet…" : "Speichern & neu senden"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ---------- Modal: Angebot-Details (Read-only) ----------
function QuoteDetailModal({ quote, onClose }) {
  const q = quote || {};
  const items = q.line_items || [];
  const { netSum, mwstSum, grossSum } = calcVatTotals(items, MWST_RATE_PERCENT);
  const uniqRates = [...new Set(items.map((it) => Math.round(Number(it.tax_rate || MWST_RATE_PERCENT))))];
  const mwstRateLabel = uniqRates.length === 1 ? `${uniqRates[0]} % MwSt` : "MwSt";
  const depositAmount = q.deposit_percent > 0 ? Math.round(grossSum * q.deposit_percent / 100 * 100) / 100 : 0;

  const fmtDate = (iso) => iso ? formatDateDE(iso) : "—";
  const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  const statusText = {
    processing: "Wird gerade erstellt & versendet …",
    sent: "An Kunde versendet — wartet auf Rückmeldung",
    send_failed: "⚠ Versand fehlgeschlagen — bitte erneut senden",
    accepted: "Vom Kunden angenommen ✓",
    declined: "Vom Kunden abgelehnt",
    expired: "Abgelaufen (Gültigkeit überschritten)",
    cancelled: "Storniert",
  }[q.status] || q.status;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 md:p-6" data-testid="quote-detail-modal">
      <div className="bg-white w-full max-w-3xl max-h-[94vh] overflow-y-auto border border-[#E3DECE]">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-[#E3DECE] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Angebot {q.quote_number || "—"}</p>
            <h3 className="font-serif text-2xl text-[#2C2A26]">{q.betrieb}</h3>
            <p className="text-xs text-[#757067] mt-1">{statusText}</p>
          </div>
          <button onClick={onClose} className="text-[#757067] hover:text-[#2C2A26] text-2xl leading-none px-3" data-testid="quote-detail-close">×</button>
        </div>

        <div className="p-6 space-y-6">
          {/* Kunde */}
          <div className="border border-[#E3DECE] p-5 bg-[#FAFAF7]">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52] mb-3">Empfänger</p>
            <p className="font-medium text-[#2C2A26]">{q.betrieb}</p>
            {q.ansprechpartner && <p className="text-sm text-[#2C2A26]">Ansprechpartner: {q.ansprechpartner}</p>}
            <p className="text-sm text-[#757067] mt-1">{q.email}{q.telefon ? ` · ${q.telefon}` : ""}</p>
            <p className="text-sm text-[#757067]">{q.ort}</p>
          </div>

          {/* Einsatz */}
          <div className="border border-[#E3DECE] p-5 bg-[#FAFAF7]">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52] mb-3">Einsatz</p>
            {q.custom_dates && q.custom_dates.length > 0 ? (
              <div>
                <p className="text-sm text-[#2C2A26] mb-2"><b>{q.custom_dates.length}</b> Einzeltage:</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-sm">
                  {q.custom_dates.map((d, i) => (
                    <div key={i} className="text-[#2C2A26]">
                      {formatDateDE(d.date)}
                      {(d.von || d.bis) && (
                        <span className="text-[#757067] text-xs ml-2">
                          {d.von || "?"}–{d.bis || "?"}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#2C2A26]">
                {fmtDate(q.einsatzdatum)}
                {q.anzahl_tage > 1 && ` · ${q.anzahl_tage} Tage`}
                {(q.von_uhrzeit || q.bis_uhrzeit) && (
                  <span className="text-[#757067] ml-2">
                    ({q.von_uhrzeit || "?"}–{q.bis_uhrzeit || "?"})
                  </span>
                )}
              </p>
            )}
          </div>

          {/* Positionen */}
          <div className="border border-[#E3DECE] p-5 bg-[#FAFAF7]">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52] mb-3">Positionen</p>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-[#757067] border-b border-[#E3DECE]">
                <tr>
                  <th className="text-left py-2">Bezeichnung</th>
                  <th className="text-right py-2 pl-2">Menge</th>
                  <th className="text-right py-2 pl-2">Einzel</th>
                  <th className="text-right py-2 pl-2">Netto</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const net = Number(it.quantity || 0) * Number(it.unit_price_net || 0);
                  return (
                    <tr key={i} className="border-b border-[#E3DECE]/60 align-top">
                      <td className="py-3">
                        <div className="font-medium text-[#2C2A26]">{it.name}</div>
                        {it.description && <div className="text-xs text-[#757067] mt-0.5 whitespace-pre-wrap">{it.description}</div>}
                      </td>
                      <td className="py-3 text-right pl-2 font-mono whitespace-nowrap">{Number(it.quantity).toString().replace(".", ",")} {it.unit_name}</td>
                      <td className="py-3 text-right pl-2 font-mono whitespace-nowrap">{Number(it.unit_price_net).toFixed(2).replace(".", ",")} €</td>
                      <td className="py-3 text-right pl-2 font-mono whitespace-nowrap">{net.toFixed(2).replace(".", ",")} €</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="text-sm">
                <tr>
                  <td colSpan="3" className="pt-3 text-right text-[#757067]">Netto</td>
                  <td className="pt-3 text-right font-mono">{netSum.toFixed(2).replace(".", ",")} €</td>
                </tr>
                <tr>
                  <td colSpan="3" className="text-right text-[#757067]">zzgl. {mwstRateLabel}</td>
                  <td className="text-right font-mono">{mwstSum.toFixed(2).replace(".", ",")} €</td>
                </tr>
                <tr className="border-t border-[#2C2A26]">
                  <td colSpan="3" className="pt-2 text-right font-semibold text-[#2C2A26]">Gesamt (brutto)</td>
                  <td className="pt-2 text-right font-mono font-semibold text-[#2C2A26]">{grossSum.toFixed(2).replace(".", ",")} €</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Anmerkungen */}
          {q.notes && (
            <div className="border border-[#E3DECE] p-5 bg-[#FAFAF7]">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52] mb-3">Anmerkungen für den Kunden</p>
              <p className="text-sm text-[#2C2A26] whitespace-pre-wrap">{q.notes}</p>
            </div>
          )}

          {/* Interne Notiz */}
          {q.admin_notes && (
            <div className="border border-amber-200 p-5 bg-amber-50/50">
              <p className="text-[10px] uppercase tracking-[0.25em] text-amber-800 mb-3">Interne Notiz (nicht sichtbar für Kunden)</p>
              <p className="text-sm text-[#2C2A26] whitespace-pre-wrap">{q.admin_notes}</p>
            </div>
          )}

          {/* Gültigkeit & Anzahlung */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-[#E3DECE] p-4 bg-[#FAFAF7]">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52] mb-2">Gültigkeit</p>
              <p className="text-sm text-[#2C2A26]">bis <b>{fmtDate(q.valid_until)}</b></p>
            </div>
            <div className="border border-[#E3DECE] p-4 bg-[#FAFAF7]">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52] mb-2">Anzahlung</p>
              {q.deposit_percent > 0 ? (
                <p className="text-sm text-[#2C2A26]">
                  <b>{q.deposit_percent} %</b> = {depositAmount.toFixed(2).replace(".", ",")} €
                  <span className="text-[#757067]"> · fällig {q.deposit_due_days} Tage nach Annahme</span>
                </p>
              ) : (
                <p className="text-sm text-[#757067] italic">Keine Anzahlung — Zahlung nach Einsatz</p>
              )}
            </div>
          </div>

          {/* Meta */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div className="border border-[#E3DECE] p-3 bg-white">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067] mb-1">Erstellt</p>
              <p className="font-mono text-[#2C2A26]">{fmtDateTime(q.created_at)}</p>
            </div>
            <div className="border border-[#E3DECE] p-3 bg-white">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067] mb-1">E-Mail versendet</p>
              <p className="font-mono text-[#2C2A26]">{q.email_sent_at ? fmtDateTime(q.email_sent_at) : "—"}</p>
            </div>
            {q.accepted_at && (
              <div className="border border-emerald-200 p-3 bg-emerald-50">
                <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 mb-1">Angenommen am</p>
                <p className="font-mono text-[#2C2A26]">{fmtDateTime(q.accepted_at)}</p>
              </div>
            )}
            {q.declined_at && (
              <div className="border border-red-200 p-3 bg-red-50">
                <p className="text-[10px] uppercase tracking-[0.2em] text-red-800 mb-1">Abgelehnt am</p>
                <p className="font-mono text-[#2C2A26]">{fmtDateTime(q.declined_at)}</p>
              </div>
            )}
            {q.lex_quote_id && (
              <div className="border border-[#E3DECE] p-3 bg-white md:col-span-2">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067] mb-1">LexOffice-Angebot-ID</p>
                <p className="font-mono text-xs text-[#757067] break-all">{q.lex_quote_id}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-[#E3DECE] px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            data-testid="quote-detail-close-bottom"
            className="bg-[#2C2A26] text-white hover:bg-[#1E1D1A] px-6 py-2 text-xs uppercase tracking-[0.15em]"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------- Modal: Angebot manuell annehmen ----------
function QuoteAcceptModal({ quote, pwd, onClose, onSuccess }) {  const [depositPercent, setDepositPercent] = useState(0);
  const [depositInvoiceSendDate, setDepositInvoiceSendDate] = useState("");
  const [depositPaymentDays, setDepositPaymentDays] = useState(14);
  const [adminNote, setAdminNote] = useState("");
  const [sendConfirmation, setSendConfirmation] = useState(true);
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState("form"); // "form" | "preview"
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewRecipient, setPreviewRecipient] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);

  const netSum = (quote.line_items || []).reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price_net || 0), 0);
  // Brutto pro Position mit tatsächlicher tax_rate (7 % Privatkunden / 19 % B2B)
  const grossSum = Math.round((quote.line_items || []).reduce((s, it) => {
    const net = Number(it.quantity || 0) * Number(it.unit_price_net || 0);
    return s + net * (1 + Number(it.tax_rate || MWST_RATE_PERCENT) / 100);
  }, 0) * 100) / 100;
  const depositAmount = Math.round(grossSum * (depositPercent / 100) * 100) / 100;

  const paymentDueDate = (() => {
    if (!depositInvoiceSendDate) return "";
    try {
      const d = new Date(depositInvoiceSendDate);
      d.setDate(d.getDate() + Number(depositPaymentDays || 14));
      return d.toISOString().slice(0, 10);
    } catch { return ""; }
  })();

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const r = await axios.post(
        `${API}/admin/quotes/${quote.id}/accept-preview`,
        {
          deposit_percent: Number(depositPercent) || 0,
          deposit_invoice_send_date: depositInvoiceSendDate || "",
          deposit_payment_days: Number(depositPaymentDays) || 14,
          admin_note: adminNote,
          send_confirmation: sendConfirmation,
        },
        { headers: { "X-Admin-Password": pwd } },
      );
      setPreviewHtml(r.data.html);
      setPreviewSubject(r.data.subject);
      setPreviewRecipient(r.data.recipient);
      setStep("preview");
    } catch (err) {
      toast.error("Fehler beim Laden der Vorschau: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingPreview(false);
    }
  };

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/quotes/${quote.id}/accept-manually`,
        {
          deposit_percent: Number(depositPercent) || 0,
          deposit_invoice_send_date: depositInvoiceSendDate || "",
          deposit_payment_days: Number(depositPaymentDays) || 14,
          admin_note: adminNote,
          send_confirmation: sendConfirmation,
        },
        { headers: { "X-Admin-Password": pwd } },
      );
      toast.success("Angebot angenommen — Buchung erstellt" + (sendConfirmation ? " & Auftragsbestätigung versendet" : ""));
      onSuccess();
    } catch (err) {
      toast.error("Fehler: " + fehlerText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 md:p-6" data-testid="quote-accept-modal">
      <div className={`bg-white w-full ${step === "preview" ? "max-w-4xl" : "max-w-xl"} border border-[#E3DECE] flex flex-col max-h-[92vh]`}>
        <div className="border-b border-[#E3DECE] px-6 py-4 flex-shrink-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">
            {step === "form" ? "Angebot annehmen · Schritt 1 von 2" : "Vorschau · Schritt 2 von 2"}
          </p>
          <h3 className="font-serif text-2xl text-[#2C2A26]">
            {step === "form" ? "Zusage im Namen des Kunden erfassen" : "Bitte prüfe die Auftragsbestätigung"}
          </h3>
          <p className="text-xs text-[#757067] mt-1">
            {quote.quote_number} · {quote.betrieb} · {grossSum.toFixed(2).replace(".", ",")} € brutto
          </p>
        </div>

        {step === "preview" && (
          <div className="p-6 flex-1 overflow-y-auto">
            <div className="mb-4 p-3 bg-[#FAF5F1] border-l-3 border-[#BA6D52] text-sm">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div><span className="text-[#757067]">Empfänger:</span> <b>{previewRecipient}</b></div>
                <div><span className="text-[#757067]">Betreff:</span> <b>{previewSubject}</b></div>
              </div>
            </div>
            <div className="border border-[#E3DECE] rounded overflow-hidden">
              <iframe
                title="Vorschau Auftragsbestätigung"
                srcDoc={previewHtml}
                data-testid="accept-preview-iframe"
                className="w-full"
                style={{ height: "60vh", border: 0, background: "#FAFAF7" }}
              />
            </div>
            <p className="text-xs text-[#757067] mt-3">
              Vorschau der E-Mail — das PDF im Anhang enthält die gleichen Inhalte in deinem Briefkopf.
              {sendConfirmation ? "" : " ⚠️ Häkchen 'Auftragsbestätigung senden' ist deaktiviert — es wird KEINE Mail versendet, nur die Buchung erstellt."}
            </p>
          </div>
        )}

        {step === "form" && (
        <div className="p-6 space-y-4 flex-1 overflow-y-auto">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Anzahlung %</label>
            <input
              type="number"
              min="0"
              max="100"
              value={(depositPercent === 0 || depositPercent === "0") ? "" : depositPercent}
              placeholder="0 = keine Anzahlung"
              onChange={(e) => setDepositPercent(e.target.value === "" ? 0 : Number(e.target.value))}
              data-testid="accept-deposit-percent"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
            />
          </div>

          {Number(depositPercent) > 0 && (
            <>
              <p className="text-xs text-[#757067]">
                → Anzahlungsbetrag: <b className="text-[#BA6D52]">{depositAmount.toFixed(2).replace(".", ",")} €</b> (vom Brutto)
              </p>

              <div className="bg-[#FAF5F1] border-l-2 border-[#BA6D52] p-4 space-y-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] font-semibold">Anzahlungsrechnung — Zeitplan</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.15em] text-[#757067]">Rechnungs-Versand am</label>
                    <input
                      type="date"
                      value={depositInvoiceSendDate}
                      onChange={(e) => setDepositInvoiceSendDate(e.target.value)}
                      data-testid="accept-deposit-send-date"
                      className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
                    />
                    <p className="text-[10px] text-[#757067] mt-1">Du bekommst an dem Tag eine Erinnerungs-Mail</p>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.15em] text-[#757067]">Zahlungsfrist (Tage)</label>
                    <input
                      type="number"
                      min="1"
                      value={depositPaymentDays}
                      placeholder="14"
                      onChange={(e) => setDepositPaymentDays(e.target.value === "" ? "" : Number(e.target.value))}
                      data-testid="accept-deposit-payment-days"
                      className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
                    />
                    <p className="text-[10px] text-[#757067] mt-1">Nach Erhalt der Rechnung</p>
                  </div>
                </div>
                {depositInvoiceSendDate && paymentDueDate && (
                  <p className="text-xs text-[#2C2A26] pt-1">
                    → Zahlung fällig bis <b>{new Date(paymentDueDate).toLocaleDateString("de-DE")}</b>
                  </p>
                )}
                {!depositInvoiceSendDate && (
                  <p className="text-xs text-amber-700">
                    Kein Versanddatum gewählt → Anzahlungsrechnung muss manuell erstellt werden.
                  </p>
                )}
              </div>
            </>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Interne Notiz (optional)</label>
            <input
              type="text"
              placeholder="z. B. Angebot unterschrieben am 15.02.2026"
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              data-testid="accept-admin-note"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
            />
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={sendConfirmation}
              onChange={(e) => setSendConfirmation(e.target.checked)}
              className="accent-[#BA6D52]"
            />
            <span>Auftragsbestätigung mit PDF an Kunden senden ({quote.email})</span>
          </label>
        </div>
        )}

        <div className="border-t border-[#E3DECE] px-6 py-4 flex flex-col-reverse md:flex-row justify-end gap-3 flex-shrink-0">
          {step === "form" ? (
            <>
              <button
                onClick={onClose}
                disabled={loadingPreview}
                className="px-6 py-3 text-xs uppercase tracking-[0.15em] border border-[#E3DECE] hover:bg-[#FAFAF7] disabled:opacity-60"
              >
                Abbrechen
              </button>
              <button
                onClick={loadPreview}
                disabled={loadingPreview}
                data-testid="accept-show-preview"
                className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-6 py-3 text-xs uppercase tracking-[0.15em] disabled:opacity-60 inline-flex items-center gap-2"
              >
                {loadingPreview && <Loader2 size={14} className="animate-spin" />}
                Vorschau anzeigen →
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep("form")}
                disabled={sending}
                className="px-6 py-3 text-xs uppercase tracking-[0.15em] border border-[#E3DECE] hover:bg-[#FAFAF7] disabled:opacity-60"
              >
                ← Zurück bearbeiten
              </button>
              <button
                onClick={submit}
                disabled={sending}
                data-testid="accept-submit"
                className="bg-emerald-700 text-white hover:bg-emerald-800 px-6 py-3 text-xs uppercase tracking-[0.15em] disabled:opacity-60 inline-flex items-center gap-2"
              >
                {sending && <Loader2 size={14} className="animate-spin" />}
                ✓ Jetzt verbindlich annehmen{sendConfirmation ? " & senden" : ""}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------- Modal: Neues Angebot erstellen ----------
function QuoteInput({ label, valueKey, form, setField, type = "text", placeholder = "", required = false }) {
  const raw = form[valueKey];
  // Bei type=number: 0 anzeigen als leer, damit User es einfach überschreiben kann
  const displayValue = type === "number" && (raw === 0 || raw === "0") ? "" : (raw ?? "");
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">{label}{required && " *"}</label>
      <input
        type={type}
        value={displayValue}
        placeholder={placeholder || (type === "number" ? "0" : "")}
        onChange={(e) => setField(valueKey, type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
        data-testid={`quote-field-${valueKey}`}
        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
      />
    </div>
  );
}

function QuoteCreateModal({ pwd, onClose, onSuccess }) {
  // Formularzustand wird in localStorage gespeichert, damit Eingaben nach Schließen des Fensters erhalten bleiben.
  const STORAGE_KEY = "lstz.quote_draft.v1";
  const DEFAULT_FORM = {
    betrieb: "", ansprechpartner: "", email: "", telefon: "",
    ort: "", einsatzdatum: "", anzahl_tage: 1,
    von_uhrzeit: "", bis_uhrzeit: "", notes: "",
    valid_days: 14, admin_notes: "",
    deposit_percent: 0, deposit_due_days: 7,
    customer_type: "business", anlass: "",
  };
  const DEFAULT_ITEMS = [
    { type_key: "mietkoch_werktag", name: "Mietkoch-Stunden", description: "", quantity: 8, unit_name: "Stunde", unit_price_net: 45, tax_rate: MWST_RATE_PERCENT },
  ];
  const loadDraft = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch { return null; }
  };
  const savedDraft = loadDraft();

  const [form, setForm] = useState(savedDraft?.form || DEFAULT_FORM);
  // dateMode: "range" (Standard) | "custom" (Einzeltage)
  const [dateMode, setDateMode] = useState(savedDraft?.dateMode || "range");
  // customDates: [{ date: "YYYY-MM-DD", von: "HH:MM", bis: "HH:MM" }]
  const [customDates, setCustomDates] = useState(savedDraft?.customDates || []);
  const [newCustomDate, setNewCustomDate] = useState("");
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [lineItems, setLineItems] = useState(savedDraft?.lineItems || DEFAULT_ITEMS);
  const [positionTypes] = useState([
    { key: "menu_pauschale_pp", label: "Menü-Pauschale pro Person" },
    { key: "fahrtpauschale", label: "Fahrtkostenpauschale" },
    { key: "km_pauschale", label: "Kilometerpauschale" },
    { key: "uebernachtung", label: "Übernachtung" },
    { key: "material", label: "Material / Einkauf" },
    { key: "mietkoch_werktag", label: "Mietkoch-Stunden Werktag" },
    { key: "mietkoch_we", label: "Mietkoch-Stunden WE/Feiertag" },
    { key: "erst_briefing", label: "Praktisches Erst-Briefing" },
    { key: "privatkoch_planung", label: "Menü-Planung & Vorbereitung" },
    { key: "privatkoch_zutaten", label: "Lebensmittel-Einkauf" },
    { key: "privatkoch_einkauf", label: "Einkaufspauschale" },
    { key: "privatkoch_zubereitung", label: "Stundenlohn Privatkoch" },
    { key: "privatkoch_reinigung", label: "Reinigung / Küchen-Aufbereitung" },
    { key: "sonstiges", label: "Sonstiges" },
  ]);
  // Smarte Vorbelegung, wenn ein Typ ausgewählt wird — nur, wenn Feld noch leer/Default ist
  const presetFor = (type_key) => {
    const rate = taxRateForCustomerType(form.customer_type);
    if (type_key === "menu_pauschale_pp") {
      return {
        type_key,
        name: "Exklusives Private Dining · 4-Gang-Menü",
        description: "Enthält individuelle Menüplanung, Einkauf aller Frischezutaten, Vorbereitung, Zubereitung vor Ort, professionellen Tischservice sowie finale Küchenreinigung.",
        quantity: 6,
        unit_name: "Person",
        unit_price_net: 90,
        tax_rate: rate,
      };
    }
    if (POSITION_PRESETS[type_key]) {
      return { type_key, ...POSITION_PRESETS[type_key], tax_rate: rate };
    }
    return { type_key };
  };
  // Wrapper: bei Type-Change den Preset komplett übernehmen (überschreibt aktuelle Zeile)
  const applyPresetToItem = (currentItem, newTypeKey) => {
    const preset = presetFor(newTypeKey);
    // Nur überschreiben wenn Position noch leer/unangetastet (Name leer oder 0€)
    const isEmpty = !currentItem.name || currentItem.name.trim() === "" || (Number(currentItem.unit_price_net) === 0 && Number(currentItem.quantity) === 1);
    if (isEmpty && preset.name) return preset;
    return { type_key: newTypeKey };
  };

  // --- Interner Kalkulator (nur für Lukas sichtbar, nicht auf Kundendokument) ---
  const [calcOpen, setCalcOpen] = useState(false);
  const [calc, setCalc] = useState({
    personen: 6,
    waren_pp: 25,
    stunden: 6,
    stundensatz: 55,
    km: 0,
    km_satz: 0.6,
    marge_faktor: 1.5,
  });
  const setCalcField = (k, v) => setCalc((c) => ({ ...c, [k]: v }));
  const calcSelbstkosten = () => {
    const waren = Number(calc.personen || 0) * Number(calc.waren_pp || 0);
    const arbeit = Number(calc.stunden || 0) * Number(calc.stundensatz || 0);
    const fahrt = Number(calc.km || 0) * Number(calc.km_satz || 0);
    return { waren, arbeit, fahrt, total: waren + arbeit + fahrt };
  };
  const calcVerkaufspreis = () => {
    const sk = calcSelbstkosten();
    const sk_ohne_fahrt = sk.waren + sk.arbeit;
    const verkauf_ohne_fahrt = Math.round(sk_ohne_fahrt * Number(calc.marge_faktor || 1));
    const verkauf_pp = calc.personen > 0 ? Math.round(verkauf_ohne_fahrt / calc.personen) : 0;
    return { sk, sk_ohne_fahrt, verkauf_ohne_fahrt, verkauf_pp };
  };
  const applyCalcToPositions = () => {
    const { verkauf_pp, sk } = calcVerkaufspreis();
    const menuPos = {
      type_key: "menu_pauschale_pp",
      name: "Exklusives Private Dining",
      description: "Enthält individuelle Menüplanung, Einkauf aller Frischezutaten, Vorbereitung, Zubereitung vor Ort, professionellen Tischservice sowie finale Küchenreinigung.",
      quantity: Number(calc.personen || 0),
      unit_name: "Person",
      unit_price_net: verkauf_pp,
      tax_rate: taxRateForCustomerType(form.customer_type),
    };
    const fahrtPos = sk.fahrt > 0 ? {
      type_key: "fahrtpauschale",
      name: "Fahrt- und Logistikpauschale",
      description: `Anfahrt zum Einsatzort (${calc.km} km).`,
      quantity: 1,
      unit_name: "Pauschal",
      unit_price_net: Math.round(sk.fahrt),
      tax_rate: taxRateForCustomerType(form.customer_type),
    } : null;
    if (mode === "multi") {
      // In das erste Paket einfügen (wenn kein passendes vorhanden, ersetze Positionen)
      setPackages((ps) => ps.map((p, i) => i === 0 ? {
        ...p,
        line_items: fahrtPos ? [menuPos, fahrtPos] : [menuPos],
      } : p));
      toast.success(`Preise in „${packages[0]?.name || "Paket 1"}" übernommen — ${verkauf_pp} €/Pers`);
    } else {
      setLineItems(fahrtPos ? [menuPos, fahrtPos] : [menuPos]);
      toast.success(`Positionen aktualisiert — ${verkauf_pp} €/Pers`);
    }
    setCalcOpen(false);
  };
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState("edit"); // "edit" | "review"
  // Multi-Package Mode
  const [mode, setMode] = useState(savedDraft?.mode || "single"); // "single" | "multi"
  const emptyPkgItem = () => ({ type_key: "sonstiges", name: "", description: "", quantity: 1, unit_name: "Stück", unit_price_net: 0, tax_rate: taxRateForCustomerType(form.customer_type) });
  const [packages, setPackages] = useState(savedDraft?.packages || [
    { name: "Menü Klassik", description: "3-Gänge saisonal", line_items: [
      { type_key: "privatkoch_zutaten", name: "Lebensmittel-Einkauf", description: "", quantity: 1, unit_name: "Pauschal", unit_price_net: 400, tax_rate: MWST_RATE_PERCENT_REDUCED },
      { type_key: "privatkoch_einkauf", name: "Einkaufspauschale", description: "", quantity: 1, unit_name: "Pauschal", unit_price_net: 60, tax_rate: MWST_RATE_PERCENT_REDUCED },
      { type_key: "privatkoch_zubereitung", name: "Stundenlohn Privatkoch", description: "", quantity: 6, unit_name: "Std.", unit_price_net: 55, tax_rate: MWST_RATE_PERCENT_REDUCED },
    ]},
    { name: "Menü Premium", description: "5-Gänge Fine-Dining", line_items: [
      { type_key: "privatkoch_zutaten", name: "Lebensmittel-Einkauf", description: "", quantity: 1, unit_name: "Pauschal", unit_price_net: 720, tax_rate: MWST_RATE_PERCENT_REDUCED },
      { type_key: "privatkoch_einkauf", name: "Einkaufspauschale", description: "", quantity: 1, unit_name: "Pauschal", unit_price_net: 90, tax_rate: MWST_RATE_PERCENT_REDUCED },
      { type_key: "privatkoch_zubereitung", name: "Stundenlohn Privatkoch", description: "", quantity: 8, unit_name: "Std.", unit_price_net: 55, tax_rate: MWST_RATE_PERCENT_REDUCED },
    ]},
  ]);
  const updatePackage = (idx, patch) => setPackages((ps) => ps.map((p, i) => i === idx ? { ...p, ...patch } : p));
  const removePackage = (idx) => setPackages((ps) => ps.length > 2 ? ps.filter((_, i) => i !== idx) : ps);
  const addPackage = () => setPackages((ps) => ps.length >= 4 ? ps : [...ps, { name: `Paket ${ps.length + 1}`, description: "", line_items: [emptyPkgItem()] }]);
  const updatePkgItem = (pIdx, iIdx, patch) => setPackages((ps) => ps.map((p, i) => i === pIdx ? { ...p, line_items: p.line_items.map((it, j) => j === iIdx ? { ...it, ...patch } : it) } : p));
  const removePkgItem = (pIdx, iIdx) => setPackages((ps) => ps.map((p, i) => i === pIdx ? { ...p, line_items: p.line_items.filter((_, j) => j !== iIdx) } : p));
  const addPkgItem = (pIdx) => setPackages((ps) => ps.map((p, i) => i === pIdx ? { ...p, line_items: [...p.line_items, emptyPkgItem()] } : p));
  const pkgNet = (pkg) => pkg.line_items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price_net || 0), 0);
  const pkgGross = (pkg) => Math.round(
    pkg.line_items.reduce((s, it) => {
      const net = Number(it.quantity || 0) * Number(it.unit_price_net || 0);
      return s + net * (1 + Number(it.tax_rate || MWST_RATE_PERCENT) / 100);
    }, 0) * 100
  ) / 100;

  // Auto-Save Entwurf: bei jeder Änderung von form/dateMode/customDates/lineItems
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ form, dateMode, customDates, lineItems, mode, packages }));
    } catch { /* Speicher voll oder deaktiviert – Draft geht dann verloren, kein Blocker */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dateMode, customDates, lineItems, mode, packages]);

  // Beim Mount: sofern Draft-Positionen andere tax_rate haben als der aktuelle Kundentyp
  // (z.B. aus einem älteren Draft ohne Kundentyp-Awareness), alle Positionen syncronisieren.
  useEffect(() => {
    const expected = taxRateForCustomerType(form.customer_type);
    const itemsMismatch = lineItems.some((it) => Number(it.tax_rate || MWST_RATE_PERCENT) !== expected);
    const pkgsMismatch = packages.some((p) => (p.line_items || []).some((it) => Number(it.tax_rate || MWST_RATE_PERCENT) !== expected));
    if (itemsMismatch) {
      setLineItems((items) => items.map((it) => ({ ...it, tax_rate: expected })));
    }
    if (pkgsMismatch) {
      setPackages((pkgs) => pkgs.map((p) => ({
        ...p,
        line_items: (p.line_items || []).map((it) => ({ ...it, tax_rate: expected })),
      })));
    }
    // Läuft nur einmal beim Öffnen des Modals — keine Dependencies gewünscht
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearDraft = () => {
    if (!window.confirm("Formular komplett zurücksetzen? Alle Eingaben gehen verloren.")) return;
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setForm(DEFAULT_FORM);
    setDateMode("range");
    setCustomDates([]);
    setLineItems(DEFAULT_ITEMS);
    setNewCustomDate("");
    setRangeFrom("");
    setRangeTo("");
    toast.success("Entwurf zurückgesetzt");
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateItem = (idx, patch) => setLineItems((items) => items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const removeItem = (idx) => setLineItems((items) => items.filter((_, i) => i !== idx));
  const defaultTaxRate = taxRateForCustomerType(form.customer_type);
  const addItem = () => setLineItems((items) => [
    ...items,
    { type_key: "sonstiges", name: "", description: "", quantity: 1, unit_name: "Stück", unit_price_net: 0, tax_rate: defaultTaxRate },
  ]);

  const { netSum, mwstSum, grossSum } = calcVatTotals(lineItems, MWST_RATE_PERCENT);

  // Wechsel Betrieb ⇆ Privatkunde: setzt Kundentyp UND passt automatisch die MwSt aller
  // Positionen (auch in Multi-Package) an — 19 % Regelsatz für B2B-Mietkocheinsätze,
  // 7 % ermäßigt für Privatkoch-Leistungen (§ 12 Abs. 2 UStG).
  const switchCustomerType = (newType) => {
    if (form.customer_type === newType) return;
    const newRate = taxRateForCustomerType(newType);
    setField("customer_type", newType);
    setLineItems((items) => items.map((it) => ({ ...it, tax_rate: newRate })));
    setPackages((pkgs) => pkgs.map((p) => ({
      ...p,
      line_items: (p.line_items || []).map((it) => ({ ...it, tax_rate: newRate })),
    })));
    toast(`MwSt aller Positionen auf ${newRate} % gesetzt`, { icon: "🧾" });
  };

  // ---- Custom-Dates Helpers ----
  const addCustomDate = () => {
    if (!newCustomDate) { toast.error("Bitte ein Datum wählen"); return; }
    if (customDates.some((d) => d.date === newCustomDate)) {
      toast.error("Dieser Tag ist bereits in der Liste");
      return;
    }
    setCustomDates((cds) => [...cds, { date: newCustomDate, von: "", bis: "" }].sort((a, b) => a.date.localeCompare(b.date)));
    setNewCustomDate("");
  };
  const updateCustomDate = (idx, patch) =>
    setCustomDates((cds) => cds.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const removeCustomDate = (idx) =>
    setCustomDates((cds) => cds.filter((_, i) => i !== idx));
  const addDateRange = () => {
    if (!rangeFrom || !rangeTo) { toast.error("Bitte Von & Bis wählen"); return; }
    if (rangeFrom > rangeTo) { toast.error("Von muss vor Bis liegen"); return; }
    // Parse ISO-String Y-M-D zeitzonen-sicher (kein toISOString() → würde in CEST einen Tag zurück schieben)
    const [fy, fm, fd] = rangeFrom.split("-").map(Number);
    const [ty, tm, td] = rangeTo.split("-").map(Number);
    const start = new Date(fy, fm - 1, fd);
    const end = new Date(ty, tm - 1, td);
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const added = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const iso = fmt(d);
      if (!customDates.some((c) => c.date === iso) && !added.some((c) => c.date === iso)) {
        added.push({ date: iso, von: "", bis: "" });
      }
    }
    if (added.length === 0) { toast.error("Alle Tage sind bereits in der Liste"); return; }
    setCustomDates((cds) => [...cds, ...added].sort((a, b) => a.date.localeCompare(b.date)));
    toast.success(`${added.length} ${added.length === 1 ? "Tag" : "Tage"} hinzugefügt`);
    setRangeFrom("");
    setRangeTo("");
  };
  const clearCustomDates = () => setCustomDates([]);

  const dateValid =
    dateMode === "range" ? !!form.einsatzdatum : customDates.length > 0;
  const singleValid = lineItems.length && lineItems.every((it) => it.name && it.name.trim()) && netSum > 0;
  const multiValid = packages.length >= 2 && packages.length <= 4 && packages.every((p) => p.name.trim() && p.line_items.length > 0 && p.line_items.every((it) => it.name && it.name.trim()) && pkgNet(p) > 0);
  const canSubmit = form.betrieb.trim() && form.email.trim() && form.ort.trim() && dateValid && (mode === "multi" ? multiValid : singleValid);

  const submit = () => {
    if (!canSubmit) {
      const reason = mode === "multi" ? "2–4 Pakete mit Name + mind. 1 Position pro Paket erforderlich" : "mindestens eine Position mit Namen";
      toast.error(`Bitte alle Pflichtfelder ausfüllen (${reason})`);
      return;
    }
    setStep("review");
  };

  const buildPayload = () => {
    const payload = {
      ...form,
      anzahl_tage: Number(form.anzahl_tage) || 1,
      valid_days: Number(form.valid_days) || 14,
      deposit_percent: Number(form.deposit_percent) || 0,
      deposit_due_days: Number(form.deposit_due_days) || 7,
      custom_dates: dateMode === "custom" ? customDates : [],
    };
    if (mode === "multi") {
      payload.line_items = [];
      payload.packages = packages.map((p) => ({
        name: p.name.trim(),
        description: (p.description || "").trim(),
        line_items: p.line_items.map((it) => ({
          ...it,
          quantity: Number(it.quantity) || 0,
          unit_price_net: Number(it.unit_price_net) || 0,
        })),
      }));
    } else {
      payload.line_items = lineItems.map((it) => ({
        ...it,
        quantity: Number(it.quantity) || 0,
        unit_price_net: Number(it.unit_price_net) || 0,
      }));
    }
    if (dateMode === "custom" && customDates.length > 0) {
      payload.einsatzdatum = customDates[0].date;
      payload.anzahl_tage = customDates.length;
    }
    return payload;
  };

  const [sendingTest, setSendingTest] = useState(false);
  const sendTestPreview = async () => {
    setSendingTest(true);
    try {
      const payload = buildPayload();
      const { data } = await axios.post(
        `${API}/admin/quotes/preview-test-email`,
        payload,
        { headers: { "X-Admin-Password": pwd } }
      );
      toast.success(`Vorschau-PDF wurde an ${data.sent_to || "dich"} gesendet — bitte E-Mail-Postfach prüfen`);
    } catch (err) {
      toast.error("Test-Vorschau fehlgeschlagen: " + (err.response?.data?.detail || err.message));
    } finally {
      setSendingTest(false);
    }
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      const payload = buildPayload();
      await axios.post(`${API}/admin/quotes`, payload, { headers: { "X-Admin-Password": pwd } });
      const okMsg = mode === "multi"
        ? `Multi-Package-Angebot (${packages.length} Pakete) wird an ${form.email} versendet — Kunde wählt online ein Paket`
        : "Angebot wird erstellt & im Hintergrund an den Kunden versendet";
      toast.success(okMsg, { duration: 6000 });
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      onSuccess();
    } catch (err) {
      toast.error("Fehler: " + fehlerText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 md:p-6" data-testid="quote-create-modal">
      <div className="bg-white w-full max-w-4xl max-h-[94vh] overflow-y-auto border border-[#E3DECE]">
        <div className="sticky top-0 bg-white border-b border-[#E3DECE] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">{step === "review" ? "Vorschau vor Versand" : "Neues Angebot"}</p>
            <h3 className="font-serif text-2xl text-[#2C2A26]">{step === "review" ? "Bitte final prüfen" : "Unverbindliches Angebot erstellen"}</h3>
            <p className="text-xs text-[#757067] mt-1">{step === "review" ? "So sieht das Angebot für den Kunden aus. Wenn alles passt → Absenden." : "Wird in LexOffice angelegt und per E-Mail an den Kunden versendet. Eingaben werden automatisch gespeichert."}</p>
          </div>
          <div className="flex items-center gap-2">
            {step === "edit" && (
              <button
                onClick={clearDraft}
                data-testid="quote-draft-reset"
                title="Formular komplett leeren"
                className="text-[10px] uppercase tracking-[0.15em] text-red-700 hover:text-red-900 hover:bg-red-50 px-3 py-2 border border-red-200"
              >
                ⟲ Zurücksetzen
              </button>
            )}
            <button onClick={onClose} className="text-[#757067] hover:text-[#2C2A26] text-2xl leading-none px-3">×</button>
          </div>
        </div>

        {step === "review" ? (
          <div className="p-6 space-y-6" data-testid="quote-review-panel">
            <div className="border border-[#E3DECE] p-5 bg-[#FAFAF7]">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Empfänger</p>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${form.customer_type === "private" ? "border-[#BA6D52] text-[#BA6D52]" : "border-[#757067] text-[#757067]"}`}>
                  {form.customer_type === "private" ? "Privatkunde" : "Betrieb"}
                </span>
              </div>
              <p className="font-medium">{form.betrieb}</p>
              {form.ansprechpartner && form.customer_type !== "private" && <p className="text-sm text-[#757067]">Ansprechpartner: {form.ansprechpartner}</p>}
              {form.anlass && <p className="text-sm text-[#BA6D52] italic mt-1">Anlass: {form.anlass}</p>}
              <p className="text-sm text-[#757067]">{form.email}{form.telefon ? ` · ${form.telefon}` : ""}</p>
              <p className="text-sm text-[#757067] mt-1">{form.ort}</p>
            </div>

            <div className="border border-[#E3DECE] p-5 bg-[#FAFAF7]">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52] mb-2">Einsatz</p>
              {dateMode === "custom" && customDates.length > 0 ? (
                <div className="text-sm">
                  <p className="mb-2"><b>{customDates.length} Einsatztage</b></p>
                  <ul className="space-y-1 text-[#2C2A26]">
                    {customDates.map((cd, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="text-[#BA6D52] font-mono text-xs">•</span>
                        <span>
                          {new Date(cd.date).toLocaleDateString("de-DE")}
                          {(cd.von && cd.bis) ? ` · ${cd.von}–${cd.bis} Uhr` : cd.von ? ` · ab ${cd.von} Uhr` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm">
                  {form.einsatzdatum ? new Date(form.einsatzdatum).toLocaleDateString("de-DE") : "—"}
                  {Number(form.anzahl_tage) > 1 ? ` — ${form.anzahl_tage} Tage` : ""}
                  {form.von_uhrzeit && form.bis_uhrzeit ? ` · ${form.von_uhrzeit}–${form.bis_uhrzeit} Uhr` : ""}
                </p>
              )}
              {form.notes && <p className="text-xs mt-2 whitespace-pre-line text-[#2C2A26]">{form.notes}</p>}
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52] mb-2">{mode === "multi" ? `${packages.length} Pakete zur Auswahl` : "Positionen"}</p>
              {mode === "multi" ? (
                <div className="space-y-4" data-testid="quote-review-packages">
                  {packages.map((pkg, pIdx) => {
                    const pNet = pkgNet(pkg);
                    // MwSt pro Position mit tatsächlicher tax_rate (7 % Privatkunden / 19 % B2B)
                    const pMwst = Math.round(pkg.line_items.reduce((s, it) => {
                      const net = Number(it.quantity || 0) * Number(it.unit_price_net || 0);
                      return s + net * (Number(it.tax_rate || MWST_RATE_PERCENT) / 100);
                    }, 0) * 100) / 100;
                    const pGross = Math.round((pNet + pMwst) * 100) / 100;
                    return (
                      <div key={pIdx} className="border-2 border-[#BA6D52]/40 bg-white" data-testid={`quote-review-package-${pIdx}`}>
                        <div className="bg-[#FBF7EC] px-5 py-3 border-b border-[#E3DECE]">
                          <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Paket {pIdx + 1}</p>
                          <p className="font-serif text-xl text-[#2C2A26]">{pkg.name || <span className="italic text-red-600">Name fehlt</span>}</p>
                          {pkg.description && <p className="text-sm text-[#5C574E] mt-1 italic">{pkg.description}</p>}
                        </div>
                        <div>
                          {pkg.line_items.map((it, idx) => {
                            const rn = Number(it.quantity || 0) * Number(it.unit_price_net || 0);
                            return (
                              <div key={idx} className={`px-5 py-3 flex items-baseline justify-between ${idx > 0 ? "border-t border-[#E3DECE]" : ""}`}>
                                <div className="flex-1 pr-4">
                                  <p className="text-sm font-medium">{it.name || <span className="italic text-red-600">Bezeichnung fehlt</span>}</p>
                                  <p className="text-xs text-[#757067] mt-1">
                                    {Number(it.quantity || 0).toString().replace(".", ",")} {it.unit_name} × {Number(it.unit_price_net || 0).toFixed(2).replace(".", ",")} € netto
                                  </p>
                                </div>
                                <p className="font-mono text-sm whitespace-nowrap">{rn.toFixed(2).replace(".", ",")} €</p>
                              </div>
                            );
                          })}
                        </div>
                        <div className="bg-[#FAFAF7] px-5 py-3 border-t border-[#E3DECE] flex items-baseline justify-between">
                          <span className="text-xs uppercase tracking-[0.2em]">Gesamt Brutto</span>
                          <span className="font-serif text-2xl text-[#BA6D52]" data-testid={`quote-review-package-${pIdx}-gross`}>{pGross.toFixed(2).replace(".", ",")} €</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
              <><div className="border border-[#E3DECE]">
                {lineItems.map((it, idx) => {
                  const rowNet = Number(it.quantity || 0) * Number(it.unit_price_net || 0);
                  return (
                    <div key={idx} className={`px-4 py-3 flex items-baseline justify-between ${idx > 0 ? "border-t border-[#E3DECE]" : ""}`}>
                      <div className="flex-1 pr-4">
                        <p className="text-sm font-medium">{it.name}</p>
                        {it.description && <p className="text-xs text-[#757067] mt-1">{it.description}</p>}
                        <p className="text-xs text-[#757067] mt-1">
                          {Number(it.quantity || 0).toString().replace(".", ",")} {it.unit_name} × {Number(it.unit_price_net || 0).toFixed(2).replace(".", ",")} € netto
                        </p>
                      </div>
                      <p className="font-mono text-sm whitespace-nowrap">{rowNet.toFixed(2).replace(".", ",")} €</p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-[#757067]">Netto</span><span className="font-mono">{netSum.toFixed(2).replace(".", ",")} €</span></div>
                <div className="flex justify-between"><span className="text-[#757067]">zzgl. {defaultTaxRate} % MwSt</span><span className="font-mono">{mwstSum.toFixed(2).replace(".", ",")} €</span></div>
                <div className="flex justify-between border-t border-[#E3DECE] pt-2 items-baseline">
                  <span className="text-xs uppercase tracking-[0.2em]">Gesamt Brutto</span>
                  <span className="font-serif text-2xl text-[#BA6D52]" data-testid="quote-review-gross">{grossSum.toFixed(2).replace(".", ",")} €</span>
                </div>
              </div></>
              )}
            </div>

            <div className="border border-[#E3DECE] p-4 bg-[#FAFAF7] text-sm space-y-1">
              <p>Gültig für <b>{Number(form.valid_days) || 14} Tage</b> nach Versand</p>
              {Number(form.deposit_percent) > 0 ? (
                <p>Anzahlung: <b>{(grossSum * Number(form.deposit_percent) / 100).toFixed(2).replace(".", ",")} €</b> ({form.deposit_percent} % vom Brutto), fällig {form.deposit_due_days || 7} Tage nach Annahme</p>
              ) : (
                <p className="text-[#757067]">Keine Anzahlung vorgesehen</p>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              {mode === "multi" ? (
                <>
                  <b>⚠ Nach dem Absenden</b> wird eine E-Mail mit {packages.length} Paket-Auswahl an <b>{form.email}</b> versendet.
                  Das <b>LexOffice-Angebot wird erst nach Paket-Wahl</b> automatisch erzeugt (mit den Positionen des gewählten Pakets).
                </>
              ) : (
                <>
                  <b>⚠ Nach dem Absenden</b> wird das Angebot direkt in LexOffice erstellt (mit Angebotsnummer) und per E-Mail an <b>{form.email}</b> versendet. Änderungen sind danach nicht mehr möglich — nur Stornieren.
                </>
              )}
            </div>
          </div>
        ) : (
        <div className="p-6 space-y-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-[0.2em] text-[#757067] font-semibold">Kunde</p>
              <div className="inline-flex border border-[#E3DECE] text-xs" role="tablist" aria-label="Kundentyp">
                <button
                  type="button"
                  onClick={() => switchCustomerType("business")}
                  data-testid="quote-customer-business"
                  className={`px-3 py-1.5 uppercase tracking-wider ${form.customer_type !== "private" ? "bg-[#BA6D52] text-white" : "bg-white text-[#757067] hover:text-[#2C2A26]"}`}
                >
                  Betrieb
                </button>
                <button
                  type="button"
                  onClick={() => switchCustomerType("private")}
                  data-testid="quote-customer-private"
                  className={`px-3 py-1.5 uppercase tracking-wider border-l border-[#E3DECE] ${form.customer_type === "private" ? "bg-[#BA6D52] text-white" : "bg-white text-[#757067] hover:text-[#2C2A26]"}`}
                >
                  Privatkunde
                </button>
              </div>
            </div>
            <div className="mb-3 -mt-1 p-2 border border-[#E3DECE] bg-[#FAF5F1] text-[10px] text-[#757067] leading-relaxed" data-testid="quote-vat-hint">
              {form.customer_type === "private" ? (
                <><b className="text-[#BA6D52] uppercase tracking-wider">7 % MwSt (ermäßigt)</b> · Privatkoch-Leistung — alle Positionen stehen auf dem ermäßigten Steuersatz für Verpflegungsdienstleistungen (§ 12 Abs. 2 UStG).</>
              ) : (
                <><b className="text-[#BA6D52] uppercase tracking-wider">19 % MwSt (Regelsatz)</b> · B2B-Mietkocheinsatz — alle Positionen stehen auf dem Regelsatz.</>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <QuoteInput
                label={form.customer_type === "private" ? "Familie / Kundenname" : "Betrieb"}
                valueKey="betrieb"
                required
                placeholder={form.customer_type === "private" ? "z. B. Familie Mustermann" : "z. B. Restaurant Beispiel GmbH"}
                form={form}
                setField={setField}
              />
              {form.customer_type !== "private" && (
                <QuoteInput label="Ansprechpartner" valueKey="ansprechpartner"  form={form} setField={setField}/>
              )}
              <QuoteInput label="E-Mail" valueKey="email" type="email" required  form={form} setField={setField}/>
              <QuoteInput label="Telefon" valueKey="telefon"  form={form} setField={setField}/>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Anlass (optional)</label>
                <input
                  type="text"
                  value={form.anlass}
                  onChange={(e) => setField("anlass", e.target.value)}
                  data-testid="quote-field-anlass"
                  placeholder={form.customer_type === "private" ? "z. B. Private Dining · Geburtstag · Hochzeit" : "z. B. Firmenfeier · Sommerfest · Kick-off"}
                  className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
                />
                <p className="text-[10px] text-[#757067] mt-1">Wird als Untertitel im Angebots-PDF angezeigt.</p>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Adresse (Straße Nr, PLZ Ort) *</label>
                <input
                  type="text"
                  value={form.ort}
                  onChange={(e) => setField("ort", e.target.value)}
                  data-testid="quote-field-ort"
                  placeholder="Musterstraße 12, 12345 Musterstadt"
                  className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
                />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-[0.2em] text-[#757067] font-semibold">Einsatz</p>
              <div className="inline-flex border border-[#E3DECE] text-xs" role="tablist" aria-label="Datums-Modus">
                <button
                  type="button"
                  onClick={() => setDateMode("range")}
                  data-testid="quote-datemode-range"
                  className={`px-3 py-1.5 uppercase tracking-wider ${dateMode === "range" ? "bg-[#BA6D52] text-white" : "bg-white text-[#757067] hover:text-[#2C2A26]"}`}
                >
                  Datumsbereich
                </button>
                <button
                  type="button"
                  onClick={() => setDateMode("custom")}
                  data-testid="quote-datemode-custom"
                  className={`px-3 py-1.5 uppercase tracking-wider border-l border-[#E3DECE] ${dateMode === "custom" ? "bg-[#BA6D52] text-white" : "bg-white text-[#757067] hover:text-[#2C2A26]"}`}
                >
                  Einzeltage
                </button>
              </div>
            </div>

            {dateMode === "range" ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <QuoteInput label="Einsatzdatum" valueKey="einsatzdatum" type="date" required  form={form} setField={setField}/>
                <QuoteInput label="Anzahl Tage" valueKey="anzahl_tage" type="number"  form={form} setField={setField}/>
                <QuoteInput label="Von (optional)" valueKey="von_uhrzeit" type="time"  form={form} setField={setField}/>
                <QuoteInput label="Bis (optional)" valueKey="bis_uhrzeit" type="time"  form={form} setField={setField}/>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-[#757067] leading-relaxed">
                  Wähle einzelne Tage <b>oder</b> ganze Zeiträume (auch nicht-fortlaufend, z.&nbsp;B. 29.09.–04.10. + Pause + 07.10.–11.10.).
                </p>

                {/* Zeitraum hinzufügen */}
                <div className="border border-[#E3DECE] bg-[#FAFAF7] p-3">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] font-semibold mb-2">Zeitraum hinzufügen</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Von</label>
                      <input
                        type="date"
                        value={rangeFrom}
                        onChange={(e) => setRangeFrom(e.target.value)}
                        data-testid="quote-range-from"
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Bis</label>
                      <input
                        type="date"
                        value={rangeTo}
                        onChange={(e) => setRangeTo(e.target.value)}
                        data-testid="quote-range-to"
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={addDateRange}
                      data-testid="quote-range-add"
                      className="bg-[#BA6D52] text-white hover:bg-[#9E5841] py-2 px-4 text-xs uppercase tracking-[0.15em]"
                    >
                      + Zeitraum übernehmen
                    </button>
                  </div>
                  <p className="text-[11px] text-[#757067] mt-2 font-light italic">
                    Alle Tage des Zeitraums werden einzeln in die Liste eingefügt — beliebig oft wiederholbar.
                  </p>
                </div>

                {/* Einzeltag hinzufügen */}
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Einzelnen Tag hinzufügen</label>
                    <input
                      type="date"
                      value={newCustomDate}
                      onChange={(e) => setNewCustomDate(e.target.value)}
                      data-testid="quote-customdate-new"
                      className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addCustomDate}
                    data-testid="quote-customdate-add"
                    className="bg-white border border-[#BA6D52] text-[#BA6D52] hover:bg-[#BA6D52] hover:text-white py-2 px-4 text-xs uppercase tracking-[0.15em] whitespace-nowrap"
                  >
                    + Hinzufügen
                  </button>
                </div>
                {customDates.length === 0 ? (
                  <p className="text-sm text-[#757067] italic border border-dashed border-[#E3DECE] py-6 text-center">
                    Noch keine Einsatztage — füge oben Tage hinzu.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {customDates.map((cd, idx) => (
                      <div key={idx} className="border border-[#E3DECE] bg-[#FAFAF7] p-3" data-testid={`quote-customdate-${idx}`}>
                        <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-12 md:col-span-4">
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Datum</label>
                            <input
                              type="date"
                              value={cd.date}
                              onChange={(e) => updateCustomDate(idx, { date: e.target.value })}
                              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1.5 px-2 text-sm"
                            />
                          </div>
                          <div className="col-span-5 md:col-span-3">
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Von (optional)</label>
                            <input
                              type="time"
                              value={cd.von}
                              onChange={(e) => updateCustomDate(idx, { von: e.target.value })}
                              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1.5 px-2 text-sm"
                            />
                          </div>
                          <div className="col-span-5 md:col-span-3">
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Bis (optional)</label>
                            <input
                              type="time"
                              value={cd.bis}
                              onChange={(e) => updateCustomDate(idx, { bis: e.target.value })}
                              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1.5 px-2 text-sm"
                            />
                          </div>
                          <div className="col-span-2 md:col-span-2 text-right">
                            <button
                              type="button"
                              onClick={() => removeCustomDate(idx)}
                              data-testid={`quote-customdate-remove-${idx}`}
                              className="text-red-700 hover:text-red-900 hover:bg-red-50 px-3 py-2 text-lg leading-none"
                              title="Tag entfernen"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-xs text-[#757067]">
                        {customDates.length} {customDates.length === 1 ? "Einsatztag" : "Einsatztage"} · Kalender wird automatisch geblockt bei Annahme.
                      </p>
                      <button
                        type="button"
                        onClick={clearCustomDates}
                        data-testid="quote-customdates-clear"
                        className="text-[10px] uppercase tracking-[0.15em] text-red-700 hover:text-red-900 hover:bg-red-50 px-3 py-1.5"
                      >
                        Alle löschen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Beschreibung / Anmerkungen für den Kunden</label>
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                placeholder="z. B. Menü-Absprache, Ansprechpartner vor Ort etc."
                className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
              />
            </div>
          </div>

          <div>
            <button
              type="button"
              onClick={() => setCalcOpen((o) => !o)}
              data-testid="quote-calc-toggle"
              className="w-full flex items-center justify-between border border-dashed border-[#BA6D52]/60 bg-[#FBF7EC]/50 hover:bg-[#FBF7EC] px-4 py-3 text-left"
            >
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#BA6D52] font-semibold">🧮 Interner Kalkulator</p>
                <p className="text-[11px] text-[#8A7A68] mt-1">Nur du siehst das — Kunde sieht nur die fertige Pauschale.</p>
              </div>
              <span className="text-xs text-[#BA6D52]">{calcOpen ? "Schließen ▲" : "Öffnen ▼"}</span>
            </button>
            {calcOpen && (
              <div className="border-x border-b border-[#BA6D52]/40 bg-white p-5 space-y-4" data-testid="quote-calc-panel">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.15em] text-[#BA6D52]">Personen</label>
                    <input type="number" min="1" value={calc.personen} onChange={(e) => setCalcField("personen", Number(e.target.value) || 0)} data-testid="calc-personen" className="mt-1 w-full bg-[#FAFAF7] border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.15em] text-[#BA6D52]">Waren/Pers €</label>
                    <input type="number" step="0.5" value={calc.waren_pp} onChange={(e) => setCalcField("waren_pp", Number(e.target.value) || 0)} data-testid="calc-waren" className="mt-1 w-full bg-[#FAFAF7] border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.15em] text-[#BA6D52]">Stunden</label>
                    <input type="number" step="0.5" value={calc.stunden} onChange={(e) => setCalcField("stunden", Number(e.target.value) || 0)} data-testid="calc-stunden" className="mt-1 w-full bg-[#FAFAF7] border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.15em] text-[#BA6D52]">Stundensatz €</label>
                    <input type="number" step="1" value={calc.stundensatz} onChange={(e) => setCalcField("stundensatz", Number(e.target.value) || 0)} data-testid="calc-satz" className="mt-1 w-full bg-[#FAFAF7] border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase tracking-[0.15em] text-[#BA6D52]">Marge-Faktor</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="range" min="1.0" max="3.0" step="0.05" value={calc.marge_faktor} onChange={(e) => setCalcField("marge_faktor", Number(e.target.value))} data-testid="calc-marge" className="flex-1 accent-[#BA6D52]" />
                      <span className="text-sm font-mono w-14 text-right">{Number(calc.marge_faktor).toFixed(2)}×</span>
                    </div>
                    <p className="text-[10px] text-[#8A7A68] mt-1">1,5× = solide Marge · 2,0× = Fine-Dining · 2,5× = Premium</p>
                  </div>
                </div>

                {(() => {
                  const { sk, sk_ohne_fahrt, verkauf_ohne_fahrt, verkauf_pp } = calcVerkaufspreis();
                  const marge_eur = verkauf_ohne_fahrt - sk_ohne_fahrt;
                  return (
                    <div className="border-t border-[#E3DECE] pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="text-xs space-y-1">
                        <p className="text-[10px] uppercase tracking-[0.15em] text-[#757067] font-semibold mb-2">Interne Kalkulation</p>
                        <div className="flex justify-between"><span className="text-[#757067]">Waren ({calc.personen} × {calc.waren_pp} €)</span><span className="font-mono">{sk.waren.toFixed(2)} €</span></div>
                        <div className="flex justify-between"><span className="text-[#757067]">Arbeit ({calc.stunden} × {calc.stundensatz} €)</span><span className="font-mono">{sk.arbeit.toFixed(2)} €</span></div>
                        {sk.fahrt > 0 && <div className="flex justify-between"><span className="text-[#757067]">Fahrt ({calc.km} × {calc.km_satz} €)</span><span className="font-mono">{sk.fahrt.toFixed(2)} €</span></div>}
                        <div className="flex justify-between border-t border-[#E3DECE] pt-1 mt-1"><span className="text-[#2C2A26] font-medium">Selbstkosten (ohne Fahrt)</span><span className="font-mono">{sk_ohne_fahrt.toFixed(2)} €</span></div>
                        <div className="flex justify-between text-emerald-800"><span>Marge ({Number(calc.marge_faktor).toFixed(2)}×)</span><span className="font-mono">+ {marge_eur.toFixed(2)} €</span></div>
                      </div>
                      <div className="bg-[#FBF7EC] border border-[#BA6D52]/30 p-4">
                        <p className="text-[10px] uppercase tracking-[0.15em] text-[#BA6D52] font-semibold">Was der Kunde sieht</p>
                        <div className="mt-2 space-y-1 text-sm">
                          <div className="flex justify-between"><span>Menü-Pauschale × {calc.personen}</span><span className="font-mono">{verkauf_ohne_fahrt.toFixed(2)} €</span></div>
                          <div className="text-xs text-[#757067]">= {verkauf_pp} € / Person</div>
                          {sk.fahrt > 0 && <div className="flex justify-between mt-1"><span>Fahrtpauschale</span><span className="font-mono">{sk.fahrt.toFixed(2)} €</span></div>}
                          <div className="flex justify-between border-t border-[#BA6D52]/30 pt-2 mt-2 items-baseline">
                            <span className="text-xs uppercase tracking-[0.15em]">Gesamt Netto</span>
                            <span className="font-serif text-xl text-[#BA6D52]">{(verkauf_ohne_fahrt + sk.fahrt).toFixed(2)} €</span>
                          </div>
                        </div>
                        <button type="button" onClick={applyCalcToPositions} data-testid="calc-apply" className="mt-4 w-full bg-[#BA6D52] hover:bg-[#9E5841] text-white py-3 text-xs uppercase tracking-[0.15em]">
                          → In Positionen übernehmen
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          <div>
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-3">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#757067] font-semibold">Angebots-Modus</p>
                <p className="text-[11px] text-[#8A7A68] mt-1">Einzelnes Angebot: eine Positionsliste. Mehrere Pakete: der Kunde wählt online eines aus.</p>
              </div>
              <div className="inline-flex border border-[#E3DECE] rounded-none overflow-hidden" data-testid="quote-mode-toggle">
                <button
                  type="button"
                  onClick={() => setMode("single")}
                  data-testid="quote-mode-single"
                  className={`px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${mode === "single" ? "bg-[#BA6D52] text-white" : "bg-white text-[#757067] hover:bg-[#FAFAF7]"}`}
                >Einzelnes Angebot</button>
                <button
                  type="button"
                  onClick={() => setMode("multi")}
                  data-testid="quote-mode-multi"
                  className={`px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${mode === "multi" ? "bg-[#BA6D52] text-white" : "bg-white text-[#757067] hover:bg-[#FAFAF7]"}`}
                >Mehrere Pakete</button>
              </div>
            </div>

            {mode === "multi" ? (
              <div className="space-y-4" data-testid="quote-packages-editor">
                {packages.map((pkg, pIdx) => (
                  <div key={pIdx} className="border-2 border-[#BA6D52]/40 bg-[#FBF7EC] p-4" data-testid={`quote-package-${pIdx}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          value={pkg.name}
                          onChange={(e) => updatePackage(pIdx, { name: e.target.value })}
                          placeholder={`Paket-Name (z. B. Menü ${pIdx === 0 ? "Klassik" : pIdx === 1 ? "Premium" : "Signature"})`}
                          className="w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-3 text-base font-serif"
                          data-testid={`quote-package-${pIdx}-name`}
                        />
                        <textarea
                          rows={2}
                          value={pkg.description || ""}
                          onChange={(e) => updatePackage(pIdx, { description: e.target.value })}
                          placeholder="Kurzbeschreibung — wird dem Kunden angezeigt (z. B. 3-Gänge-Menü mit saisonalen Zutaten, klassisch serviert)"
                          className="w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-3 text-sm"
                          data-testid={`quote-package-${pIdx}-description`}
                        />
                      </div>
                      {packages.length > 2 && (
                        <button
                          type="button"
                          onClick={() => { if (window.confirm(`Paket „${pkg.name || pIdx + 1}" wirklich entfernen?`)) removePackage(pIdx); }}
                          className="text-[10px] uppercase tracking-[0.15em] text-red-700 hover:bg-red-50 px-3 py-2 border border-red-200 shrink-0"
                          data-testid={`quote-package-${pIdx}-remove`}
                        >Paket entfernen</button>
                      )}
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-2 mt-4">Positionen in diesem Paket</p>
                    <div className="space-y-2">
                      {pkg.line_items.map((it, iIdx) => (
                        <div key={iIdx} className="border border-[#E3DECE] p-2 bg-white grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-12 md:col-span-3">
                            <label className="text-[9px] uppercase tracking-[0.15em] text-[#BA6D52]">Typ</label>
                            <select value={it.type_key} data-testid={`quote-package-${pIdx}-item-${iIdx}-type`} onChange={(e) => updatePkgItem(pIdx, iIdx, applyPresetToItem(it, e.target.value))} className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1 px-2 text-xs">
                              {positionTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                            </select>
                          </div>
                          <div className="col-span-12 md:col-span-4">
                            <label className="text-[9px] uppercase tracking-[0.15em] text-[#BA6D52]">Bezeichnung</label>
                            <input type="text" value={it.name} onChange={(e) => updatePkgItem(pIdx, iIdx, { name: e.target.value })} className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1 px-2 text-xs" />
                          </div>
                          <div className="col-span-3 md:col-span-1">
                            <label className="text-[9px] uppercase tracking-[0.15em] text-[#BA6D52]">Menge</label>
                            <input type="number" step="0.25" value={(it.quantity === 0 || it.quantity === "0") ? "" : (it.quantity ?? "")} placeholder="0" onChange={(e) => updatePkgItem(pIdx, iIdx, { quantity: e.target.value === "" ? "" : Number(e.target.value) })} className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1 px-2 text-xs" />
                          </div>
                          <div className="col-span-4 md:col-span-2">
                            <label className="text-[9px] uppercase tracking-[0.15em] text-[#BA6D52]">Einheit</label>
                            <input type="text" value={it.unit_name} onChange={(e) => updatePkgItem(pIdx, iIdx, { unit_name: e.target.value })} className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1 px-2 text-xs" />
                          </div>
                          <div className="col-span-4 md:col-span-1">
                            <label className="text-[9px] uppercase tracking-[0.15em] text-[#BA6D52]">Netto €</label>
                            <input type="number" step="0.01" value={(it.unit_price_net === 0 || it.unit_price_net === "0") ? "" : (it.unit_price_net ?? "")} placeholder="0" onChange={(e) => updatePkgItem(pIdx, iIdx, { unit_price_net: e.target.value === "" ? "" : Number(e.target.value) })} className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1 px-2 text-xs" />
                          </div>
                          <div className="col-span-1 md:col-span-1 flex justify-end">
                            {pkg.line_items.length > 1 && (
                              <button type="button" onClick={() => removePkgItem(pIdx, iIdx)} className="text-red-600 hover:bg-red-50 px-2 py-1 text-lg leading-none">×</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <button type="button" onClick={() => addPkgItem(pIdx)} className="text-[10px] uppercase tracking-[0.15em] text-[#BA6D52] hover:bg-[#FAFAF7] px-3 py-2 border border-[#BA6D52] self-start" data-testid={`quote-package-${pIdx}-add-item`}>+ Position</button>
                      <div className="text-sm">
                        <span className="text-[#757067]">Paket-Total: </span>
                        <b className="text-[#BA6D52]">{pkgGross(pkg).toFixed(2)} €</b>
                        <span className="text-[#8A7A68] text-xs"> (brutto)</span>
                      </div>
                    </div>
                  </div>
                ))}
                {packages.length < 4 && (
                  <button
                    type="button"
                    onClick={addPackage}
                    className="w-full border-2 border-dashed border-[#BA6D52]/40 text-[#BA6D52] hover:bg-[#FBF7EC] py-4 text-xs uppercase tracking-[0.2em]"
                    data-testid="quote-add-package"
                  >+ Weiteres Paket anlegen (max. 4)</button>
                )}
              </div>
            ) : null}
          </div>

          <div className={mode === "multi" ? "hidden" : ""}>
            <div className="flex justify-between items-center mb-3">
              <p className="text-xs uppercase tracking-[0.2em] text-[#757067] font-semibold">Positionen</p>
              <span className="text-xs text-[#757067]">Positionen frei editierbar</span>
            </div>
            <div className="space-y-3">
              {lineItems.map((it, idx) => (
                <div key={idx} className="border border-[#E3DECE] p-3 bg-[#FAFAF7]">
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-12 md:col-span-4">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Typ</label>
                      <select
                        value={it.type_key}
                        data-testid={`quote-item-${idx}-type`}
                        onChange={(e) => updateItem(idx, applyPresetToItem(it, e.target.value))}
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1.5 px-2 text-sm"
                      >
                        {positionTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                      </select>
                    </div>
                    <div className="col-span-12 md:col-span-8">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Bezeichnung</label>
                      <input
                        type="text"
                        value={it.name}
                        onChange={(e) => updateItem(idx, { name: e.target.value })}
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1.5 px-2 text-sm"
                      />
                    </div>
                    <div className="col-span-4 md:col-span-3">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Menge</label>
                      <input type="number" step="0.25"
                        value={(it.quantity === 0 || it.quantity === "0") ? "" : (it.quantity ?? "")}
                        placeholder="0"
                        onChange={(e) => updateItem(idx, { quantity: e.target.value === "" ? "" : Number(e.target.value) })}
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1.5 px-2 text-sm" />
                    </div>
                    <div className="col-span-4 md:col-span-3">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Einheit</label>
                      <input type="text" value={it.unit_name || "Stück"}
                        onChange={(e) => updateItem(idx, { unit_name: e.target.value })}
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1.5 px-2 text-sm" />
                    </div>
                    <div className="col-span-4 md:col-span-3">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Netto/Einheit €</label>
                      <input type="number" step="0.01"
                        value={(it.unit_price_net === 0 || it.unit_price_net === "0") ? "" : (it.unit_price_net ?? "")}
                        placeholder="0"
                        onChange={(e) => updateItem(idx, { unit_price_net: e.target.value === "" ? "" : Number(e.target.value) })}
                        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-1.5 px-2 text-sm" />
                    </div>
                    <div className="col-span-12 md:col-span-3 flex items-end justify-between">
                      <div className="text-right flex-1">
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Netto ges.</label>
                        <div className="mt-1 py-1.5 px-2 text-sm font-mono">
                          {(Number(it.quantity || 0) * Number(it.unit_price_net || 0)).toFixed(2).replace(".", ",")} €
                        </div>
                      </div>
                      <button onClick={() => removeItem(idx)} className="text-red-700 hover:text-red-900 hover:bg-red-50 px-2 py-2 text-lg leading-none">×</button>
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={addItem} data-testid="quote-add-position"
                className="w-full border border-dashed border-[#BA6D52] text-[#BA6D52] hover:bg-[#BA6D52]/5 py-3 uppercase tracking-[0.15em] text-xs">
                + Position hinzufügen
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#757067] mb-3 font-semibold">Gültigkeit &amp; Anzahlung</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <QuoteInput label="Gültig für (Tage)" valueKey="valid_days" type="number" form={form} setField={setField} />
              <QuoteInput label="Anzahlung %" valueKey="deposit_percent" type="number" placeholder="0 = keine" form={form} setField={setField} />
              <QuoteInput label="Anzahlung fällig in (Tagen)" valueKey="deposit_due_days" type="number" form={form} setField={setField} />
            </div>
            {Number(form.deposit_percent) > 0 && (
              <p className="text-xs text-[#757067] mt-2">
                → Kunde sieht: Anzahlung <b className="text-[#BA6D52]">{(grossSum * Number(form.deposit_percent) / 100).toFixed(2).replace(".", ",")} €</b> fällig {form.deposit_due_days || 7} Tage nach Annahme.
              </p>
            )}
          </div>

          <div className="border-t border-[#E3DECE] pt-4 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-[#757067]">Netto</span><span className="font-mono">{netSum.toFixed(2).replace(".", ",")} €</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#757067]">zzgl. {defaultTaxRate} % MwSt</span><span className="font-mono">{mwstSum.toFixed(2).replace(".", ",")} €</span></div>
            <div className="flex justify-between text-base font-medium border-t border-[#E3DECE] pt-2">
              <span>Gesamt Brutto</span>
              <span className="font-serif text-xl text-[#BA6D52]" data-testid="quote-total-gross">{grossSum.toFixed(2).replace(".", ",")} €</span>
            </div>
          </div>
        </div>
        )}

        <div className="sticky bottom-0 bg-white border-t border-[#E3DECE] px-6 py-4 flex flex-col-reverse md:flex-row justify-end gap-3">
          {step === "review" ? (
            <>
              <button onClick={() => setStep("edit")} disabled={sending || sendingTest}
                className="px-6 py-3 text-xs uppercase tracking-[0.15em] border border-[#E3DECE] hover:bg-[#FAFAF7] disabled:opacity-60"
                data-testid="quote-review-back"
              >
                ← Zurück bearbeiten
              </button>
              <button onClick={sendTestPreview} disabled={sending || sendingTest} data-testid="quote-review-test-send"
                title="Verschickt eine Vorschau-PDF an deine eigene E-Mail — noch KEIN LexOffice, KEINE Nummer verbraucht"
                className="px-6 py-3 text-xs uppercase tracking-[0.15em] border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] disabled:opacity-60 inline-flex items-center gap-2">
                {sendingTest && <Loader2 size={14} className="animate-spin" />}
                📧 Test-Vorschau an mich senden
              </button>
              <button onClick={confirmSend} disabled={sending || sendingTest} data-testid="quote-review-confirm"
                className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
                {sending && <Loader2 size={14} className="animate-spin" />}
                ✓ Jetzt absenden
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} disabled={sending}
                className="px-6 py-3 text-xs uppercase tracking-[0.15em] border border-[#E3DECE] hover:bg-[#FAFAF7] disabled:opacity-60">
                Abbrechen
              </button>
              <button onClick={submit} disabled={sending || !canSubmit} data-testid="quote-submit"
                className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
                Vorschau anzeigen →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== Stunden-Angebote (Rate Card) ====================
function RCField({ label, k, type = "text", placeholder = "", required = false, form, set }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">
        {label}{required && " *"}
      </label>
      <input
        type={type}
        value={form[k]}
        onChange={(e) => set(k, e.target.value)}
        placeholder={placeholder}
        data-testid={`rate-card-field-${k}`}
        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm"
      />
    </div>
  );
}

function RCTextarea({ label, k, rows = 3, form, set }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">{label}</label>
      <textarea
        rows={rows}
        value={form[k]}
        onChange={(e) => set(k, e.target.value)}
        data-testid={`rate-card-field-${k}`}
        className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm font-light"
      />
    </div>
  );
}

function RateCardQuotesSection({ pwd }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/rate-card-quotes`, {
        headers: { "X-Admin-Password": pwd },
      });
      setList(data || []);
    } catch {
      toast.error("Stunden-Angebote konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [pwd]);

  const del = async (id) => {
    if (!window.confirm("Stunden-Angebot löschen?")) return;
    try {
      await axios.delete(`${API}/admin/rate-card-quotes/${id}`, { headers: { "X-Admin-Password": pwd } });
      toast.success("Gelöscht");
      fetchList();
    } catch { toast.error("Fehler beim Löschen"); }
  };

  const statusBadge = (s) => {
    const map = {
      sent: ["bg-blue-50 text-blue-900 border-blue-200", "Versendet"],
      send_failed: ["bg-amber-50 text-amber-900 border-amber-200", "⚠ Versand fehlgeschl."],
      accepted: ["bg-emerald-50 text-emerald-900 border-emerald-200", "✓ Angenommen"],
      declined: ["bg-red-50 text-red-900 border-red-200", "Abgelehnt"],
      cancelled: ["bg-gray-100 text-gray-700 border-gray-300", "Storniert"],
    };
    const [cls, label] = map[s] || ["bg-gray-50 text-gray-700 border-gray-200", s];
    return <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wider border ${cls}`}>{label}</span>;
  };

  return (
    <div className="border border-[#E3DECE] bg-[#FAF5F1]/40 p-5" data-testid="rate-card-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Stunden-Angebote</p>
          <p className="text-xs text-[#757067] mt-1 font-light">
            Rate-Card ohne konkrete Tage — nur Stundensätze. Kunde kann via Link annehmen.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="rate-card-create-btn"
          className="bg-[#2C2A26] text-white hover:bg-[#3d3a34] px-4 py-2.5 text-xs uppercase tracking-[0.15em]"
        >
          + Neues Stunden-Angebot
        </button>
      </div>

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-[#BA6D52]" size={20} /></div>
      ) : list.length === 0 ? (
        <p className="py-6 text-center text-[#757067] text-sm">Noch keine Stunden-Angebote erstellt.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-[#E3DECE] text-left">
              <tr className="text-xs uppercase tracking-wider text-[#757067]">
                <th className="pb-2 pr-4">Nr.</th>
                <th className="pb-2 pr-4">Betrieb</th>
                <th className="pb-2 pr-4">Sätze WT / WE</th>
                <th className="pb-2 pr-4">Erstellt</th>
                <th className="pb-2 pr-4">Status</th>
                <th className="pb-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((q) => (
                <tr key={q.id} className="border-b border-[#E3DECE] hover:bg-white" data-testid={`rate-card-row-${q.id}`}>
                  <td className="py-3 pr-4 font-mono text-xs">{q.quote_number || "—"}</td>
                  <td className="py-3 pr-4">
                    <p className="font-medium">{q.betrieb}</p>
                    {q.ansprechpartner && <p className="text-xs text-[#757067]">{q.ansprechpartner}</p>}
                  </td>
                  <td className="py-3 pr-4 text-xs">
                    {Number(q.rate_flat_eur) > 0
                      ? `${Number(q.rate_flat_eur).toFixed(2).replace(".", ",")} € pauschal`
                      : `${Number(q.rate_weekday_eur).toFixed(2).replace(".", ",")} € / ${Number(q.rate_weekend_eur).toFixed(2).replace(".", ",")} €`}
                  </td>
                  <td className="py-3 pr-4 text-xs text-[#757067]">
                    {q.created_at ? new Date(q.created_at).toLocaleDateString("de-DE") : "—"}
                  </td>
                  <td className="py-3 pr-4">{statusBadge(q.status)}</td>
                  <td className="py-3 pr-4 text-right">
                    <button
                      onClick={() => setDetail(q)}
                      className="text-[10px] uppercase tracking-wider text-[#BA6D52] hover:underline mr-3"
                      data-testid={`rate-card-detail-${q.id}`}
                    >
                      Details
                    </button>
                    <button
                      onClick={() => del(q.id)}
                      className="text-[10px] uppercase tracking-wider text-red-700 hover:underline"
                      data-testid={`rate-card-delete-${q.id}`}
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <RateCardCreateModal
          pwd={pwd}
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); fetchList(); }}
        />
      )}

      {detail && <RateCardDetailModal quote={detail} onClose={() => setDetail(null)} pwd={pwd} onRefresh={fetchList} />}
    </div>
  );
}

function RateCardDetailModal({ quote, onClose, pwd, onRefresh }) {
  const link = `${window.location.origin}/stunden-angebot/${quote.action_token}`;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 md:p-6">
      <div className="bg-white w-full max-w-3xl max-h-[94vh] overflow-y-auto border border-[#E3DECE]">
        <div className="sticky top-0 bg-white border-b border-[#E3DECE] px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Stunden-Angebot</p>
            <h3 className="font-serif text-2xl">{quote.quote_number} — {quote.betrieb}</h3>
          </div>
          <button onClick={onClose} className="text-[#757067] hover:text-[#2C2A26] text-2xl leading-none px-3">×</button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Kunde</p>
            <p className="mt-1">{quote.betrieb}</p>
            {quote.ansprechpartner && <p className="text-[#757067]">{quote.ansprechpartner}</p>}
            <p className="text-[#757067]">{quote.email}{quote.telefon ? ` · ${quote.telefon}` : ""}</p>
            <p className="text-[#757067]">{quote.ort}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Konditionen</p>
            {Number(quote.rate_flat_eur) > 0 ? (
              <p className="mt-1"><b>{Number(quote.rate_flat_eur).toFixed(2).replace(".", ",")} € netto / h</b> (pauschal für alle Tage)</p>
            ) : (
              <>
                <p className="mt-1">Werktag: <b>{Number(quote.rate_weekday_eur).toFixed(2).replace(".", ",")} € netto / h</b></p>
                <p>WE/Feiertag: <b>{Number(quote.rate_weekend_eur).toFixed(2).replace(".", ",")} € netto / h</b></p>
              </>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Annahme-Link</p>
            <div className="mt-1 flex items-center gap-2">
              <input readOnly value={link} className="flex-1 bg-[#FAFAF7] border border-[#E3DECE] px-2 py-1.5 text-xs font-mono" />
              <button
                onClick={() => { navigator.clipboard.writeText(link); toast.success("Link kopiert"); }}
                className="text-xs uppercase tracking-wider border border-[#BA6D52] text-[#BA6D52] hover:bg-[#BA6D52] hover:text-white px-3 py-1.5"
              >
                Kopieren
              </button>
            </div>
          </div>
          {quote.status === "accepted" && (
            <div className="bg-emerald-50 border border-emerald-200 p-3 text-emerald-900">
              <p className="text-xs uppercase tracking-wider">✓ Angenommen</p>
              {quote.accepted_at && <p className="text-xs mt-1">am {new Date(quote.accepted_at).toLocaleString("de-DE")}</p>}
              {quote.accepted_customer_message && <p className="text-sm mt-2 italic">&bdquo;{quote.accepted_customer_message}&ldquo;</p>}
            </div>
          )}

          {/* Time-Tracker nur wenn angenommen */}
          {quote.status === "accepted" && (
            <TimeTrackerPanel rateCard={quote} pwd={pwd} onChange={onRefresh}/>
          )}
        </div>
      </div>
    </div>
  );
}

function TimeTrackerPanel({ rateCard, pwd, onChange }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [nowTick, setNowTick] = useState(0);
  const [manual, setManual] = useState({ open: false, start: "", end: "", notes: "" });
  const [invoicing, setInvoicing] = useState(false);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/rate-cards/${rateCard.id}/time-entries`, {
        headers: { "X-Admin-Password": pwd },
      });
      setEntries(data || []);
    } catch { toast.error("Zeit-Einträge konnten nicht geladen werden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchEntries(); }, [rateCard.id]);

  const running = entries.find((e) => e.status === "running");

  // Live-Timer
  useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => setNowTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [running]);

  const elapsed = () => {
    if (!running) return "0:00:00";
    const start = new Date(running.started_at).getTime();
    const diff = Math.max(0, Date.now() - start);
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const start = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/rate-cards/${rateCard.id}/time-entries/start`, { notes }, { headers: { "X-Admin-Password": pwd } });
      toast.success("Time-Tracker gestartet");
      setNotes("");
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };
  const stop = async () => {
    if (!running) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/time-entries/${running.id}/stop`, {}, { headers: { "X-Admin-Password": pwd } });
      toast.success("Beendet & Bestätigungsmail an Kunde gesendet");
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Zeit-Eintrag löschen?")) return;
    try {
      await axios.delete(`${API}/admin/time-entries/${id}`, { headers: { "X-Admin-Password": pwd } });
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };
  const addManual = async () => {
    if (!manual.start || !manual.end) { toast.error("Start und Ende angeben"); return; }
    setBusy(true);
    try {
      // datetime-local → ISO
      const started_at = new Date(manual.start).toISOString();
      const ended_at = new Date(manual.end).toISOString();
      await axios.post(`${API}/admin/rate-cards/${rateCard.id}/time-entries`, {
        started_at, ended_at, notes: manual.notes,
      }, { headers: { "X-Admin-Password": pwd } });
      toast.success("Nachtrag gespeichert & Bestätigungsmail an Kunde");
      setManual({ open: false, start: "", end: "", notes: "" });
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };

  const createInvoice = async () => {
    const confirmed = entries.filter((e) => e.status === "confirmed");
    if (confirmed.length === 0) { toast.error("Noch keine bestätigten Stunden"); return; }
    const totalH = confirmed.reduce((s, e) => s + Number(e.hours || 0), 0);
    if (!window.confirm(`Rechnung mit ${confirmed.length} bestätigten Einträgen (${totalH.toFixed(2).replace(".", ",")} h) erstellen und an Kunde senden?`)) return;
    setInvoicing(true);
    try {
      const { data } = await axios.post(`${API}/admin/rate-cards/${rateCard.id}/create-invoice`, {}, { headers: { "X-Admin-Password": pwd } });
      toast.success(`Rechnung wird im Hintergrund erstellt (${data.total_hours.toString().replace(".", ",")} h)`);
      fetchEntries();
      onChange && onChange();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setInvoicing(false); }
  };

  const statusPill = (s) => {
    const map = {
      running: ["bg-blue-50 text-blue-900 border-blue-200 animate-pulse", "Läuft"],
      pending_customer: ["bg-amber-50 text-amber-900 border-amber-200", "Wartet auf Kunde"],
      confirmed: ["bg-emerald-50 text-emerald-900 border-emerald-200", "✓ Bestätigt"],
      disputed: ["bg-red-50 text-red-900 border-red-200", "⚠ Rückfrage"],
      invoiced: ["bg-gray-100 text-gray-700 border-gray-300", "Abgerechnet"],
    };
    const [cls, label] = map[s] || ["bg-gray-50 text-gray-700 border-gray-200", s];
    return <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider border ${cls}`}>{label}</span>;
  };

  const totalConfirmedNet = entries
    .filter((e) => e.status === "confirmed")
    .reduce((s, e) => s + Number(e.hours || 0) * Number(e.hourly_rate_eur || 0), 0);
  const confirmedCount = entries.filter((e) => e.status === "confirmed").length;

  return (
    <div className="border-t border-[#E3DECE] pt-5 mt-5" data-testid="time-tracker-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Time-Tracker &amp; Abrechnung</p>
        {confirmedCount > 0 && (
          <button
            onClick={createInvoice}
            disabled={invoicing}
            data-testid="rate-card-create-invoice"
            className="bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2"
          >
            {invoicing && <Loader2 size={14} className="animate-spin" />}
            Rechnung erstellen ({confirmedCount} × Einträge · {totalConfirmedNet.toFixed(2).replace(".", ",")} € netto)
          </button>
        )}
      </div>

      {/* Live-Tracker */}
      <div className="border border-[#E3DECE] p-4 bg-[#FAFAF7]">
        {running ? (
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs text-blue-800 uppercase tracking-wider">▶ Läuft seit {new Date(running.started_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr</p>
              <p className="font-mono text-3xl mt-1 text-[#2C2A26]" data-testid="tracker-elapsed">{elapsed()}</p>
              {running.notes && <p className="text-xs text-[#757067] mt-1">{running.notes}</p>}
            </div>
            <button
              onClick={stop}
              disabled={busy}
              data-testid="tracker-stop"
              className="bg-red-700 text-white hover:bg-red-800 disabled:opacity-60 px-5 py-3 text-xs uppercase tracking-[0.15em]"
            >
              ■ Stopp &amp; Bestätigung senden
            </button>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-2 md:items-center">
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notiz (optional, z. B. Aushilfe Mittagsservice)"
              data-testid="tracker-notes"
              className="flex-1 bg-white border border-[#E3DECE] py-2 px-2 text-sm focus:outline-none focus:border-[#BA6D52]"
            />
            <button
              onClick={start}
              disabled={busy}
              data-testid="tracker-start"
              className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-5 py-3 text-xs uppercase tracking-[0.15em]"
            >
              ▶ Jetzt starten
            </button>
            <button
              onClick={() => setManual({ ...manual, open: !manual.open })}
              data-testid="tracker-manual-toggle"
              className="border border-[#E3DECE] hover:border-[#BA6D52] px-4 py-3 text-xs uppercase tracking-[0.15em] text-[#757067] hover:text-[#2C2A26]"
            >
              + Nachtragen
            </button>
          </div>
        )}
        {manual.open && !running && (
          <div className="mt-3 border-t border-[#E3DECE] pt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Start</label>
              <input type="datetime-local" value={manual.start} onChange={(e) => setManual({ ...manual, start: e.target.value })}
                className="mt-1 w-full bg-white border border-[#E3DECE] py-2 px-2 text-sm"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Ende</label>
              <input type="datetime-local" value={manual.end} onChange={(e) => setManual({ ...manual, end: e.target.value })}
                className="mt-1 w-full bg-white border border-[#E3DECE] py-2 px-2 text-sm"/>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Notiz</label>
              <input type="text" value={manual.notes} onChange={(e) => setManual({ ...manual, notes: e.target.value })}
                className="mt-1 w-full bg-white border border-[#E3DECE] py-2 px-2 text-sm"/>
            </div>
            <div className="md:col-span-2 text-right">
              <button onClick={addManual} disabled={busy}
                className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-[0.15em]">
                Speichern &amp; Bestätigung senden
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Liste */}
      <div className="mt-4">
        {loading ? (
          <div className="py-4 flex justify-center"><Loader2 className="animate-spin text-[#BA6D52]" size={18}/></div>
        ) : entries.length === 0 ? (
          <p className="text-center text-[#757067] text-sm py-3">Noch keine Zeit-Einträge.</p>
        ) : (
          <ul className="divide-y divide-[#E3DECE] border border-[#E3DECE] bg-white">
            {entries.map((e) => (
              <li key={e.id} className="px-3 py-2 flex items-baseline justify-between text-sm" data-testid={`time-entry-${e.id}`}>
                <div className="flex-1">
                  <p className="text-xs text-[#757067]">
                    {new Date(e.started_at).toLocaleDateString("de-DE")} · {new Date(e.started_at).toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"})}
                    {e.ended_at ? ` – ${new Date(e.ended_at).toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"})}` : " (läuft)"}
                  </p>
                  <p className="font-mono">
                    {Number(e.hours || 0).toFixed(2).replace(".", ",")} h × {Number(e.hourly_rate_eur || 0).toFixed(2).replace(".", ",")} €
                    <span className="ml-2">{statusPill(e.status)}</span>
                  </p>
                  {e.notes && <p className="text-xs text-[#757067] italic">{e.notes}</p>}
                  {e.customer_dispute_message && <p className="text-xs text-red-700 mt-1">Rückfrage: {e.customer_dispute_message}</p>}
                </div>
                {e.status !== "invoiced" && (
                  <button onClick={() => del(e.id)} className="text-[10px] text-red-700 hover:underline uppercase tracking-wider">
                    Löschen
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function RateCardCreateModal({ pwd, onClose, onSuccess }) {
  const STORAGE_KEY = "lstz.rate_card_draft.v1";
  const DEFAULT_FORM = {
    betrieb: "",
    ansprechpartner: "",
    email: "",
    telefon: "",
    ort: "",
    anrede: "Sehr geehrte Familie,",
    intro_text: "vielen Dank für Ihr Interesse an einer Zusammenarbeit. Anbei mein unverbindliches Stunden-Angebot als Grundlage für unser Gespräch.",
    rate_mode: "split",  // "split" (WT/WE) oder "flat"
    rate_flat_eur: 42,
    rate_weekday_eur: 49,
    rate_weekend_eur: 59,
    travel_note: "Fahrtkosten werden nach tatsächlichem Aufwand berechnet und im konkreten Einsatzfall gesondert ausgewiesen.",
    closing_text: "Wenn die Zusammenarbeit für beide Seiten stimmt, passe ich das Angebot gerne individuell an — sowohl bei Konditionen als auch beim Umfang. Ich freue mich auf Ihre Rückmeldung.",
    admin_notes: "",
  };
  const loadDraft = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch { return null; }
  };
  const [form, setForm] = useState(loadDraft() || DEFAULT_FORM);
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState("edit"); // "edit" | "review"

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch { /* ignore */ }
  }, [form]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit =
    form.betrieb.trim() &&
    form.email.trim() &&
    form.ort.trim() &&
    (form.rate_mode === "flat"
      ? Number(form.rate_flat_eur) > 0
      : (Number(form.rate_weekday_eur) > 0 && Number(form.rate_weekend_eur) > 0));

  const goReview = () => {
    if (!canSubmit) { toast.error("Bitte Pflichtfelder + Stundensatz ausfüllen"); return; }
    setStep("review");
  };

  const submit = async () => {
    setSending(true);
    try {
      const payload = {
        ...form,
        rate_flat_eur: form.rate_mode === "flat" ? Number(form.rate_flat_eur) : 0,
        rate_weekday_eur: form.rate_mode === "flat" ? 0 : Number(form.rate_weekday_eur),
        rate_weekend_eur: form.rate_mode === "flat" ? 0 : Number(form.rate_weekend_eur),
      };
      delete payload.rate_mode;
      await axios.post(`${API}/admin/rate-card-quotes`, payload, { headers: { "X-Admin-Password": pwd } });
      toast.success("Stunden-Angebot erstellt & versendet");
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      onSuccess();
    } catch (err) {
      toast.error("Fehler: " + fehlerText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 md:p-6" data-testid="rate-card-create-modal">
      <div className="bg-white w-full max-w-3xl max-h-[94vh] overflow-y-auto border border-[#E3DECE]">
        <div className="sticky top-0 bg-white border-b border-[#E3DECE] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">{step === "review" ? "Vorschau vor Versand" : "Neues Stunden-Angebot"}</p>
            <h3 className="font-serif text-2xl text-[#2C2A26]">{step === "review" ? "Bitte final prüfen" : "Rate Card erstellen"}</h3>
            <p className="text-xs text-[#757067] mt-1">{step === "review" ? "So sieht das Stunden-Angebot für den Kunden aus. Wenn alles passt → Absenden." : "Wird direkt per E-Mail mit PDF und Annahme-Link an den Kunden gesendet."}</p>
          </div>
          <button onClick={onClose} className="text-[#757067] hover:text-[#2C2A26] text-2xl leading-none px-3">×</button>
        </div>

        {step === "review" ? (
          <div className="p-6 space-y-6" data-testid="rate-card-review-panel">
            <div className="border border-[#E3DECE] p-5 bg-[#FAFAF7]">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52] mb-2">Empfänger</p>
              <p className="font-medium">{form.betrieb}</p>
              {form.ansprechpartner && <p className="text-sm text-[#757067]">Ansprechpartner: {form.ansprechpartner}</p>}
              <p className="text-sm text-[#757067]">{form.email}{form.telefon ? ` · ${form.telefon}` : ""}</p>
              <p className="text-sm text-[#757067] mt-1">{form.ort}</p>
            </div>

            {/* PDF/E-Mail-Vorschau im Editorial-Look */}
            <div className="border border-[#E3DECE] p-6 md:p-8 bg-white">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#BA6D52] mb-2">Stunden-Angebot</p>
              <h4 className="font-serif text-3xl text-[#2C2A26]">{form.betrieb}</h4>
              <p className="text-sm text-[#757067] mt-1">Angebot-Nr. wird beim Versand erzeugt (SA…)</p>

              {form.anrede && (
                <p className="mt-6 font-serif text-lg text-[#2C2A26] leading-relaxed">{form.anrede}</p>
              )}
              {form.intro_text && (
                <p className="mt-3 text-[#2C2A26] leading-relaxed whitespace-pre-line text-sm">{form.intro_text}</p>
              )}

              <div className="mt-6 border-l-4 border-[#BA6D52] bg-[#FAFAF7] p-4 space-y-2">
                {form.rate_mode === "flat" ? (
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm"><b>Stundensatz</b> (pauschal, alle Tage)</span>
                    <span className="font-serif text-xl text-[#BA6D52]">{Number(form.rate_flat_eur).toFixed(2).replace(".", ",")} €<span className="text-xs text-[#757067] ml-1">/ h netto</span></span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm"><b>Stundensatz Werktag</b> (Mo–Fr)</span>
                      <span className="font-serif text-xl text-[#BA6D52]">{Number(form.rate_weekday_eur).toFixed(2).replace(".", ",")} €<span className="text-xs text-[#757067] ml-1">/ h netto</span></span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm"><b>Stundensatz Wochenende / Feiertag</b></span>
                      <span className="font-serif text-xl text-[#BA6D52]">{Number(form.rate_weekend_eur).toFixed(2).replace(".", ",")} €<span className="text-xs text-[#757067] ml-1">/ h netto</span></span>
                    </div>
                  </>
                )}
                <p className="text-xs text-[#757067] pt-1">zzgl. gesetzliche Mehrwertsteuer (19 %).</p>
              </div>

              {form.travel_note && (
                <div className="mt-5">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52] mb-1">Fahrtkosten</p>
                  <p className="text-sm text-[#2C2A26] leading-relaxed whitespace-pre-line">{form.travel_note}</p>
                </div>
              )}

              {form.closing_text && (
                <p className="mt-5 text-sm text-[#2C2A26] leading-relaxed whitespace-pre-line">{form.closing_text}</p>
              )}

              <div className="mt-6 text-center">
                <span className="inline-block bg-[#BA6D52] text-white px-6 py-2 text-[10px] uppercase tracking-[0.25em]">
                  Angebot annehmen (Kunden-Button)
                </span>
              </div>

              <p className="text-[11px] text-[#757067] mt-5 border-t border-[#E3DECE] pt-3">
                Unverbindlich · Grundlage für ein persönliches Gespräch. Es gelten die AGB auf stutz-cuisine.com/agb.
              </p>
            </div>

            {form.admin_notes && (
              <div className="border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <b>Interne Notiz (wird NICHT versendet):</b><br/>
                <span className="whitespace-pre-line">{form.admin_notes}</span>
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <b>⚠ Nach dem Absenden</b> wird das Stunden-Angebot mit fortlaufender Nummer (SA…) erstellt, ein PDF generiert und an <b>{form.email}</b> versendet. Der Kunde erhält einen Annahme-Link per E-Mail.
            </div>
          </div>
        ) : (
        <div className="p-6 space-y-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#757067] font-semibold mb-3">Empfänger</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <RCField label="Betrieb / Familie" k="betrieb" required placeholder="z. B. Familie Mustermann" form={form} set={set}/>
              <RCField label="Ansprechpartner" k="ansprechpartner" form={form} set={set}/>
              <RCField label="E-Mail" k="email" type="email" required form={form} set={set}/>
              <RCField label="Telefon" k="telefon" form={form} set={set}/>
              <div className="md:col-span-2">
                <RCField label="Adresse (Straße Nr, PLZ Ort)" k="ort" required placeholder="Musterstraße 12, 12345 Musterstadt" form={form} set={set}/>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#757067] font-semibold mb-3">Anschreiben</p>
            <div className="space-y-3">
              <RCField label="Anrede" k="anrede" placeholder="Sehr geehrte Familie …," form={form} set={set}/>
              <RCTextarea label="Einleitung" k="intro_text" rows={4} form={form} set={set}/>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#757067] font-semibold mb-3">Konditionen</p>
            <div className="inline-flex border border-[#E3DECE] text-xs mb-3">
              <button
                type="button"
                onClick={() => set("rate_mode", "split")}
                data-testid="rate-mode-split"
                className={`px-3 py-1.5 uppercase tracking-wider ${form.rate_mode === "split" ? "bg-[#BA6D52] text-white" : "bg-white text-[#757067]"}`}
              >
                Werktag / WE
              </button>
              <button
                type="button"
                onClick={() => set("rate_mode", "flat")}
                data-testid="rate-mode-flat"
                className={`px-3 py-1.5 uppercase tracking-wider border-l border-[#E3DECE] ${form.rate_mode === "flat" ? "bg-[#BA6D52] text-white" : "bg-white text-[#757067]"}`}
              >
                Fester Stundensatz
              </button>
            </div>
            {form.rate_mode === "flat" ? (
              <div>
                <RCField label="Stundensatz (Netto € / h, pauschal für alle Tage)" k="rate_flat_eur" type="number" required placeholder="42" form={form} set={set}/>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <RCField label="Stundensatz Werktag (Netto € / h)" k="rate_weekday_eur" type="number" required placeholder="49" form={form} set={set}/>
                <RCField label="Stundensatz WE/Feiertag (Netto € / h)" k="rate_weekend_eur" type="number" required placeholder="59" form={form} set={set}/>
              </div>
            )}
            <p className="text-xs text-[#757067] mt-2">MwSt (19 %) kommt automatisch dazu.</p>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#757067] font-semibold mb-3">Fahrtkosten</p>
            <div className="space-y-3">
              <RCTextarea label="Fahrtkosten-Hinweis (leer = wird nicht gedruckt)" k="travel_note" rows={3} form={form} set={set}/>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#757067] font-semibold mb-3">Schlusstext</p>
            <div className="space-y-3">
              <RCTextarea label="Schluss-Text" k="closing_text" rows={4} form={form} set={set}/>
            </div>
          </div>

          <div>
            <RCTextarea label="Interne Notizen (nur für dich)" k="admin_notes" rows={2} form={form} set={set}/>
          </div>
        </div>
        )}

        <div className="sticky bottom-0 bg-white border-t border-[#E3DECE] px-6 py-4 flex items-center justify-end gap-3">
          {step === "review" ? (
            <>
              <button
                onClick={() => setStep("edit")}
                data-testid="rate-card-back-to-edit"
                className="border border-[#E3DECE] hover:border-[#BA6D52] px-6 py-3 text-xs uppercase tracking-[0.15em] text-[#757067] hover:text-[#2C2A26]"
              >
                ← Zurück bearbeiten
              </button>
              <button
                onClick={submit}
                disabled={sending}
                data-testid="rate-card-submit"
                className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2"
              >
                {sending && <Loader2 size={14} className="animate-spin" />}
                Verbindlich absenden
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                data-testid="rate-card-cancel"
                className="border border-[#E3DECE] hover:border-[#BA6D52] px-6 py-3 text-xs uppercase tracking-[0.15em] text-[#757067] hover:text-[#2C2A26]"
              >
                Abbrechen
              </button>
              <button
                onClick={goReview}
                disabled={!canSubmit}
                data-testid="rate-card-goto-review"
                className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.15em]"
              >
                Vorschau anzeigen →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ==================== Header-Widget: laufender Time-Tracker ====================
function RunningTrackerBadge({ pwd, onSwitchTab }) {
  const [running, setRunning] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  const fetchRunning = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/time-entries/running`, {
        headers: { "X-Admin-Password": pwd },
      });
      setRunning(data?.running || null);
    } catch { /* leiser Fehler */ }
  };

  useEffect(() => {
    fetchRunning();
    const iv = setInterval(fetchRunning, 30000); // Poll alle 30s
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwd]);

  // Live-Sekundentimer
  useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [running]);

  const stop = async () => {
    if (!running) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/time-entries/${running.id}/stop`, {}, {
        headers: { "X-Admin-Password": pwd },
      });
      toast.success("Time-Tracker gestoppt & Bestätigungsmail an Kunde gesendet");
      setRunning(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  if (!running) return null;

  const start = new Date(running.started_at).getTime();
  const diff = Math.max(0, Date.now() - start);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const elapsed = `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  const rc = running.rate_card || {};

  return (
    <div
      className="hidden md:flex items-center gap-3 border border-blue-300 bg-blue-50 pl-3 pr-2 py-2 animate-pulse"
      data-testid="running-tracker-badge"
      title={`Tracker läuft für ${rc.betrieb}`}
    >
      <span className="inline-block w-2 h-2 bg-blue-700 rounded-full" />
      <div className="text-xs leading-tight">
        <p className="uppercase tracking-wider text-blue-800 text-[10px]">▶ Läuft</p>
        <p className="font-mono text-sm text-[#2C2A26]" data-testid="header-elapsed">{elapsed}</p>
        <p className="text-[10px] text-[#757067] truncate max-w-[180px]">{rc.betrieb || "—"}</p>
      </div>
      <button
        onClick={onSwitchTab}
        className="text-[10px] uppercase tracking-wider border border-blue-300 text-blue-800 hover:bg-blue-100 px-2 py-1.5"
        title="Zum Angebot springen"
      >
        Zum Angebot
      </button>
      <button
        onClick={stop}
        disabled={busy}
        data-testid="header-tracker-stop"
        className="text-[10px] uppercase tracking-wider bg-red-700 hover:bg-red-800 text-white disabled:opacity-60 px-3 py-2"
      >
        ■ Stopp
      </button>
    </div>
  );
}

// ==================== Dashboard-Kachel: Offene bestätigte Stunden ====================
function PendingInvoicingTile({ pwd }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoicing, setInvoicing] = useState(null);

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/rate-cards/pending-invoicing`, {
        headers: { "X-Admin-Password": pwd },
      });
      setList(data || []);
    } catch { /* leise */ }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchList(); }, [pwd]);

  const createInvoice = async (rateCardId) => {
    if (!window.confirm("Rechnung mit allen bestätigten Stunden erstellen und an Kunde senden?")) return;
    setInvoicing(rateCardId);
    try {
      const { data } = await axios.post(`${API}/admin/rate-cards/${rateCardId}/create-invoice`, {}, {
        headers: { "X-Admin-Password": pwd },
      });
      toast.success(`Rechnung wird erstellt (${data.total_hours.toString().replace(".", ",")} h · ${data.total_gross_eur.toFixed(2).replace(".", ",")} € brutto)`);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setInvoicing(null); }
  };

  const totalGross = list.reduce((s, r) => s + Number(r.confirmed_gross_eur || 0), 0);

  return (
    <div className="border border-[#E3DECE] p-5" data-testid="pending-invoicing-tile">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Offene bestätigte Stunden</p>
          <p className="text-xs text-[#757067] mt-1">Kunden mit bestätigten Zeit-Einträgen, die noch nicht abgerechnet sind.</p>
        </div>
        {list.length > 0 && (
          <p className="font-serif text-2xl text-[#BA6D52]">{totalGross.toFixed(2).replace(".", ",")} €<span className="text-xs text-[#757067] ml-1">brutto</span></p>
        )}
      </div>
      {loading ? (
        <div className="py-4 flex justify-center"><Loader2 className="animate-spin text-[#BA6D52]" size={18}/></div>
      ) : list.length === 0 ? (
        <p className="py-3 text-center text-[#757067] text-sm">Keine offenen bestätigten Stunden — alles abgerechnet ✓</p>
      ) : (
        <ul className="divide-y divide-[#E3DECE] border-t border-[#E3DECE]">
          {list.map((r) => (
            <li key={r.rate_card_id} className="py-3 flex items-center justify-between gap-3" data-testid={`pending-row-${r.rate_card_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{r.betrieb}</p>
                <p className="text-xs text-[#757067] font-mono">
                  {r.quote_number} · {r.entries_count} {r.entries_count === 1 ? "Eintrag" : "Einträge"} · seit {r.oldest_entry ? new Date(r.oldest_entry).toLocaleDateString("de-DE") : "—"}
                </p>
              </div>
              <div className="text-right">
                <p className="font-mono text-sm"><b>{Number(r.confirmed_hours).toFixed(2).replace(".", ",")} h</b></p>
                <p className="text-xs text-[#757067]">{Number(r.confirmed_gross_eur).toFixed(2).replace(".", ",")} € brutto</p>
              </div>
              <button
                onClick={() => createInvoice(r.rate_card_id)}
                disabled={invoicing === r.rate_card_id}
                data-testid={`pending-invoice-btn-${r.rate_card_id}`}
                className="bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60 px-3 py-2 text-[10px] uppercase tracking-wider inline-flex items-center gap-1"
              >
                {invoicing === r.rate_card_id && <Loader2 size={12} className="animate-spin" />}
                Rechnung
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ==================== Fahrtenbuch (Mileage Log) ====================
function MileageTab({ pwd }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState({ entries: [], total_km: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const params = () => {
    const p = { year };
    if (month > 0) p.month = month;
    return p;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/mileage-entries`, {
        params: params(),
        headers: { "X-Admin-Password": pwd },
      });
      setData(data);
    } catch { toast.error("Fahrten konnten nicht geladen werden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, [pwd, year, month]);

  const del = async (id) => {
    if (!window.confirm("Fahrt löschen?")) return;
    try {
      await axios.delete(`${API}/admin/mileage-entries/${id}`, { headers: { "X-Admin-Password": pwd } });
      fetchData();
    } catch { toast.error("Fehler beim Löschen"); }
  };

  const downloadPdf = async () => {
    try {
      const resp = await axios.get(`${API}/admin/mileage-entries/export/pdf`, {
        params: params(),
        headers: { "X-Admin-Password": pwd },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = month > 0 ? `${year}-${String(month).padStart(2, "0")}` : `${year}`;
      a.download = `Fahrtenbuch_${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF geöffnet");
    } catch { toast.error("PDF konnte nicht erstellt werden"); }
  };

  const monthLabels = [
    { v: 0, l: "Gesamtes Jahr" },
    ...["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"].map((n,i)=>({v:i+1,l:n})),
  ];
  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) years.push(y);

  return (
    <div data-testid="mileage-tab">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Fahrtenbuch</p>
          <p className="text-sm text-[#757067] mt-1 font-light">
            Kilometer mit Privat-PKW erfassen — für die Abrechnung mit dem Steuerberater.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
            data-testid="mileage-month-select"
            className="bg-white border border-[#E3DECE] px-2 py-2 text-sm">
            {monthLabels.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            data-testid="mileage-year-select"
            className="bg-white border border-[#E3DECE] px-2 py-2 text-sm">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={downloadPdf} data-testid="mileage-export-pdf"
            className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#BA6D52] hover:text-white px-4 py-2 text-xs uppercase tracking-[0.15em]">
            PDF exportieren
          </button>
          <button onClick={() => setShowCreate(true)} data-testid="mileage-create-btn"
            className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-4 py-2 text-xs uppercase tracking-[0.15em]">
            + Neue Fahrt
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="border border-[#E3DECE] p-5 bg-[#FAFAF7]">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#757067]">Fahrten im Zeitraum</p>
          <p className="font-serif text-3xl text-[#2C2A26] mt-1" data-testid="mileage-count">{data.count}</p>
        </div>
        <div className="border border-[#E3DECE] p-5 bg-[#FAFAF7]">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#757067]">Gesamte Kilometer</p>
          <p className="font-serif text-3xl text-[#BA6D52] mt-1" data-testid="mileage-total-km">
            {Number(data.total_km).toFixed(1).replace(".", ",")} <span className="text-lg">km</span>
          </p>
        </div>
        <div className="border border-[#BA6D52] p-5 bg-[#FAF5F1]">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Betrag ({(data.km_rate_eur ?? 0.3).toFixed(2).replace(".", ",")} € / km)</p>
          <p className="font-serif text-3xl text-[#BA6D52] mt-1" data-testid="mileage-total-amount">
            {Number(data.total_amount_eur ?? Number(data.total_km) * 0.3).toFixed(2).replace(".", ",")} €
          </p>
          <p className="text-[10px] text-[#757067] mt-1">Dieser Euro-Betrag gehört in die Buchhaltung — nicht die Kilometer</p>
        </div>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-[#BA6D52]" size={20} /></div>
      ) : data.entries.length === 0 ? (
        <p className="py-8 text-center text-[#757067] text-sm">Keine Fahrten im gewählten Zeitraum.</p>
      ) : (
        <div className="overflow-x-auto border border-[#E3DECE]">
          <table className="w-full text-sm">
            <thead className="bg-[#2C2A26] text-white text-left">
              <tr className="text-[10px] uppercase tracking-wider">
                <th className="px-3 py-2">Datum</th>
                <th className="px-3 py-2">Zweck / Kunde</th>
                <th className="px-3 py-2">Von → Nach</th>
                <th className="px-3 py-2 text-center">H+R</th>
                <th className="px-3 py-2 text-right">km</th>
                <th className="px-3 py-2 text-right">Betrag</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.id} className="border-b border-[#E3DECE] hover:bg-[#FAFAF7]" data-testid={`mileage-row-${e.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">
                    {(() => { try { return new Date(e.entry_date).toLocaleDateString("de-DE"); } catch { return e.entry_date; } })()}
                  </td>
                  <td className="px-3 py-2">
                    <p>{e.purpose}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {e.trip_type === "shopping" ? (
                        <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-900 px-1.5 py-0.5">Einkauf</span>
                      ) : e.trip_type === "customer_visit" ? (
                        <span className="text-[9px] uppercase tracking-wider bg-blue-100 text-blue-900 px-1.5 py-0.5">Kundeneinsatz</span>
                      ) : null}
                      {e.customer_ref && <span className="text-[10px] text-[#757067]">↳ {e.customer_ref}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">{e.from_location} → {e.to_location}</td>
                  <td className="px-3 py-2 text-center text-xs">{e.is_round_trip ? "Ja" : "einf."}</td>
                  <td className="px-3 py-2 text-right font-mono"><b>{Number(e.kilometers).toFixed(1).replace(".", ",")}</b></td>
                  <td className="px-3 py-2 text-right font-mono text-[#BA6D52]">
                    {Number(e.amount_eur ?? Number(e.kilometers) * 0.3).toFixed(2).replace(".", ",")} €
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => del(e.id)} data-testid={`mileage-delete-${e.id}`}
                      className="text-[10px] uppercase tracking-wider text-red-700 hover:underline">Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <MileageCreateModal pwd={pwd} onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); fetchData(); }} />
      )}
    </div>
  );
}

function MileageCreateModal({ pwd, onClose, onSuccess }) {
  const today = toYMDLocal();
  const [form, setForm] = useState({
    entry_date: today,
    purpose: "",
    from_location: "Michelstadt",
    to_location: "",
    kilometers: "",
    is_round_trip: true,
    trip_type: "customer_visit",
    customer_ref: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Beim Wechsel des Typs sinnvolle Defaults vorbelegen (nur wenn Zweck noch leer)
  const setType = (t) => {
    setForm((f) => {
      const nf = { ...f, trip_type: t };
      if (!f.purpose.trim()) {
        nf.purpose = t === "shopping"
          ? "Einkauf für Privatkoch-Menü"
          : "Einsatz vor Ort";
      }
      return nf;
    });
  };

  const canSubmit = form.entry_date && form.purpose.trim() && form.from_location.trim() && form.to_location.trim() && Number(form.kilometers) > 0;

  const submit = async () => {
    if (!canSubmit) { toast.error("Bitte Pflichtfelder ausfüllen"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/mileage-entries`, {
        ...form,
        kilometers: Number(form.kilometers),
      }, { headers: { "X-Admin-Password": pwd } });
      toast.success("Fahrt gespeichert");
      onSuccess();
    } catch (err) {
      toast.error("Fehler: " + fehlerText(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 md:p-6" data-testid="mileage-create-modal">
      <div className="bg-white w-full max-w-xl max-h-[94vh] overflow-y-auto border border-[#E3DECE]">
        <div className="sticky top-0 bg-white border-b border-[#E3DECE] px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Neue Fahrt</p>
            <h3 className="font-serif text-2xl text-[#2C2A26]">Kilometer erfassen</h3>
          </div>
          <button onClick={onClose} className="text-[#757067] hover:text-[#2C2A26] text-2xl leading-none px-3">×</button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Fahrttyp</label>
            <div className="mt-2 inline-flex border border-[#E3DECE] text-xs">
              <button type="button" onClick={() => setType("customer_visit")}
                data-testid="mileage-type-customer"
                className={`px-3 py-2 uppercase tracking-wider ${form.trip_type === "customer_visit" ? "bg-[#BA6D52] text-white" : "bg-white text-[#757067]"}`}>
                Kundeneinsatz
              </button>
              <button type="button" onClick={() => setType("shopping")}
                data-testid="mileage-type-shopping"
                className={`px-3 py-2 uppercase tracking-wider border-l border-[#E3DECE] ${form.trip_type === "shopping" ? "bg-[#BA6D52] text-white" : "bg-white text-[#757067]"}`}>
                Einkaufsfahrt
              </button>
            </div>
            {form.trip_type === "shopping" && (
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 mt-2 px-2 py-1.5">
                <b>Hinweis:</b> Steuerlich relevant, wenn die einfache Anfahrt zum Supermarkt {'>'} 2 Stunden dauert.
              </p>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Datum *</label>
            <input type="date" value={form.entry_date} onChange={(e) => set("entry_date", e.target.value)}
              data-testid="mileage-field-date"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Zweck / Anlass *</label>
            <input type="text" value={form.purpose} onChange={(e) => set("purpose", e.target.value)}
              placeholder={form.trip_type === "shopping"
                ? "z. B. Einkauf Frischeparadies für Menü-Abend Familie Muster"
                : "z. B. Menü-Abend bei Familie Muster / Aushilfe Gasthaus zur Post"}
              data-testid="mileage-field-purpose"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Von *</label>
              <input type="text" value={form.from_location} onChange={(e) => set("from_location", e.target.value)}
                data-testid="mileage-field-from"
                className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Nach *</label>
              <input type="text" value={form.to_location} onChange={(e) => set("to_location", e.target.value)}
                placeholder={form.trip_type === "shopping" ? "z. B. Frischeparadies Frankfurt" : "z. B. Betrieb / Kunde vor Ort"}
                data-testid="mileage-field-to"
                className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Kilometer (gesamt) *</label>
              <input type="number" step="0.1" value={form.kilometers} onChange={(e) => set("kilometers", e.target.value)}
                placeholder="z. B. 120"
                data-testid="mileage-field-km"
                className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Fahrttyp</label>
              <div className="mt-1 flex items-center gap-2 h-[38px]">
                <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
                  <input type="checkbox" checked={form.is_round_trip}
                    onChange={(e) => set("is_round_trip", e.target.checked)}
                    data-testid="mileage-field-roundtrip"
                    className="accent-[#BA6D52]" />
                  Hin &amp; Rückfahrt
                </label>
              </div>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Kunde aus der Kartei (optional)</label>
            <div className="mt-1">
              <CustomerPicker
                pwd={pwd}
                value={form.customer_ref}
                onChange={(v) => set("customer_ref", v)}
                onSelect={(k) => setForm((f) => ({
                  ...f,
                  customer_ref: k.betrieb,
                  to_location: [k.strasse, [k.plz, k.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ") || f.to_location,
                  purpose: f.purpose.trim() && f.purpose !== "Einsatz vor Ort"
                    ? f.purpose
                    : (f.trip_type === "shopping" ? `Einkauf für ${k.betrieb}` : `Einsatz vor Ort bei ${k.betrieb}`),
                }))}
                placeholder="z. B. Hotel Zentlinde GbR — tippen für Vorschläge"
                testId="mileage-field-customer"
              />
            </div>
            <p className="text-[10px] text-[#757067] mt-1">
              Auswahl füllt Ziel-Adresse und Zweck automatisch aus.
            </p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Notiz (optional)</label>
            <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] py-2 px-2 text-sm font-light" />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E3DECE] px-6 py-4 flex items-center justify-end gap-3">
          <button onClick={onClose}
            className="border border-[#E3DECE] hover:border-[#BA6D52] px-6 py-3 text-xs uppercase tracking-[0.15em] text-[#757067]">
            Abbrechen
          </button>
          <button onClick={submit} disabled={saving || !canSubmit} data-testid="mileage-submit"
            className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}


// ==================== Dokumente (Downloads für Buchhaltung / Behörden) ====================
function DocumentsTile({ pwd }) {
  const [busy, setBusy] = useState(null);

  const download = async (path, filename, label) => {
    setBusy(label);
    try {
      const resp = await axios.get(`${API}${path}`, {
        headers: { "X-Admin-Password": pwd },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${label} heruntergeladen`);
    } catch { toast.error("Download fehlgeschlagen"); }
    finally { setBusy(null); }
  };

  const docs = [
    {
      key: "businessplan-ihk",
      title: "Businessplan IHK-Fassung",
      subtitle: "Für Gründungszuschuss + Fachkundige Stellungnahme (mit 36-Monats-Finanzplan & Lebenslauf)",
      path: "/admin/businessplan/ihk-pdf",
      filename: "Businessplan_IHK_Lukas_Stutz.pdf",
    },
    {
      key: "businessplan",
      title: "Businessplan Kurzfassung",
      subtitle: "Für Bank / Steuerberater / interne Verwendung",
      path: "/admin/businessplan/pdf",
      filename: "Businessplan_Lukas_Stutz.pdf",
    },
  ];

  return (
    <div className="border border-[#E3DECE] p-5" data-testid="documents-tile">
      <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold mb-1">Dokumente</p>
      <p className="text-xs text-[#757067] font-light mb-4">Offizielle Unterlagen zum Download für Buchhaltung, Bank oder Behörden.</p>
      <ul className="divide-y divide-[#E3DECE] border-t border-[#E3DECE]">
        {docs.map((d) => (
          <li key={d.key} className="py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium">{d.title}</p>
              <p className="text-xs text-[#757067]">{d.subtitle}</p>
            </div>
            <button
              onClick={() => download(d.path, d.filename, d.title)}
              disabled={busy === d.title}
              data-testid={`document-download-${d.key}`}
              className="bg-[#2C2A26] text-white hover:bg-[#3d3a34] disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-wider inline-flex items-center gap-2"
            >
              {busy === d.title && <Loader2 size={12} className="animate-spin" />}
              PDF laden
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

